import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendar, 
  faChartBar, 
  faList, 
  faSync, 
  faExclamationTriangle, 
  faCheckCircle, 
  faTimesCircle, 
  faEnvelope, 
  faClock, 
  faUtensils, 
  faUsers, 
  faPoundSign, 
  faEdit, 
  faUser, 
  faDownload, 
  faSpinner, 
  faInfoCircle, 
  faFire, 
  faFile, 
  faPrint,
  faExchangeAlt,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faPlus,
  faCreditCard,
  faTableCells,
  faCommentDots,
  faTrash,
  faWineGlass,
  faGift,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import config from '../../../config/environment';
import logo from '../../../assets/logo.png';
import FloorPlanView from './FloorPlanView';

const Panel = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem 2rem;
  padding-top: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  
  @media print {
    box-shadow: none;
    padding: 1rem;
  }
`;

const PageHeader = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px 12px 0 0;
  padding: 2rem 2.5rem;
  margin: -1.5rem -2rem 2rem -2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border-bottom: 3px solid #dee2e6;
  
  @media print {
    display: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 1.5rem;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
  color: white;
`;

const HeaderTitle = styled.h1`
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
  font-weight: 700;
  color: #2c3e50;
  letter-spacing: -0.5px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: 1rem;
  color: #6c757d;
  font-weight: 400;
`;

const SectionTitle = styled.h2`
  font-size: 1.35rem;
  margin-bottom: 0.75rem;
  margin-top: 0;
  color: #333;
  
  @media print {
    font-size: 1.25rem;
  }
`;

const SmallText = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1.5rem;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #dee2e6;
  
  @media print {
    display: none;
  }
`;

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background: transparent;
  color: ${props => props.active ? '#007bff' : '#666'};
  font-weight: ${props => props.active ? '600' : '400'};
  font-size: 0.95rem;
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? '#007bff' : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.2s ease;
  
  &:hover {
    color: #007bff;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  @media print {
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
  }
`;

const SummaryCard = styled.div`
  background: ${props => {
    switch(props.variant) {
      case 'primary':
        return 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)';
      case 'success':
        return 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)';
      case 'warning':
        return 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)';
      case 'danger':
        return 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)';
      default:
        return 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)';
    }
  }};
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px ${props => {
    switch(props.variant) {
      case 'primary':
        return 'rgba(0, 123, 255, 0.3)';
      case 'success':
        return 'rgba(40, 167, 69, 0.3)';
      case 'warning':
        return 'rgba(255, 193, 7, 0.3)';
      case 'danger':
        return 'rgba(220, 53, 69, 0.3)';
      default:
        return 'rgba(108, 117, 125, 0.3)';
    }
  }};
  border: 2px solid ${props => {
    switch(props.variant) {
      case 'primary':
        return '#0056b3';
      case 'success':
        return '#1e7e34';
      case 'warning':
        return '#e0a800';
      case 'danger':
        return '#c82333';
      default:
        return '#5a6268';
    }
  }};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: rgba(255, 255, 255, 0.3);
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px ${props => {
      switch(props.variant) {
        case 'primary':
          return 'rgba(0, 123, 255, 0.4)';
        case 'success':
          return 'rgba(40, 167, 69, 0.4)';
        case 'warning':
          return 'rgba(255, 193, 7, 0.4)';
        case 'danger':
          return 'rgba(220, 53, 69, 0.4)';
        default:
          return 'rgba(108, 117, 125, 0.4)';
      }
    }};
  }
  
  @media print {
    box-shadow: none;
    border: 1px solid #dee2e6;
    transform: none;
  }
`;

const SummaryLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.95;
  margin-bottom: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SummaryValue = styled.div`
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media print {
    font-size: 1.5rem;
    text-shadow: none;
  }
`;

const DayOverviewCard = styled.div`
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  page-break-inside: avoid;
  
  @media print {
    page-break-inside: avoid;
    margin-bottom: 1rem;
    padding: 1rem;
  }
`;

const DayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #dee2e6;
  
  @media print {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const DayTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  color: #192f6a;
  font-weight: 700;
  
  @media print {
    font-size: 1.1rem;
  }
`;

const DayStats = styled.div`
  display: flex;
  gap: 1.5rem;
  font-size: 0.9rem;
  color: #666;
  
  @media print {
    flex-wrap: wrap;
    gap: 0.75rem;
  }
`;

const TimeSlotSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
`;

const TimeSlotTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  color: #333;
  font-weight: 600;
  
  @media print {
    font-size: 0.95rem;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 0.75rem;
  
  @media print {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
  }
`;

const StatItem = styled.div`
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #007bff;
  
  @media print {
    padding: 0.5rem;
    border-left: 2px solid #007bff;
  }
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #192f6a;
  
  @media print {
    font-size: 1rem;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  
  @media print {
    font-size: 0.85rem;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  font-size: 0.9rem;
  
  @media print {
    padding: 0.5rem;
    font-size: 0.8rem;
  }
`;

const Td = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #dee2e6;
  font-size: 0.9rem;
  
  @media print {
    padding: 0.5rem;
    font-size: 0.8rem;
  }
`;

const Tr = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
  
  @media print {
    &:hover {
      background: transparent;
    }
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  background: ${props => props.variant === 'success' ? '#d4edda' : props.variant === 'warning' ? '#fff3cd' : props.variant === 'danger' ? '#f8d7da' : '#e2e3e5'};
  color: ${props => props.variant === 'success' ? '#155724' : props.variant === 'warning' ? '#856404' : props.variant === 'danger' ? '#721c24' : '#383d41'};
  margin-right: 0.5rem;
  margin-bottom: 0.25rem;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  background: ${props => props.variant === 'primary' ? '#007bff' : props.variant === 'success' ? '#28a745' : props.variant === 'danger' ? '#dc3545' : '#6c757d'};
  color: white;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media print {
    display: none;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
`;

const Filters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  align-items: flex-end;
  
  @media print {
    display: none;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FilterLabel = styled.label`
  font-size: 0.85rem;
  color: #666;
  font-weight: 500;
`;

const FilterInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const DetailsRow = styled.tr`
  background: #f8f9fa;
  
  ${Td} {
    padding: 1rem;
  }
  
  @media print {
    background: white;
    border: 1px solid #dee2e6;
  }
`;

const ProductList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.5rem 0;
`;

const ProductItem = styled.li`
  padding: 0.5rem;
  margin: 0.25rem 0;
  background: white;
  border-left: 3px solid #007bff;
  border-radius: 4px;
  
  @media print {
    border-left: 2px solid #007bff;
    page-break-inside: avoid;
  }
`;

const ModifierList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.25rem 0 0 1rem;
  font-size: 0.85rem;
  color: #666;
`;

const QuestionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.25rem 0 0 1rem;
  font-size: 0.85rem;
  color: #666;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const PrintSection = styled.div`
  @media print {
    display: block;
  }
  @media screen {
    display: none;
  }
`;

const ScreenSection = styled.div`
  @media print {
    display: none;
  }
  @media screen {
    display: block;
  }
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  margin-top: 0;
  flex-wrap: wrap;
  gap: 1rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const OrderPrintCard = styled.div`
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  page-break-inside: avoid;
  
  @media screen {
    display: none;
  }
  
  @media print {
    display: block;
  }
`;

const AllergenAlert = styled.div`
  padding: 0.75rem;
  background: #fff3cd;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  border-left: 3px solid #ffc107;
  
  @media print {
    border-left: 2px solid #ffc107;
  }
`;

// Calendar View Styled Components
const CalendarContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, #192f6a 0%, #2a4a8f 100%);
  color: white;
`;

const CalendarHeaderTitle = styled.h2`
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
`;

const CalendarNav = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const CalendarNavButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1.25rem;
  font-weight: 700;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: #e9ecef;
  border-top: 1px solid #e9ecef;
`;

const CalendarDayHeader = styled.div`
  padding: 1rem;
  text-align: center;
  font-weight: 700;
  font-size: 0.9rem;
  color: #666;
  background: #f8f9fa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CalendarDay = styled.div`
  background: ${props => 
    props.isToday ? '#fff3cd' : 
    props.isSelected ? '#e7f3ff' : 
    props.isOtherMonth ? '#f8f9fa' : 
    'white'};
  min-height: 120px;
  padding: 0.75rem;
  cursor: ${props => props.hasBookings ? 'pointer' : 'default'};
  transition: all 0.2s;
  border: ${props => 
    props.isToday ? '2px solid #ffc107' : 
    props.isSelected ? '2px solid #007bff' : 
    '2px solid transparent'};
  
  &:hover {
    background: ${props => 
      props.hasBookings ? '#f0f8ff' : 
      props.isOtherMonth ? '#f8f9fa' : 
      '#fafafa'};
    transform: ${props => props.hasBookings ? 'scale(1.02)' : 'none'};
    box-shadow: ${props => props.hasBookings ? '0 4px 12px rgba(0,0,0,0.1)' : 'none'};
  }
`;

const CalendarDayNumber = styled.div`
  font-size: 1.25rem;
  font-weight: ${props => props.isToday ? '700' : '600'};
  color: ${props => 
    props.isToday ? '#856404' : 
    props.isOtherMonth ? '#adb5bd' : 
    '#333'};
  margin-bottom: 0.5rem;
`;

const CalendarDayBookings = styled.div`
  margin-top: 0.5rem;
`;

const CalendarDayBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: ${props => {
    const count = props.count || 0;
    if (count >= 81) return 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)';
    if (count >= 51) return 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)';
    return 'linear-gradient(135deg, #28a745 0%, #218838 100%)';
  }};
  color: white;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`;

const CalendarDayGuests = styled.div`
  font-size: 0.7rem;
  color: #666;
  margin-top: 0.25rem;
`;

const CalendarSummaryPanel = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  padding: 1.5rem;
  margin-top: 2rem;
`;

const CalendarDayDetail = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  padding: 2rem;
  margin-top: 2rem;
  border: 2px solid #007bff;
`;

const CalendarLegend = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  justify-content: center;
`;

const CalendarLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #666;
`;

const CalendarLegendColor = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: ${props => props.color};
`;

const BookingsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  
  thead {
    background: #f8f9fa;
  }
  
  th {
    padding: 1rem;
    text-align: left;
    font-weight: 700;
    color: #333;
    border-bottom: 2px solid #dee2e6;
    font-size: 0.95rem;
  }
  
  td {
    padding: 1rem;
    border-bottom: 1px solid #dee2e6;
    vertical-align: middle;
  }
  
  tbody tr:hover {
    background: #f8f9fa;
  }
  
  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${props => {
    const status = props.status?.toLowerCase() || '';
    if (status.includes('confirmed') || status.includes('paid')) return '#d4edda';
    if (status.includes('pending')) return '#fff3cd';
    if (status.includes('cancelled')) return '#f8d7da';
    return '#e7f3ff';
  }};
  color: ${props => {
    const status = props.status?.toLowerCase() || '';
    if (status.includes('confirmed') || status.includes('paid')) return '#155724';
    if (status.includes('pending')) return '#856404';
    if (status.includes('cancelled')) return '#721c24';
    return '#004085';
  }};
  border: 1px solid ${props => {
    const status = props.status?.toLowerCase() || '';
    if (status.includes('confirmed') || status.includes('paid')) return '#c3e6cb';
    if (status.includes('pending')) return '#ffeaa7';
    if (status.includes('cancelled')) return '#f5c6cb';
    return '#bee5eb';
  }};
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.5rem;
  max-width: 900px;
  width: 95%;
  max-height: 95vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    max-width: 98%;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 3px solid #192f6a;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: #192f6a;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ModalClose = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background: #f8f9fa;
    color: #000;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.625rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.95rem;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.625rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.95rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const InfoBox = styled.div`
  background: #f8f9fa;
  border-left: 3px solid #007bff;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
`;

const ErrorBox = styled.div`
  background: #f8d7da;
  border-left: 3px solid #dc3545;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  color: #721c24;
`;

const SuccessBox = styled.div`
  background: #d4edda;
  border-left: 3px solid #28a745;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  color: #155724;
`;

const AuditTrailList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const AuditTrailItem = styled.li`
  padding: 1rem;
  background: #f8f9fa;
  border-left: 3px solid #ffc107;
  border-radius: 4px;
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const AuditDate = styled.div`
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.5rem;
`;

const AuditChange = styled.div`
  font-size: 0.95rem;
  color: #333;
  margin-bottom: 0.25rem;
`;

const AuditReason = styled.div`
  font-size: 0.85rem;
  color: #666;
  font-style: italic;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #dee2e6;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 1rem;
  color: #666;
  font-style: italic;
`;

const AfternoonTeaBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('calendar'); // Default to calendar view
  const [selectedDate, setSelectedDate] = useState(null); // For filtering by specific date

  // Merchandise pickups tab state
  const [merchandisePickups, setMerchandisePickups] = useState([]);
  const [merchandiseLoading, setMerchandiseLoading] = useState(false);
  const [merchandiseError, setMerchandiseError] = useState(null);
  const [merchandiseDate, setMerchandiseDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [merchandiseStatusFilter, setMerchandiseStatusFilter] = useState('all');

  // Vineyard tours tab — separate tables from afternoon tea
  const [vineyardBookings, setVineyardBookings] = useState([]);
  const [vineyardLoading, setVineyardLoading] = useState(false);
  const [vineyardError, setVineyardError] = useState(null);

  // Santa's Grotto tab — child name + age
  const [grottoBookings, setGrottoBookings] = useState([]);
  const [grottoLoading, setGrottoLoading] = useState(false);
  const [grottoError, setGrottoError] = useState(null);

  // Search for Santa tab — child name + age, plus adult ticket counts
  const [searchForSantaBookings, setSearchForSantaBookings] = useState([]);
  const [searchForSantaLoading, setSearchForSantaLoading] = useState(false);
  const [searchForSantaError, setSearchForSantaError] = useState(null);
  
  // Calendar view state
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);
  
  // Transfer booking modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferBooking, setTransferBooking] = useState(null);
  const [transferData, setTransferData] = useState({
    reason: '',
    adminName: '',
    sendEmail: true // Default to sending email
  });
  const [transferError, setTransferError] = useState(null);
  const [transferSuccessMessage, setTransferSuccessMessage] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // Individual booking references within a transaction
  const [individualBookings, setIndividualBookings] = useState([]);
  const [selectedBookingRef, setSelectedBookingRef] = useState(null);
  const [loadingIndividualBookings, setLoadingIndividualBookings] = useState(false);
  
  // VenPos booking details
  const [venposBookingDetails, setVenposBookingDetails] = useState(null);
  const [loadingVenposDetails, setLoadingVenposDetails] = useState(false);
  
  // Available events for transfer
  const [showAvailableEvents, setShowAvailableEvents] = useState(false);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [loadingAvailableEvents, setLoadingAvailableEvents] = useState(false);
  const [eventsSearchDate, setEventsSearchDate] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [transferringToVenpos, setTransferringToVenpos] = useState(false);
  const [eventsCurrentPage, setEventsCurrentPage] = useState(1);
  const [eventsTotalPages, setEventsTotalPages] = useState(1);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsPageSize] = useState(100);
  const [lastTransferredDate, setLastTransferredDate] = useState(null); // Track last transferred date for this transaction

  /** Local table bookings — transfer updates DB only, no Venpos (see `isLocalTableBooking`) */
  const [simpleTransferNewDate, setSimpleTransferNewDate] = useState('');
  const [simpleTransferNewTime, setSimpleTransferNewTime] = useState('');
  
  // Audit trail state
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [auditTrailBooking, setAuditTrailBooking] = useState(null);
  const [auditTrailData, setAuditTrailData] = useState([]);

  // Edit booking modal
  const [editBooking, setEditBooking] = useState(null);
  const [editForm, setEditForm] = useState({ visit_date: '', visit_time: '', admin_notes: '', tearoom_staff_notes: '', products: {}, number_of_guests: 2 });
  const [editSaving, setEditSaving] = useState(false);
  const [editDeleting, setEditDeleting] = useState(false);
  const [editError, setEditError] = useState(null);

  // Detailed list search
  const [detailsSearch, setDetailsSearch] = useState('');

  // Manual add booking modal state
  const [showManualAddModal, setShowManualAddModal] = useState(false);
  const [manualBookingForm, setManualBookingForm] = useState({
    event_id: '',
    event_date: '',
    title: 1, // 1=Mr, 2=Mrs, 3=Miss, 4=Ms, 5=Dr
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'GB',
    notes: '',
    admin_name: '',
    third_party_reference: ''
  });
  const [manualBookingEvents, setManualBookingEvents] = useState([]);
  const [loadingManualEvents, setLoadingManualEvents] = useState(false);
  const [creatingManualBooking, setCreatingManualBooking] = useState(false);
  const [manualBookingError, setManualBookingError] = useState(null);
  const [manualBookingSuccess, setManualBookingSuccess] = useState(null);
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  const [selectedEventTickets, setSelectedEventTickets] = useState([]);
  const [loadingEventTickets, setLoadingEventTickets] = useState(false);
  const [ticketQuantities, setTicketQuantities] = useState({});
  const [bookingProgress, setBookingProgress] = useState({ step: '', message: '' });
  const [guestModifiers, setGuestModifiers] = useState({}); // { guestIndex: { modifier, allergies } }
  const [availableModifiers, setAvailableModifiers] = useState([
    'Standard',
    'Vegetarian',
    'Vegan',
    'Gluten Free',
    'Dairy Free',
    'No Alcohol'
  ]);
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingPaymentBooking, setPendingPaymentBooking] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showAddAnotherPrompt, setShowAddAnotherPrompt] = useState(false);
  const [lastManualBookingCustomer, setLastManualBookingCustomer] = useState(null);
  const [lastManualBookingDate, setLastManualBookingDate] = useState(null);
  const [isAddingAnotherBooking, setIsAddingAnotherBooking] = useState(false);
  const [manualBookingDateFilter, setManualBookingDateFilter] = useState('');
  const [addAnotherCount, setAddAnotherCount] = useState(0);
  
  // Table/Coach Booking state (no VenPOS, local DB only)
  const [showTableBookingModal, setShowTableBookingModal] = useState(false);
  const [tableBookingForm, setTableBookingForm] = useState({
    booking_type: 'table', // 'table' or 'coach'
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    visit_date: '',
    visit_time: '',
    number_of_guests: 2,
    number_of_tables: 1,
    notes: '',
    admin_name: ''
  });
  const [creatingTableBooking, setCreatingTableBooking] = useState(false);
  const [tableBookingError, setTableBookingError] = useState(null);

  // Floor plan table selection
  const [selectedFloorPlanTable, setSelectedFloorPlanTable] = useState(null);
  const [showBookingTypeSelector, setShowBookingTypeSelector] = useState(false);

  /** Local tearoom table booking (no Venpos). GET payloads sometimes omit `booking_type`; `BEL-TABLE-*` refs are always table. */
  const isLocalTableBooking = (booking) => {
    if (!booking) return false;
    if (booking.booking_type === 'table') return true;
    const ref = booking.booking_reference || '';
    return /^BEL-TABLE-/i.test(ref);
  };

  /** Stable DB primary key for REST updates (handles string ids / alternate keys from API). */
  const resolveBookingDbId = (booking) => {
    if (!booking || typeof booking !== 'object') return null;
    const raw =
      booking.id ??
      booking.booking_id ??
      booking.ID ??
      booking.bookingId;
    if (raw === undefined || raw === null || raw === '') return null;
    const n = typeof raw === 'number' ? raw : parseInt(String(raw).trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const resolveBookingReference = (booking) =>
    String(booking?.booking_reference ?? booking?.bookingReference ?? '').trim();

  useEffect(() => {
    loadBookings();
  }, [dateFrom, dateTo]);

  const fetchIndividualBookings = async (transactionRef, fallbackBookingRef = null) => {
    const doFetch = async (ref) => {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not signed in');
      }
      const apiBase =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'https://www.belvoircastle.com/api'
          : '/api';
      const response = await fetch(
        `${apiBase}/user-bookings-proxy.php?ref=${encodeURIComponent(ref)}`,
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return { response, ref };
    };

    try {
      setLoadingIndividualBookings(true);
      setIndividualBookings([]);
      setSelectedBookingRef(null);
      setVenposBookingDetails(null);

      // Prefer transaction reference; API may key by transaction or booking reference
      console.log('🔍 [BOOKINGS] Fetching individual bookings for transaction:', transactionRef);
      let { response } = await doFetch(transactionRef);

      // If 404 and we have a fallback (e.g. booking_reference), try that (some APIs key by booking ref only)
      if (!response.ok && response.status === 404 && fallbackBookingRef && fallbackBookingRef !== transactionRef) {
        console.log('🔍 [BOOKINGS] 404 for transaction ref, trying booking reference:', fallbackBookingRef);
        const fallback = await doFetch(fallbackBookingRef);
        response = fallback.response;
      }

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();
      console.log('🔍 [BOOKINGS] Individual bookings response:', data);

      if (data.success && data.bookings && data.bookings.data) {
        setIndividualBookings(data.bookings.data);
        // Auto-select the first booking if there's only one
        if (data.bookings.data.length === 1) {
          const bookingRef = data.bookings.data[0].BookingReference;
          setSelectedBookingRef(bookingRef);
          // Automatically fetch VenPos details for single booking
          await fetchVenposBookingDetails(bookingRef);
        }
        console.log(`✅ [BOOKINGS] Found ${data.bookings.count} individual bookings`);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('❌ [BOOKINGS] Error fetching individual bookings:', error);
      setTransferError('Failed to fetch booking references: ' + error.message);
    } finally {
      setLoadingIndividualBookings(false);
    }
  };

  const fetchVenposBookingDetails = async (bookingRef) => {
    try {
      setLoadingVenposDetails(true);
      setVenposBookingDetails(null);
      
      console.log('📋 [VENPOS] Fetching booking details for:', bookingRef);
      
      // For local development, use live site; for production, use relative path
      const apiBase = window.location.hostname === 'localhost' 
        ? 'https://www.belvoircastle.com/api'
        : '/api';
      
      const response = await fetch(
        `${apiBase}/venpos-booking-details.php?booking_reference=${bookingRef}`
      );
      
      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📋 [VENPOS] Booking details response:', data);
      
      if (data.success && data.booking) {
        setVenposBookingDetails(data.booking);
        console.log('✅ [VENPOS] Loaded booking details:', data.booking.booking_reference);
      } else {
        throw new Error(data.error || 'Failed to load booking details');
      }
    } catch (error) {
      console.error('❌ [VENPOS] Error fetching booking details:', error);
      // Don't show error to user - just log it
    } finally {
      setLoadingVenposDetails(false);
    }
  };

  const handleBookingSelection = async (bookingRef) => {
    setSelectedBookingRef(bookingRef);
    await fetchVenposBookingDetails(bookingRef);
  };

  const fetchAvailableEvents = async (page = 1, append = false) => {
    try {
      setLoadingAvailableEvents(true);
      if (!append) {
        setAvailableEvents([]);
        setEventsCurrentPage(1);
      }
      
      // Get event type name from booking details
      const eventTypeName = venposBookingDetails?.event?.description || 'events';
      
      console.log('📅 [EVENTS] Fetching available events for:', eventTypeName, { 
        page, 
        searchDate: eventsSearchDate,
        templateId: venposBookingDetails?.event?.template_id
      });
      
      // Use live API endpoint when running locally; same origin /api when live
      const isLocal = window.location.hostname === 'localhost';
      const apiBase = isLocal
        ? 'https://www.belvoircastle.com/api'
        : `${window.location.origin}/api`;
      const apiUrl = `${apiBase}/venpos-available-events.php`;
      
      // Use date picker value if set, otherwise default to today
      let fromDate = new Date().toISOString();
      if (eventsSearchDate) {
        // Date picker returns YYYY-MM-DD format, convert to ISO
        const selectedDate = new Date(eventsSearchDate + 'T00:00:00');
        fromDate = selectedDate.toISOString();
        console.log('📅 [EVENTS] Using date picker date:', fromDate);
      }
      
      // No text search needed - all events are the same type (Afternoon Tea)
      let description = '';
      let code = '';
      
      // Get event template ID from the booking details (so we search for the same event type)
      const eventTemplateId = venposBookingDetails?.event?.template_id;
      
      if (!eventTemplateId) {
        console.warn('⚠️ [EVENTS] No event template ID found in booking details, using default (Afternoon Tea)');
      } else {
        console.log('📅 [EVENTS] Using event template ID from booking:', eventTemplateId);
      }
      
      // Build query params
      const params = new URLSearchParams({
        from: fromDate,
        page: page.toString(),
        pageSize: eventsPageSize.toString()
      });
      
      // Add event template ID if available
      if (eventTemplateId) {
        params.append('event_template_id', eventTemplateId);
      }
      
      if (description) {
        params.append('description', description);
      }
      if (code) {
        params.append('code', code);
      }
      
      const response = await fetch(`${apiUrl}?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type') || '';
      const text = await response.text();
      if (!contentType.includes('application/json')) {
        console.error('❌ [EVENTS] API returned non-JSON. URL:', apiUrl, 'Content-Type:', contentType, 'Preview:', text.slice(0, 200));
        throw new Error('Events API returned invalid response. Check that /api/venpos-available-events.php is deployed and accessible.');
      }
      
      const data = JSON.parse(text);
      console.log('📅 [EVENTS] Response:', data);
      
      if (data.success && data.events) {
        const newEvents = data.events.Items || [];
        const total = data.events.Total || 0;
        const pageSize = data.events.PageSize || eventsPageSize;
        const totalPages = Math.ceil(total / pageSize);
        
        if (append) {
          setAvailableEvents(prev => [...prev, ...newEvents]);
        } else {
          setAvailableEvents(newEvents);
        }
        
        setEventsCurrentPage(page);
        setEventsTotalPages(totalPages);
        setEventsTotal(total);
        
        console.log(`✅ [EVENTS] Loaded ${newEvents.length} events (Page ${page}/${totalPages}, Total: ${total})`);
      } else {
        throw new Error(data.error || 'Failed to fetch events');
      }
    } catch (err) {
      console.error('❌ [EVENTS] Error fetching available events:', err);
      alert(`Failed to load available events: ${err.message}`);
    } finally {
      setLoadingAvailableEvents(false);
    }
  };


  const handleLoadMoreEvents = () => {
    if (eventsCurrentPage < eventsTotalPages && !loadingAvailableEvents) {
      fetchAvailableEvents(eventsCurrentPage + 1, true);
    }
  };

  const handleDatePickerChange = (dateValue) => {
    setEventsSearchDate(dateValue);
    // Immediately search when date is selected
    if (dateValue) {
      fetchAvailableEvents(1, false);
    } else {
      // If cleared, reset to today
      fetchAvailableEvents(1, false);
    }
  };

  const handleShowAvailableEvents = async () => {
    // Make sure we have booking details first
    if (!venposBookingDetails) {
      alert('Please wait for booking details to load before selecting a new event.');
      return;
    }
    
    setShowAvailableEvents(true);
    setTransferSuccessMessage(null); // Clear success message when opening events
    
    // If we have a last transferred date, use it as default
    if (lastTransferredDate) {
      setEventsSearchDate(lastTransferredDate);
      console.log('📅 [EVENTS] Using last transferred date as default:', lastTransferredDate);
      // Automatically fetch events with this date
      await fetchAvailableEvents(1, false);
    } else {
      setEventsSearchDate('');
      await fetchAvailableEvents(1, false);
    }
  };

  const handleEventSelection = (event) => {
    setSelectedEvent(event);
    setTransferError(null);
    console.log('📅 [TRANSFER] Selected event:', event);
  };

  const handleConfirmTransfer = async () => {
    if (!selectedEvent) {
      setTransferError('Please select an event to transfer to');
      return;
    }

    if (!transferData.reason || !transferData.adminName) {
      setTransferError('Please enter a reason and your name for the audit trail');
      return;
    }

    // Confirm with the user
    const startDate = new Date(selectedEvent.Start);
    const eventDateStr = startDate.toLocaleDateString('en-GB', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    const eventTimeStr = startDate.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    const confirmMsg = `Are you sure you want to transfer this booking?\n\n` +
                       `From: ${formatDate(transferBooking.visit_date)} ${formatTime(transferBooking.visit_time)}\n` +
                       `To: ${eventDateStr} at ${eventTimeStr}\n\n` +
                       `Event: ${selectedEvent.Description}\n` +
                       `Available spaces: ${selectedEvent.Capacity - selectedEvent.Usage - selectedEvent.Reserved}\n\n` +
                       `This will update the booking in VenPos and the database.`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      setTransferringToVenpos(true);
      setTransferError(null);

      // Get the VenPos booking ID from the fetched details
      const venposBookingId = venposBookingDetails?.booking_id;
      if (!venposBookingId) {
        throw new Error('VenPos booking ID not found. Please try refreshing the booking details.');
      }

      console.log('🔄 [TRANSFER] Starting transfer...');
      console.log('  Booking ID:', venposBookingId);
      console.log('  New Event ID:', selectedEvent.Id);

      // Step 1: Transfer in VenPos
      const isLocal = window.location.hostname === 'localhost';
      const apiBase = isLocal
        ? 'https://www.belvoircastle.com/api'
        : `${window.location.origin}/api`;
      const transferApiUrl = `${apiBase}/venpos-transfer-booking.php`;

      const transferResponse = await fetch(transferApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          booking_id: venposBookingId,
          event_id: selectedEvent.Id
        })
      });

      const transferResult = await transferResponse.json();
      console.log('🔄 [TRANSFER] VenPos response:', transferResult);

      if (!transferResult.success) {
        throw new Error(transferResult.error || 'Failed to transfer booking in VenPos');
      }

      console.log('✅ [TRANSFER] VenPos transfer successful');

      // Step 2: Update our database
      const actualBookingRef = selectedBookingRef || 
                               (individualBookings.length === 1 ? individualBookings[0].BookingReference : null);

      if (!actualBookingRef) {
        throw new Error('No valid booking reference found');
      }

      // Format the new date and time for our database
      const newDateStr = startDate.toISOString().split('T')[0]; // YYYY-MM-DD
      const newTimeStr = startDate.toTimeString().substring(0, 5); // HH:MM

      // Format old date and time from the original booking
      const oldDateStr = transferBooking.visit_date ? 
        (transferBooking.visit_date.includes('T') 
          ? transferBooking.visit_date.split('T')[0] 
          : transferBooking.visit_date) 
        : '';
      const oldTimeStr = transferBooking.visit_time ? 
        (transferBooking.visit_time.length > 5 
          ? transferBooking.visit_time.substring(0, 5) 
          : transferBooking.visit_time) 
        : '';

      // Use transaction_reference if available, otherwise fall back to booking_reference
      const isManualBooking = transferBooking.admin_notes?.includes('Manual booking');
      const transactionRefForDb = transferBooking.transaction_reference || transferBooking.booking_reference;
      const payload = {
        booking_id: transferBooking.id,
        transaction_reference: transactionRefForDb,
        booking_reference: actualBookingRef,
        old_date: oldDateStr,
        old_time: oldTimeStr,
        new_date: newDateStr,
        new_time: newTimeStr,
        reason: transferData.reason,
        admin_name: transferData.adminName,
        customer_name: transferBooking.customer_name || '',
        customer_email: transferBooking.customer_email || ''
      };

      console.log('💾 [TRANSFER] Updating database with:', payload);

      const dbResponse = await fetch(`${config.apiEndpoint}/custom/v1/transfer-afternoon-tea-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const dbResult = await dbResponse.json();
      console.log('💾 [TRANSFER] Database response:', dbResult);

      if (!dbResponse.ok) {
        // WordPress REST API returns errors in a specific format
        const errorMessage = dbResult.message || dbResult.data?.message || `HTTP ${dbResponse.status}`;
        const errorDetails = dbResult.data || {};
        throw new Error(`Database update failed: ${errorMessage}${errorDetails.db_error ? ' (DB: ' + errorDetails.db_error + ')' : ''}`);
      }

      if (!dbResult.success) {
        throw new Error(dbResult.message || 'Failed to update database');
      }

      console.log('✅ [TRANSFER] Database updated successfully');

      // Step 3: Send confirmation email if requested
      if (transferData.sendEmail && transferBooking.customer_email) {
        try {
          setEmailSending(true);
          console.log('📧 [EMAIL] Sending transfer confirmation email...');

          const emailApiUrl = `${apiBase}/send-transfer-confirmation.php`;

          // Build transferred bookings list for email
          const transferredBookingsList = [{
            booking_reference: actualBookingRef,
            event_description: selectedEvent.Description
          }];

          const emailPayload = {
            customer_email: transferBooking.customer_email,
            customer_name: transferBooking.customer_name || 'Valued Customer',
            booking_reference: actualBookingRef,
            transaction_reference: transactionRefForDb,
            all_bookings_transferred: individualBookings.length === 1, // True if only one booking
            old_event_details: {
              description: venposBookingDetails?.event?.description || 'Afternoon Tea',
              start: transferBooking.visit_date + 'T' + (transferBooking.visit_time || '00:00:00')
            },
            new_event_details: {
              description: selectedEvent.Description,
              start: selectedEvent.Start,
              end: selectedEvent.End,
              duration: selectedEvent.Duration
            },
            transferred_bookings: transferredBookingsList
          };

          const emailResponse = await fetch(emailApiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(emailPayload)
          });

          const emailResult = await emailResponse.json();

          if (emailResult.success) {
            console.log('✅ [EMAIL] Confirmation email sent successfully');
            setEmailSent(true);
          } else {
            console.warn('⚠️ [EMAIL] Failed to send email:', emailResult.error);
            // Don't throw error - email is optional
          }
        } catch (emailErr) {
          console.error('❌ [EMAIL] Error sending email:', emailErr);
          // Don't throw error - email is optional, transfer was successful
        } finally {
          setEmailSending(false);
        }
      }

      // Store the transferred date for other bookings in this transaction
      setLastTransferredDate(newDateStr);
      
      // Check if there are other bookings in this transaction that might need transferring
      const otherBookings = individualBookings.filter(
        b => b.BookingReference !== actualBookingRef
      );
      
      // Reload bookings to show updated data
      await loadBookings();
      
      // Build success message
      let successMessage = `✅ Booking transferred successfully!\n\n` +
            `From: ${formatDate(transferBooking.visit_date)} ${formatTime(transferBooking.visit_time)}\n` +
            `To: ${eventDateStr} at ${eventTimeStr}\n\n` +
            `The booking has been updated in VenPos and the database.\n` +
            `An audit trail has been recorded.`;
      
      // Add email status to message
      if (transferData.sendEmail) {
        if (emailSent) {
          successMessage += `\n\n📧 Confirmation email sent to: ${transferBooking.customer_email}`;
        } else if (transferBooking.customer_email) {
          successMessage += `\n\n⚠️ Email sending failed, but transfer was successful.`;
        } else {
          successMessage += `\n\n⚠️ No customer email on file.`;
        }
      }
      
      // Add warning if there are other bookings
      if (otherBookings.length > 0) {
        successMessage += `\n\n⚠️ IMPORTANT REMINDER:\n` +
                         `This transaction contains ${otherBookings.length} other booking${otherBookings.length > 1 ? 's' : ''}:\n` +
                         otherBookings.map(b => `  • ${b.BookingReference}${b.EventDescription ? ' - ' + b.EventDescription : ''}`).join('\n') +
                         `\n\nYou may need to transfer ${otherBookings.length === 1 ? 'this booking' : 'these bookings'} to the same date (${eventDateStr}) to keep all bookings together.\n\n` +
                         `The modal will stay open and the next booking is pre-selected. The date picker will default to ${eventDateStr} to help you keep all bookings on the same date.`;
      }
      
      alert(successMessage);
      
      // Don't close modal if there are other bookings - let them transfer those too
      if (otherBookings.length > 0) {
        // Reset the form but keep modal open
        setSelectedEvent(null);
        setTransferData({ reason: '', adminName: '', sendEmail: true });
        setTransferError(null);
        setShowAvailableEvents(false); // Hide events list until they click the button again
        setEmailSending(false);
        setEmailSent(false);
        
        // Pre-select the next booking if available
        const nextBookingRef = otherBookings[0].BookingReference;
        setSelectedBookingRef(nextBookingRef);
        await fetchVenposBookingDetails(nextBookingRef);
        
        // Set the date picker to the transferred date (will be used when they click "Transfer to Different Date/Time")
        setEventsSearchDate(newDateStr);
        
        // Show success message
        setTransferSuccessMessage(
          `Transfer completed! Next booking (${nextBookingRef}) is pre-selected. ` +
          `The date picker will default to ${eventDateStr} when you click "Transfer to Different Date/Time" to help keep all bookings on the same date.`
        );
        // The warning banner will automatically show since individualBookings.length > 1
      } else {
        handleCloseTransferModal();
      }
    } catch (err) {
      console.error('❌ [TRANSFER] Error:', err);
      setTransferError(`Transfer failed: ${err.message}`);
      alert(`❌ Transfer failed: ${err.message}\n\nThe booking was not changed. Please try again.`);
    } finally {
      setTransferringToVenpos(false);
    }
  };


  // Extract the actual customer dietary choice from the stored modifier fields.
  // Handles three storage formats:
  //   Original booking:  name="Standard"             value="Dietary Requirments"
  //   Admin re-save:     name="Dietary requirement"  value="Standard: Dietary Requirments"
  //   Clean admin save:  name="Standard"             value=null
  const extractDietaryChoice = (name, value) => {
    const promptWords = ['dietary', 'requirement', 'requirment', 'requirments', 'allergen', 'question', 'prompt'];
    const isPrompt = (s) => s && promptWords.some((w) => s.toLowerCase().includes(w));
    const nameIsPrompt = isPrompt(name);
    const valueIsPrompt = isPrompt(value);
    if (nameIsPrompt && !valueIsPrompt) return value || name || '';
    if (valueIsPrompt && !nameIsPrompt) return name || '';
    // Both look like prompts — check for "Choice: Prompt" pattern inside value
    if (nameIsPrompt && valueIsPrompt && value) {
      const m = value.match(/^([^:]+):\s*.+$/);
      if (m) {
        const candidate = m[1].trim();
        if (!isPrompt(candidate)) return candidate;
      }
    }
    return name || value || '';
  };

  const handleOpenEditModal = (booking) => {
    const visitDate = booking.visit_date ? (booking.visit_date.includes('T') ? booking.visit_date.split('T')[0] : booking.visit_date) : '';
    const visitTime = booking.visit_time ? (booking.visit_time.length > 5 ? booking.visit_time.substring(0, 5) : booking.visit_time) : '';
    const products = {};
    (booking.products || []).forEach((p) => {
      const dietary = p.modifiers?.length
        ? p.modifiers.map((m) => extractDietaryChoice(m.modifier_name, m.modifier_value)).filter(Boolean).join(', ')
        : '';
      products[p.id] = { dietary: dietary || '', allergen_notes: p.allergen_notes || '' };
    });
    setEditBooking(booking);
    setEditForm({
      visit_date: visitDate,
      visit_time: visitTime,
      admin_notes: booking.admin_notes || '',
      tearoom_staff_notes: booking.tearoom_staff_notes || '',
      products,
      number_of_guests: isLocalTableBooking(booking)
        ? Math.max(1, parseInt(booking.number_of_guests, 10) || 1)
        : 2
    });
    setEditError(null);
  };

  const handleCloseEditModal = () => {
    setEditBooking(null);
    setEditForm({ visit_date: '', visit_time: '', admin_notes: '', tearoom_staff_notes: '', products: {}, number_of_guests: 2 });
    setEditError(null);
    setEditDeleting(false);
  };

  const handleDeleteTableBooking = async () => {
    if (!editBooking || !isLocalTableBooking(editBooking)) return;
    if (!window.confirm(`Permanently delete this table booking?\n\n${editBooking.booking_reference}\n${editBooking.customer_name}\n\nThis cannot be undone.`)) {
      return;
    }
    setEditDeleting(true);
    setEditError(null);
    try {
      const ref = resolveBookingReference(editBooking);
      const delPayload = {
        booking_reference: ref,
        bookingReference: ref
      };
      const delId = resolveBookingDbId(editBooking);
      if (delId != null) delPayload.booking_id = delId;

      const res = await fetch(`${config.apiEndpoint}/custom/v1/delete-afternoon-tea-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(delPayload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || data.error || `Delete failed (${res.status})`);
      }
      await loadBookings();
      handleCloseEditModal();
    } catch (err) {
      setEditError(err.message || 'Failed to delete booking');
    } finally {
      setEditDeleting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editBooking) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const productsPayload = Object.entries(editForm.products).map(([productId, data]) => ({
        product_id: parseInt(productId, 10),
        dietary: data.dietary || '',
        allergen_notes: data.allergen_notes || ''
      }));
      const ref = resolveBookingReference(editBooking);
      const body = {
        booking_reference: ref,
        bookingReference: ref,
        visit_date: editForm.visit_date || undefined,
        visit_time: editForm.visit_time || undefined,
        admin_notes: editForm.admin_notes || undefined,
        tearoom_staff_notes: editForm.tearoom_staff_notes ?? '',
        products: productsPayload
      };
      const eid = resolveBookingDbId(editBooking);
      if (eid != null) body.booking_id = eid;
      if (isLocalTableBooking(editBooking)) {
        const ng = Math.max(1, Math.min(50, parseInt(editForm.number_of_guests, 10) || 1));
        body.number_of_guests = ng;
      }
      const res = await fetch(`${config.apiEndpoint}/custom/v1/update-afternoon-tea-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error || 'Update failed');
      await loadBookings();
      handleCloseEditModal();
    } catch (err) {
      setEditError(err.message || 'Failed to update booking');
    } finally {
      setEditSaving(false);
    }
  };

  const handleOpenTransferModal = async (booking) => {
    setTransferBooking(booking);
    setTransferData({
      reason: '',
      adminName: '',
      sendEmail: true
    });
    setTransferError(null);
    setTransferSuccessMessage(null);
    setShowTransferModal(true);
    setShowAvailableEvents(false);
    setSelectedEvent(null);
    
    // Reset last transferred date when opening a new transaction
    setLastTransferredDate(null);

    if (isLocalTableBooking(booking)) {
      setIndividualBookings([]);
      setSelectedBookingRef(null);
      setVenposBookingDetails(null);
      setLoadingIndividualBookings(false);
      const vd = booking.visit_date
        ? (booking.visit_date.includes('T') ? booking.visit_date.split('T')[0] : booking.visit_date)
        : '';
      const vt = booking.visit_time
        ? (booking.visit_time.length > 5 ? booking.visit_time.substring(0, 5) : booking.visit_time)
        : '12:00';
      setSimpleTransferNewDate(vd);
      setSimpleTransferNewTime(vt);
      return;
    }

    setSimpleTransferNewDate('');
    setSimpleTransferNewTime('');
    
    // Fetch individual bookings: use transaction_reference if available, else booking_reference
    const refForLookup = booking.transaction_reference || booking.booking_reference;
    const fallbackRef = booking.transaction_reference ? booking.booking_reference : null;
    await fetchIndividualBookings(refForLookup, fallbackRef);
  };

  const handleCloseTransferModal = () => {
    setShowTransferModal(false);
    setTransferBooking(null);
    setTransferData({ reason: '', adminName: '', sendEmail: true });
    setTransferError(null);
    setShowAvailableEvents(false);
    setAvailableEvents([]);
    setEventsSearchDate('');
    setSelectedEvent(null);
    setTransferringToVenpos(false);
    setIndividualBookings([]);
    setSelectedBookingRef(null);
    setVenposBookingDetails(null);
    setEventsCurrentPage(1);
    setEventsTotalPages(1);
    setEventsTotal(0);
    setLastTransferredDate(null);
    setTransferSuccessMessage(null);
    setEmailSending(false);
    setEmailSent(false);
    setSimpleTransferNewDate('');
    setSimpleTransferNewTime('');
  };

  const handleConfirmSimpleTableTransfer = async () => {
    if (!transferBooking || !isLocalTableBooking(transferBooking)) return;

    if (!simpleTransferNewDate || !simpleTransferNewTime) {
      setTransferError('Please choose a new date and time for the booking.');
      return;
    }
    if (!transferData.reason?.trim() || !transferData.adminName?.trim()) {
      setTransferError('Please enter a reason and your name for the audit trail.');
      return;
    }

    const oldDateStr = transferBooking.visit_date
      ? (transferBooking.visit_date.includes('T')
        ? transferBooking.visit_date.split('T')[0]
        : transferBooking.visit_date)
      : '';
    const oldTimeStr = transferBooking.visit_time
      ? (transferBooking.visit_time.length > 5
        ? transferBooking.visit_time.substring(0, 5)
        : transferBooking.visit_time)
      : '12:00';

    const newTimeNormalized = simpleTransferNewTime.length > 5
      ? simpleTransferNewTime.substring(0, 5)
      : simpleTransferNewTime;

    if (!oldDateStr) {
      setTransferError('This booking has no visit date; edit the booking instead.');
      return;
    }

    if (oldDateStr === simpleTransferNewDate && oldTimeStr === newTimeNormalized) {
      setTransferError('The new date and time must be different from the current booking.');
      return;
    }

    const newDateObj = new Date(`${simpleTransferNewDate}T${newTimeNormalized}:00`);
    const newDateStr = simpleTransferNewDate;
    const newTimeStr = newTimeNormalized;

    const eventDateStr = newDateObj.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const eventTimeStr = newDateObj.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });

    const confirmMsg =
      `Transfer this table booking?\n\n` +
      `From: ${formatDate(transferBooking.visit_date)} ${formatTime(transferBooking.visit_time)}\n` +
      `To: ${eventDateStr} at ${eventTimeStr}\n\n` +
      `Only the tearoom calendar database will be updated (not Venpos).`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      setTransferringToVenpos(true);
      setTransferError(null);

      const isLocal = window.location.hostname === 'localhost';
      const apiBase = isLocal
        ? 'https://www.belvoircastle.com/api'
        : `${window.location.origin}/api`;

      const bookingRef = transferBooking.booking_reference || '';
      const transactionRefForDb = transferBooking.transaction_reference || transferBooking.booking_reference;
      const transferRowId = resolveBookingDbId(transferBooking);

      const payload = {
        booking_id: transferRowId ?? 0,
        transaction_reference: transactionRefForDb,
        booking_reference: bookingRef,
        old_date: oldDateStr,
        old_time: oldTimeStr,
        new_date: newDateStr,
        new_time: newTimeStr,
        reason: transferData.reason,
        admin_name: transferData.adminName,
        customer_name: transferBooking.customer_name || '',
        customer_email: transferBooking.customer_email || ''
      };

      const dbResponse = await fetch(`${config.apiEndpoint}/custom/v1/transfer-afternoon-tea-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const dbResult = await dbResponse.json();

      if (!dbResponse.ok) {
        const errorMessage = dbResult.message || dbResult.data?.message || `HTTP ${dbResponse.status}`;
        const errorDetails = dbResult.data || {};
        throw new Error(`Database update failed: ${errorMessage}${errorDetails.db_error ? ' (DB: ' + errorDetails.db_error + ')' : ''}`);
      }

      if (!dbResult.success) {
        throw new Error(dbResult.message || 'Failed to update database');
      }

      let emailSentFlag = false;
      if (transferData.sendEmail && transferBooking.customer_email) {
        try {
          setEmailSending(true);
          const emailApiUrl = `${apiBase}/send-transfer-confirmation.php`;
          const oldStartCombined = `${oldDateStr}T${oldTimeStr.length === 5 ? `${oldTimeStr}:00` : oldTimeStr}`;
          const newStartIso = `${newDateStr}T${newTimeStr}:00`;

          const emailPayload = {
            customer_email: transferBooking.customer_email,
            customer_name: transferBooking.customer_name || 'Valued Customer',
            booking_reference: bookingRef,
            transaction_reference: transactionRefForDb,
            all_bookings_transferred: true,
            old_event_details: {
              description: 'Afternoon Tea (table)',
              start: oldStartCombined
            },
            new_event_details: {
              description: 'Afternoon Tea (table)',
              start: newStartIso,
              end: newStartIso,
              duration: 120
            },
            transferred_bookings: [{
              booking_reference: bookingRef,
              event_description: 'Afternoon Tea table booking'
            }]
          };

          const emailResponse = await fetch(emailApiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(emailPayload)
          });

          const emailResult = await emailResponse.json();

          if (emailResult.success) {
            emailSentFlag = true;
            setEmailSent(true);
          }
        } catch (emailErr) {
          console.error('❌ [EMAIL] Table transfer email error:', emailErr);
        } finally {
          setEmailSending(false);
        }
      }

      await loadBookings();

      let successMessage =
        `✅ Table booking transferred successfully!\n\n` +
        `From: ${formatDate(transferBooking.visit_date)} ${formatTime(transferBooking.visit_time)}\n` +
        `To: ${eventDateStr} at ${eventTimeStr}\n\n` +
        `The tearoom calendar has been updated.`;

      if (transferData.sendEmail && transferBooking.customer_email) {
        if (emailSentFlag) {
          successMessage += `\n\n📧 Confirmation email sent to: ${transferBooking.customer_email}`;
        } else {
          successMessage += `\n\n⚠️ Email could not be sent; the transfer was saved.`;
        }
      }

      alert(successMessage);
      handleCloseTransferModal();
    } catch (err) {
      console.error('❌ [TRANSFER-TABLE] Error:', err);
      setTransferError(`Transfer failed: ${err.message}`);
      alert(`❌ Transfer failed: ${err.message}\n\nThe booking was not changed. Please try again.`);
    } finally {
      setTransferringToVenpos(false);
    }
  };

  // Manual Add Booking Functions
  const handleOpenManualAddModal = async () => {
    setIsAddingAnotherBooking(false);
    setAddAnotherCount(0);
    setShowManualAddModal(true);
    setManualBookingError(null);
    setManualBookingSuccess(null);
    setManualBookingForm({
      event_id: '',
      event_date: '',
      title: 1,
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      city: '',
      county: '',
      postcode: '',
      country: 'GB',
      notes: '',
      admin_name: ''
    });
    setEventSearchTerm('');
    setManualBookingEvents([]);
    setSelectedEventTickets([]);
    setTicketQuantities({});
    setGuestModifiers({});
    setBookingProgress({ step: '', message: '' });
    setManualBookingDateFilter('');
    // Load afternoon tea events (default flow)
    await fetchManualBookingEvents('', 'afternoon tea');
  };

  /** Open manual add modal for "add another" (e.g. entry ticket): same customer, all events, no event pre-selected */
  const handleOpenManualAddForAnother = async () => {
    setIsAddingAnotherBooking(true);
    setAddAnotherCount(prev => prev + 1);
    setShowAddAnotherPrompt(false);
    setShowManualAddModal(true);
    setManualBookingError(null);
    setManualBookingSuccess(null);
    const customer = lastManualBookingCustomer || {};
    setManualBookingForm({
      event_id: '',
      event_date: '',
      title: customer.title ?? 1,
      first_name: customer.first_name ?? '',
      last_name: customer.last_name ?? '',
      email: customer.email ?? '',
      phone: customer.phone ?? '',
      address_line1: customer.address_line1 ?? '',
      address_line2: customer.address_line2 ?? '',
      city: customer.city ?? '',
      county: customer.county ?? '',
      postcode: customer.postcode ?? '',
      country: customer.country ?? 'GB',
      notes: customer.notes ?? '',
      admin_name: customer.admin_name ?? ''
    });
    setEventSearchTerm('');
    setManualBookingEvents([]);
    setSelectedEventTickets([]);
    setTicketQuantities({});
    setGuestModifiers({});
    setBookingProgress({ step: '', message: '' });
    setManualBookingDateFilter(lastManualBookingDate || '');
    // Load all events (no afternoon tea filter) on the same date as the afternoon tea booking
    await fetchManualBookingEvents(lastManualBookingDate || '', '');
  };

  const handleCloseManualAddModal = () => {
    setShowManualAddModal(false);
    setSelectedFloorPlanTable(null); // Clear floor plan table selection
    setManualBookingForm({
      event_id: '',
      event_date: '',
      title: 1,
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      city: '',
      county: '',
      postcode: '',
      country: 'GB',
      notes: '',
      admin_name: ''
    });
    setManualBookingError(null);
    setManualBookingSuccess(null);
    setEventSearchTerm('');
    setManualBookingEvents([]);
    setSelectedEventTickets([]);
    setTicketQuantities({});
    setGuestModifiers({});
    setBookingProgress({ step: '', message: '' });
  };

  const fetchManualBookingEvents = async (searchDate = '', description = 'afternoon tea') => {
    try {
      setLoadingManualEvents(true);
      setManualBookingError(null);
      
      const isLocal = window.location.hostname === 'localhost';
      const apiBase = isLocal
        ? 'https://www.belvoircastle.com/api'
        : `${window.location.origin}/api`;
      
      const apiUrl = `${apiBase}/venpos-get-events.php`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          location_id: 978,
          search_date: searchDate || new Date().toISOString().split('T')[0],
          description: description || ''
        })
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch events');
      }
      
      setManualBookingEvents(result.data || []);
    } catch (err) {
      console.error('Error fetching events:', err);
      setManualBookingError('Failed to load available events: ' + err.message);
    } finally {
      setLoadingManualEvents(false);
    }
  };

  const fetchEventTickets = async (eventId) => {
    try {
      setLoadingEventTickets(true);
      setManualBookingError(null);
      
      const isLocal = window.location.hostname === 'localhost';
      const apiBase = isLocal
        ? 'https://www.belvoircastle.com/api'
        : `${window.location.origin}/api`;
      
      const apiUrl = `${apiBase}/venpos-get-event-details.php`;
      
      console.log('🎫 [TICKETS] Fetching tickets for event:', eventId);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event_id: eventId,
          location_id: 978
        })
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch event details');
      }
      
      console.log('🎫 [TICKETS] Found tickets:', result.tickets);
      
      setSelectedEventTickets(result.tickets || []);
      
      // Initialize quantities to 0
      const initialQuantities = {};
      (result.tickets || []).forEach(ticket => {
        initialQuantities[ticket.Id] = 0;
      });
      setTicketQuantities(initialQuantities);
      
    } catch (err) {
      console.error('Error fetching event tickets:', err);
      setManualBookingError('Failed to load event tickets: ' + err.message);
      setSelectedEventTickets([]);
    } finally {
      setLoadingEventTickets(false);
    }
  };

  const handleCreateManualBooking = async () => {
    try {
      // Validate required fields
      if (!manualBookingForm.event_id) {
        setManualBookingError('Please select an event');
        return;
      }
      
      // Calculate total guests from tickets
      const totalGuests = Object.values(ticketQuantities).reduce((sum, qty) => sum + qty, 0);
      if (totalGuests === 0) {
        setManualBookingError('Please select at least one ticket');
        return;
      }
      
      if (!manualBookingForm.first_name || !manualBookingForm.last_name) {
        setManualBookingError('Please enter customer name');
        return;
      }
      if (!manualBookingForm.email) {
        setManualBookingError('Please enter customer email');
        return;
      }
      if (!manualBookingForm.admin_name) {
        setManualBookingError('Please enter your admin name');
        return;
      }
      if (!manualBookingForm.postcode) {
        setManualBookingError('Please enter customer postcode');
        return;
      }
      if (!manualBookingForm.city || !manualBookingForm.city.trim()) {
        setManualBookingError('Please enter customer city');
        return;
      }
      
      setCreatingManualBooking(true);
      setManualBookingError(null);
      setBookingProgress({ step: 'reserving', message: 'Reserving tickets in VenPOS...' });
      
      console.log('🆕 [MANUAL-BOOKING] Creating booking...');
      console.log('  Event ID:', manualBookingForm.event_id);
      console.log('  Customer:', manualBookingForm.first_name, manualBookingForm.last_name);
      console.log('  Tickets:', ticketQuantities);
      console.log('  Total Guests:', totalGuests);
      
      const isLocal = window.location.hostname === 'localhost';
      const apiBase = isLocal
        ? 'https://www.belvoircastle.com/api'
        : `${window.location.origin}/api`;
      
      // Build tickets array from selections
      const tickets = selectedEventTickets
        .filter(ticket => ticketQuantities[ticket.Id] > 0)
        .map(ticket => ({
          AdmissionTicketId: ticket.Id, // WebSaleItemId
          EventTicketId: ticket.EventTicketId, // Needed for VenPOS reservation
          Quantity: ticketQuantities[ticket.Id],
          Price: parseFloat(ticket.Price),
          ProductCode: ticket.ProductCode,
          Description: ticket.Text || ticket.ProductDescription
        }));
      
      console.log('  Formatted tickets for VenPOS:', tickets);
      
      // Calculate total amount
      const totalAmount = tickets.reduce((sum, ticket) => sum + (ticket.Price * ticket.Quantity), 0);
      console.log('  Total Amount: £' + totalAmount.toFixed(2));

      // Construct products array with modifiers and allergies for each guest
      const products = [];
      let guestIndex = 0;
      selectedEventTickets.forEach(ticket => {
        const qty = ticketQuantities[ticket.Id] || 0;
        for (let i = 0; i < qty; i++) {
          const guestMod = guestModifiers[guestIndex] || {};
          products.push({
            web_sale_item_id: ticket.Id,
            product_name: ticket.Text || ticket.ProductDescription,
            quantity: 1,
            unit_price: parseFloat(ticket.Price),
            total_price: parseFloat(ticket.Price),
            event_id: manualBookingForm.event_id,
            modifier_name: guestMod.modifier || 'Standard',
            modifier_value: guestMod.modifier || 'Standard',
            allergen_notes: guestMod.allergies || ''
          });
          guestIndex++;
        }
      });

      console.log('  Products with modifiers/allergies:', products);
      
      setBookingProgress({ step: 'creating', message: '🎫 Creating booking in VenPOS...' });
      
      // Step 1: Create booking in VenPOS (reserve + confirm + mark as paid)
      const venposResponse = await fetch(`${apiBase}/venpos-create-booking.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event_id: manualBookingForm.event_id,
          event_date: manualBookingForm.event_date,
          title: manualBookingForm.title,
          first_name: manualBookingForm.first_name,
          last_name: manualBookingForm.last_name,
          email: manualBookingForm.email,
          phone: manualBookingForm.phone,
          address_line1: manualBookingForm.address_line1,
          address_line2: manualBookingForm.address_line2,
          city: manualBookingForm.city,
          county: manualBookingForm.county,
          postcode: manualBookingForm.postcode,
          country: manualBookingForm.country,
          notes: manualBookingForm.notes,
          third_party_reference: manualBookingForm.third_party_reference,
          tickets: tickets,
          total_amount: totalAmount
        })
      });
      
      const venposResult = await venposResponse.json();
      console.log('🔄 [MANUAL-BOOKING] VenPOS response:', venposResult);
      
      if (!venposResult.success) {
        throw new Error(venposResult.error || 'Failed to create booking in VenPOS');
      }
      
      console.log('✅ [MANUAL-BOOKING] VenPOS booking created');
      
      // API returns { success, message, data }; booking and payment_details live in data
      const d = venposResult.data || {};
      const bookingReference = d.booking_reference ||
                              venposResult.booking_reference ||
                              d.transaction_reference ||
                              `MANUAL-${Date.now()}`;
      const transactionReference = d.transaction_reference ||
                                  venposResult.transaction_reference ||
                                  bookingReference;
      const venposBookingId = d.venpos_booking_id ||
                             venposResult.venpos_booking_id ||
                             null;
      const basketHeaderId = d.basket_header_id || venposResult.basket_header_id || null;
      
      // Only save afternoon tea bookings to our database
      // Entry tickets and other events are only created in VenPOS
      const eventDate = manualBookingForm.event_date.split('T')[0]; // YYYY-MM-DD
      const eventTime = manualBookingForm.event_date.includes('T') 
        ? manualBookingForm.event_date.split('T')[1].substring(0, 5) 
        : '12:00';
      
      if (!isAddingAnotherBooking) {
        setBookingProgress({ step: 'saving', message: '💾 Saving booking to database...' });
        
        // Step 2: Add to our database
        
        const dbPayload = {
          booking_reference: bookingReference,
          transaction_reference: transactionReference,
          venpos_booking_id: venposBookingId,
          table_id: selectedFloorPlanTable ? selectedFloorPlanTable.id : null,
          customer_name: `${manualBookingForm.first_name} ${manualBookingForm.last_name}`,
          customer_email: manualBookingForm.email,
          customer_phone: manualBookingForm.phone || '',
          visit_date: eventDate,
          visit_time: eventTime,
          number_of_guests: totalGuests,
          total_amount: totalAmount,
          admin_notes: `Manual booking created by ${manualBookingForm.admin_name}. ${manualBookingForm.notes || ''}`,
          created_manually: true,
          products: products,
          third_party_reference: manualBookingForm.third_party_reference || ''
        };
        
        console.log('💾 [MANUAL-BOOKING] Saving to database:', dbPayload);
        
        const dbResponse = await fetch(`${config.apiEndpoint}/custom/v1/create-afternoon-tea-booking`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(dbPayload)
        });
        
        const dbResult = await dbResponse.json();
        console.log('💾 [MANUAL-BOOKING] Database response:', dbResult);
        
        if (!dbResponse.ok) {
          const errorMessage = dbResult.message || dbResult.data?.message || `HTTP ${dbResponse.status}`;
          throw new Error(`Database save failed: ${errorMessage}`);
        }
        
        if (!dbResult.success) {
          throw new Error(dbResult.message || 'Failed to save booking to database');
        }
      
        console.log('✅ [MANUAL-BOOKING] Booking saved to database');
      } else {
        console.log('ℹ️ [MANUAL-BOOKING] Entry ticket/other event - skipping database save (VenPOS only)');
      }
      
      // Remember customer details and date for "Add another booking" (e.g. entry ticket)
      setLastManualBookingCustomer({
        title: manualBookingForm.title,
        first_name: manualBookingForm.first_name,
        last_name: manualBookingForm.last_name,
        email: manualBookingForm.email,
        phone: manualBookingForm.phone || '',
        address_line1: manualBookingForm.address_line1 || '',
        address_line2: manualBookingForm.address_line2 || '',
        city: manualBookingForm.city || '',
        county: manualBookingForm.county || '',
        postcode: manualBookingForm.postcode || '',
        country: manualBookingForm.country || 'GB',
        notes: manualBookingForm.notes || '',
        admin_name: manualBookingForm.admin_name || ''
      });
      // Save the booking date (YYYY-MM-DD) so we can filter events on the same day
      setLastManualBookingDate(eventDate);
      // Close the manual add modal
      handleCloseManualAddModal();
      
      // Extract payment methods from VenPOS response (lives in data.payment_details; backend normalizes to TenderTypes)
      const paymentDetails = d.payment_details || venposResult.payment_details || {};
      const availablePaymentMethods = paymentDetails.TenderTypes ||
        paymentDetails.AvailableTenderTypes ||
        paymentDetails.TenderTypeList ||
        (Array.isArray(paymentDetails) ? paymentDetails : []);
      
      console.log('💳 [MANUAL-BOOKING] Payment details keys:', paymentDetails ? Object.keys(paymentDetails).slice(0, 20) : []);
      console.log('💳 [MANUAL-BOOKING] Available payment methods:', availablePaymentMethods?.length ?? 0, availablePaymentMethods);
      
      // Prepare pending payment booking data (include fields needed for confirmation email)
      const eventDesc = selectedEvent?.Description || selectedEvent?.Code || (isAddingAnotherBooking ? 'Event Booking' : 'Afternoon Tea - Aviary Tearoom');
      const pendingBooking = {
        bookingReference,
        venposBookingId,
        totalAmount,
        basketHeaderId,
        customerName: `${manualBookingForm.first_name} ${manualBookingForm.last_name}`,
        customerEmail: manualBookingForm.email,
        eventDate,
        eventTime,
        eventDescription: eventDesc,
        totalGuests,
        savedToDb: !isAddingAnotherBooking
      };
      
      // If VenPOS didn't return payment methods, use default so admin can still process payment
      const DEFAULT_TENDER_TYPE_ID = '4947';
      const methods = availablePaymentMethods.length > 0
        ? availablePaymentMethods
        : [{ Id: DEFAULT_TENDER_TYPE_ID, Text: 'Default / Cash (VenPOS)', Description: 'Default payment method' }];
      const defaultSelection = methods[0]?.Id ?? DEFAULT_TENDER_TYPE_ID;

      // Show payment modal
      setPendingPaymentBooking(pendingBooking);
      setPaymentMethods(methods);
      setSelectedPaymentMethod(defaultSelection);
      setShowPaymentModal(true);
      
    } catch (err) {
      console.error('❌ [MANUAL-BOOKING] Error:', err);
      setManualBookingError('Failed to create booking: ' + err.message);
      setBookingProgress({ step: '', message: '' });
    } finally {
      setCreatingManualBooking(false);
      setBookingProgress({ step: '', message: '' });
    }
  };

  const handleProcessPayment = async () => {
    if (!pendingPaymentBooking || !selectedPaymentMethod) {
      alert('Please select a payment method');
      return;
    }

    try {
      setProcessingPayment(true);

      console.log('💳 [PAYMENT] Processing payment...');
      console.log('  Booking ID:', pendingPaymentBooking.venposBookingId);
      console.log('  Payment Method:', selectedPaymentMethod);
      console.log('  Amount:', pendingPaymentBooking.totalAmount);

      const isLocal = window.location.hostname === 'localhost';
      const apiBase = isLocal
        ? 'https://www.belvoircastle.com/api'
        : `${window.location.origin}/api`;

      // Call payment API
      const response = await fetch(`${apiBase}/venpos-process-payment.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          booking_id: pendingPaymentBooking.venposBookingId,
          tender_type_id: selectedPaymentMethod,
          amount_to_pay: pendingPaymentBooking.totalAmount
        })
      });

      const result = await response.json();
      console.log('💳 [PAYMENT] Payment response:', result);

      if (!result.success) {
        throw new Error(result.error || 'Failed to process payment');
      }

      console.log('✅ [PAYMENT] Payment processed successfully');
      console.log('  Transaction Reference:', result.transaction_reference);

      // Update the booking in database with transaction reference
      const dbResponse = await fetch(`${config.apiEndpoint}/custom/v1/update-afternoon-tea-booking-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          booking_reference: pendingPaymentBooking.bookingReference,
          transaction_reference: result.transaction_reference,
          payment_status: 'paid'
        })
      });

      if (dbResponse.ok) {
        console.log('✅ [PAYMENT] Database updated with transaction reference');
      }

      // Send confirmation email for this event (Transaction Reference + QR) – for both afternoon tea and entry/other tickets
      if (pendingPaymentBooking.customerEmail) {
        try {
          const emailPayload = {
            customer_email: pendingPaymentBooking.customerEmail,
            customer_name: pendingPaymentBooking.customerName,
            reference_for_qr: result.transaction_reference,
            booking_reference: pendingPaymentBooking.bookingReference,
            visit_date: pendingPaymentBooking.eventDate,
            visit_time: pendingPaymentBooking.eventTime,
            event_description: pendingPaymentBooking.eventDescription || 'Afternoon Tea - Aviary Tearoom',
            payment_pending: false
          };
          const emailRes = await fetch(`${apiBase}/send-manual-booking-confirmation.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(emailPayload)
          });
          const emailResult = await emailRes.json();
          if (emailResult.success) {
            console.log('✅ [EMAIL] Confirmation email sent to', pendingPaymentBooking.customerEmail);
          } else {
            console.warn('⚠️ [EMAIL] Confirmation email failed:', emailResult.error);
          }
        } catch (emailErr) {
          console.warn('⚠️ [EMAIL] Confirmation email error:', emailErr);
        }
      }

      // Success!
      alert(
        `✅ Payment processed successfully!\n\n` +
        `Booking Reference: ${pendingPaymentBooking.bookingReference}\n` +
        `Transaction Reference: ${result.transaction_reference}\n` +
        `Amount Paid: £${pendingPaymentBooking.totalAmount.toFixed(2)}`
      );

      // Close modal and refresh, then offer to add another (e.g. entry ticket)
      setShowPaymentModal(false);
      setPendingPaymentBooking(null);
      loadBookings();
      setShowAddAnotherPrompt(true);

    } catch (err) {
      console.error('❌ [PAYMENT] Error:', err);
      alert('Failed to process payment: ' + err.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleViewAuditTrail = async (booking) => {
    setAuditTrailBooking(booking);
    setShowAuditTrail(true);
    
    try {
      const url = `${config.apiEndpoint}/custom/v1/get-booking-audit-trail?booking_id=${booking.id}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Failed to load audit trail');
      }
      
      const data = await response.json();
      console.log('📜 [AUDIT] Response:', data);
      
      if (data.success) {
        setAuditTrailData(data.audit_trail || []);
      } else {
        setAuditTrailData([]);
      }
    } catch (err) {
      console.error('❌ [AUDIT] Error:', err);
      setAuditTrailData([]);
    }
  };

  const handleCloseAuditTrail = () => {
    setShowAuditTrail(false);
    setAuditTrailBooking(null);
    setAuditTrailData([]);
  };

  // Table/Coach Booking Functions
  const handleOpenTableBookingModal = (type = 'table') => {
    setTableBookingForm({
      ...tableBookingForm,
      booking_type: type,
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      visit_date: '',
      visit_time: '',
      number_of_guests: type === 'coach' ? 20 : 2,
      number_of_tables: type === 'table' ? 1 : 0,
      notes: '',
      admin_name: ''
    });
    setTableBookingError(null);
    setShowTableBookingModal(true);
  };

  const handleCloseTableBookingModal = () => {
    setShowTableBookingModal(false);
    setTableBookingError(null);
    setSelectedFloorPlanTable(null); // Clear floor plan table selection
  };

  const handleCreateTableBooking = async () => {
    try {
      // Validation
      if (!tableBookingForm.customer_name || !tableBookingForm.customer_email) {
        setTableBookingError('Please enter customer name and email');
        return;
      }
      if (!tableBookingForm.visit_date || !tableBookingForm.visit_time) {
        setTableBookingError('Please select date and time');
        return;
      }
      if (!tableBookingForm.admin_name) {
        setTableBookingError('Please enter your admin name');
        return;
      }

      setCreatingTableBooking(true);
      setTableBookingError(null);

      // Generate a booking reference (no VenPOS involved)
      const typePrefix = tableBookingForm.booking_type === 'coach' ? 'COACH' : 'TABLE';
      const randomStr = Math.random().toString(36).substr(2, 8).toUpperCase();
      const bookingReference = `BEL-${typePrefix}-${randomStr}`;

      const typeLabel = tableBookingForm.booking_type === 'coach' ? 'Coach' : 'Table';
      const entryTicketNote = 'Please ensure this person has booked a sufficient entry ticket.';
      const createdBy = `${typeLabel} booking created by ${tableBookingForm.admin_name}.`;
      const adminNotesParts = [createdBy, entryTicketNote, tableBookingForm.notes].filter(Boolean);
      const adminNotes = adminNotesParts.join(' ').trim();

      const payload = {
        booking_type: tableBookingForm.booking_type,
        table_id: selectedFloorPlanTable ? selectedFloorPlanTable.id : null,
        booking_reference: bookingReference,
        customer_name: tableBookingForm.customer_name,
        customer_email: tableBookingForm.customer_email,
        customer_phone: tableBookingForm.customer_phone || '',
        visit_date: tableBookingForm.visit_date,
        visit_time: tableBookingForm.visit_time,
        number_of_guests: tableBookingForm.number_of_guests,
        total_amount: 0,
        payment_status: 'unpaid',
        booking_status: 'confirmed',
        admin_notes: adminNotes,
        created_manually: true
      };

      const response = await fetch(`${config.apiEndpoint}/custom/v1/create-afternoon-tea-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to create booking');
      }

      // Send confirmation email (no QR code)
      const apiBase = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'https://www.belvoircastle.com/api'
        : `${window.location.origin}/api`;
      try {
        const emailRes = await fetch(`${apiBase}/send-table-booking-confirmation.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_email: tableBookingForm.customer_email,
            customer_name: tableBookingForm.customer_name,
            booking_reference: bookingReference,
            visit_date: tableBookingForm.visit_date,
            visit_time: tableBookingForm.visit_time,
            number_of_guests: tableBookingForm.number_of_guests,
            booking_type: tableBookingForm.booking_type,
            notes: tableBookingForm.notes || ''
          })
        });
        const emailResult = await emailRes.json();
        if (emailResult.success) {
          alert(`✅ ${typeLabel} booking created successfully!\n\nReference: ${bookingReference}\n\nConfirmation email sent to ${tableBookingForm.customer_email}`);
        } else {
          alert(`✅ ${typeLabel} booking created successfully!\n\nReference: ${bookingReference}\n\n(Confirmation email could not be sent: ${emailResult.error || 'unknown error'})`);
        }
      } catch (emailErr) {
        console.warn('Table booking confirmation email failed:', emailErr);
        alert(`✅ ${typeLabel} booking created successfully!\n\nReference: ${bookingReference}\n\n(Confirmation email could not be sent.)`);
      }

      handleCloseTableBookingModal();
      loadBookings();

    } catch (err) {
      console.error('❌ [TABLE-BOOKING] Error:', err);
      setTableBookingError('Failed to create booking: ' + err.message);
    } finally {
      setCreatingTableBooking(false);
    }
  };

  // Floor plan table click handlers
  const handleFloorPlanTableClick = (tableInfo) => {
    console.log('🎯 [FLOOR-PLAN] Table clicked:', tableInfo);
    
    if (tableInfo.booking) {
      // Occupied table - open booking details
      handleOpenEditModal(tableInfo.booking);
    } else {
      // Free table - ask which booking type to create
      setSelectedFloorPlanTable(tableInfo.table);
      setShowBookingTypeSelector(true);
    }
  };

  const handleSelectBookingTypeFromFloorPlan = (type) => {
    setShowBookingTypeSelector(false);
    
    if (type === 'afternoon_tea') {
      // Open afternoon tea booking modal with table pre-selected
      handleOpenManualAddModal();
    } else {
      // Open table/coach booking modal with table and type pre-selected
      setTableBookingForm({
        ...tableBookingForm,
        booking_type: type,
        visit_date: selectedCalendarDay || toLocalDateString(new Date()),
        visit_time: '',
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        number_of_guests: type === 'coach' ? 20 : 2,
        number_of_tables: type === 'table' ? 1 : 0,
        notes: '',
        admin_name: ''
      });
      setTableBookingError(null);
      setShowTableBookingModal(true);
    }
  };

  // Get booking color based on type
  const getBookingColor = (booking) => {
    if (isLocalTableBooking(booking)) {
      return '#17a2b8';
    }
    const type = booking.booking_type || 'afternoon_tea';
    switch (type) {
      case 'coach':
        return '#fd7e14'; // Orange for coach bookings
      case 'table':
        return '#17a2b8'; // Cyan for table bookings
      case 'afternoon_tea':
      default:
        return '#28a745'; // Green for afternoon tea
    }
  };

  const getBookingTypeLabel = (booking) => {
    if (isLocalTableBooking(booking)) {
      return 'Table';
    }
    const type = booking.booking_type || 'afternoon_tea';
    switch (type) {
      case 'coach':
        return 'Coach';
      case 'table':
        return 'Table';
      case 'afternoon_tea':
      default:
        return 'Afternoon Tea';
    }
  };

  // Calendar View Functions - use local date strings to avoid timezone misalignment
  const toLocalDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { year, month, daysInMonth, startingDayOfWeek, firstDay, lastDay };
  };

  const getBookingsForDate = (date) => {
    const dateStr = toLocalDateString(date);
    return bookings.filter(b => b.visit_date?.split('T')[0] === dateStr);
  };

  const getCalendarMonthBookingsSummary = () => {
    const { year, month } = getDaysInMonth(calendarDate);
    const monthBookings = bookings.filter(b => {
      if (!b.visit_date) return false;
      const bookingDate = new Date(b.visit_date);
      return bookingDate.getFullYear() === year && bookingDate.getMonth() === month;
    });
    
    const totalBookings = monthBookings.length;
    const totalGuests = monthBookings.reduce((sum, b) => {
      return sum + (b.products?.reduce((pSum, p) => pSum + (parseInt(p.quantity, 10) || 1), 0) || parseInt(b.number_of_guests, 10) || 0);
    }, 0);
    
    const totalRevenue = monthBookings.reduce((sum, b) => sum + (parseFloat(b.total_amount) || 0), 0);
    
    // Group by date
    const byDate = {};
    monthBookings.forEach(b => {
      const dateStr = b.visit_date.split('T')[0];
      if (!byDate[dateStr]) {
        byDate[dateStr] = { bookings: 0, guests: 0 };
      }
      byDate[dateStr].bookings++;
      byDate[dateStr].guests += b.products?.reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 1), 0) || parseInt(b.number_of_guests, 10) || 0;
    });
    
    const daysWithBookings = Object.keys(byDate).length;
    const busiestDay = Object.entries(byDate).sort((a, b) => b[1].bookings - a[1].bookings)[0];
    
    return {
      totalBookings,
      totalGuests,
      totalRevenue,
      daysWithBookings,
      busiestDay: busiestDay ? {
        date: busiestDay[0],
        bookings: busiestDay[1].bookings,
        guests: busiestDay[1].guests
      } : null
    };
  };

  const renderCalendarView = () => {
    const { year, month, daysInMonth, startingDayOfWeek } = getDaysInMonth(calendarDate);
    const monthName = calendarDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    const today = new Date();
    const todayStr = toLocalDateString(today);
    
    // Build calendar days array
    const calendarDays = [];
    
    // Previous month days
    const prevMonthDays = startingDayOfWeek;
    const prevMonth = new Date(year, month, 0);
    const prevMonthLastDay = prevMonth.getDate();
    
    for (let i = prevMonthDays - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const date = new Date(year, month - 1, day);
      calendarDays.push({ date, isOtherMonth: true, day });
    }
    
    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      calendarDays.push({ date, isOtherMonth: false, day });
    }
    
    // Next month days to fill the grid
    const remainingDays = 42 - calendarDays.length; // 6 weeks * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      calendarDays.push({ date, isOtherMonth: true, day });
    }
    
    const monthSummary = getCalendarMonthBookingsSummary();
    
    return (
      <>
        <ScreenSection>
          <CalendarContainer>
            <CalendarHeader>
              <CalendarHeaderTitle>{monthName}</CalendarHeaderTitle>
              <CalendarNav>
                <CalendarNavButton onClick={() => {
                  const newDate = new Date(calendarDate);
                  newDate.setMonth(newDate.getMonth() - 1);
                  setCalendarDate(newDate);
                  setSelectedCalendarDay(null);
                }}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </CalendarNavButton>
                <CalendarNavButton onClick={() => {
                  setCalendarDate(new Date());
                  setSelectedCalendarDay(null);
                }}>
                  Today
                </CalendarNavButton>
                <CalendarNavButton onClick={() => {
                  const newDate = new Date(calendarDate);
                  newDate.setMonth(newDate.getMonth() + 1);
                  setCalendarDate(newDate);
                  setSelectedCalendarDay(null);
                }}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </CalendarNavButton>
              </CalendarNav>
            </CalendarHeader>
            
            <CalendarGrid>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <CalendarDayHeader key={day}>{day}</CalendarDayHeader>
              ))}
              
              {calendarDays.map((dayData, index) => {
                const dateStr = toLocalDateString(dayData.date);
                const dayBookings = getBookingsForDate(dayData.date);
                const hasBookings = dayBookings.length > 0;
                const isToday = dateStr === todayStr;
                const isSelected = selectedCalendarDay === dateStr;
                const totalGuests = dayBookings.reduce((sum, b) => {
                  return sum + (b.products?.reduce((pSum, p) => pSum + (parseInt(p.quantity, 10) || 1), 0) || parseInt(b.number_of_guests, 10) || 0);
                }, 0);
                
                return (
                  <CalendarDay
                    key={index}
                    isOtherMonth={dayData.isOtherMonth}
                    isToday={isToday}
                    isSelected={isSelected}
                    hasBookings={hasBookings}
                    onClick={() => {
                      if (hasBookings) {
                        setSelectedCalendarDay(dateStr);
                      }
                    }}
                  >
                    <CalendarDayNumber 
                      isToday={isToday} 
                      isOtherMonth={dayData.isOtherMonth}
                    >
                      {dayData.day}
                    </CalendarDayNumber>
                    
                    {hasBookings && (
                      <CalendarDayBookings>
                        <CalendarDayBadge count={dayBookings.length}>
                          {dayBookings.length} {dayBookings.length === 1 ? 'booking' : 'bookings'}
                        </CalendarDayBadge>
                        <CalendarDayGuests>
                          <FontAwesomeIcon icon={faUsers} style={{ marginRight: '0.25rem', fontSize: '0.7rem' }} />
                          {totalGuests} guest{totalGuests !== 1 ? 's' : ''}
                        </CalendarDayGuests>
                        {/* Booking type indicators */}
                        <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.35rem', flexWrap: 'wrap' }}>
                          {(() => {
                            const types = {
                              afternoon_tea: dayBookings.filter(b => (b.booking_type || 'afternoon_tea') === 'afternoon_tea').length,
                              table: dayBookings.filter(b => isLocalTableBooking(b)).length,
                              coach: dayBookings.filter(b => b.booking_type === 'coach').length
                            };
                            return (
                              <>
                                {types.afternoon_tea > 0 && (
                                  <div style={{
                                    background: '#28a745',
                                    color: 'white',
                                    padding: '0.15rem 0.4rem',
                                    borderRadius: '8px',
                                    fontSize: '0.65rem',
                                    fontWeight: '700',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.2rem'
                                  }}>
                                    <FontAwesomeIcon icon={faUtensils} style={{ fontSize: '0.6rem' }} />
                                    {types.afternoon_tea}
                                  </div>
                                )}
                                {types.table > 0 && (
                                  <div style={{
                                    background: '#17a2b8',
                                    color: 'white',
                                    padding: '0.15rem 0.4rem',
                                    borderRadius: '8px',
                                    fontSize: '0.65rem',
                                    fontWeight: '700',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.2rem'
                                  }}>
                                    <FontAwesomeIcon icon={faUtensils} style={{ fontSize: '0.6rem' }} />
                                    {types.table}
                                  </div>
                                )}
                                {types.coach > 0 && (
                                  <div style={{
                                    background: '#fd7e14',
                                    color: 'white',
                                    padding: '0.15rem 0.4rem',
                                    borderRadius: '8px',
                                    fontSize: '0.65rem',
                                    fontWeight: '700',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.2rem'
                                  }}>
                                    <FontAwesomeIcon icon={faUsers} style={{ fontSize: '0.6rem' }} />
                                    {types.coach}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </CalendarDayBookings>
                    )}
                  </CalendarDay>
                );
              })}
            </CalendarGrid>
            
            <CalendarLegend>
              <CalendarLegendItem>
                <CalendarLegendColor color="linear-gradient(135deg, #28a745 0%, #218838 100%)" />
                <span>Low (1-50 bookings)</span>
              </CalendarLegendItem>
              <CalendarLegendItem>
                <CalendarLegendColor color="linear-gradient(135deg, #ffc107 0%, #e0a800 100%)" />
                <span>Medium (51-80 bookings)</span>
              </CalendarLegendItem>
              <CalendarLegendItem>
                <CalendarLegendColor color="linear-gradient(135deg, #dc3545 0%, #c82333 100%)" />
                <span>High (81+ bookings)</span>
              </CalendarLegendItem>
              <CalendarLegendItem>
                <CalendarLegendColor color="#fff3cd" style={{ border: '2px solid #ffc107' }} />
                <span>Today</span>
              </CalendarLegendItem>
            </CalendarLegend>
            
            <div style={{ 
              marginTop: '1.5rem', 
              padding: '1rem', 
              background: '#f8f9fa', 
              borderRadius: '8px',
              border: '2px solid #e9ecef'
            }}>
              <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', fontWeight: '600', color: '#495057' }}>
                Booking Types
              </h4>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: '24px', height: '4px', background: '#28a745', borderRadius: '2px' }} />
                  <span style={{ fontSize: '0.9rem', color: '#495057' }}>Afternoon Tea</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: '24px', height: '4px', background: '#17a2b8', borderRadius: '2px' }} />
                  <span style={{ fontSize: '0.9rem', color: '#495057' }}>Table Booking</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ width: '24px', height: '4px', background: '#fd7e14', borderRadius: '2px' }} />
                  <span style={{ fontSize: '0.9rem', color: '#495057' }}>Coach Booking</span>
                </div>
              </div>
            </div>
          </CalendarContainer>
        </ScreenSection>
        
        {/* Month Summary */}
        <ScreenSection>
          <CalendarSummaryPanel>
            <h3 style={{ margin: '0 0 1.5rem 0', color: '#192f6a', fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FontAwesomeIcon icon={faChartBar} />
              {monthName} Summary
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
              <div style={{ padding: '1rem', background: '#e7f3ff', borderRadius: '8px', border: '2px solid #007bff' }}>
                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>Total Bookings</div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#007bff' }}>{monthSummary.totalBookings}</div>
              </div>
              <div style={{ padding: '1rem', background: '#d4edda', borderRadius: '8px', border: '2px solid #28a745' }}>
                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>Total Guests</div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#28a745' }}>{monthSummary.totalGuests}</div>
              </div>
              <div style={{ padding: '1rem', background: '#f8d7da', borderRadius: '8px', border: '2px solid #dc3545' }}>
                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>Days with Bookings</div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#dc3545' }}>{monthSummary.daysWithBookings}</div>
              </div>
            </div>
            {monthSummary.busiestDay && (
              <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px', border: '2px solid #6c757d' }}>
                <div style={{ fontSize: '1rem', color: '#666', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FontAwesomeIcon icon={faFire} />
                  Busiest Day
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#333' }}>
                  {new Date(monthSummary.busiestDay.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                  <span style={{ marginLeft: '1rem', fontSize: '1rem', color: '#666' }}>
                    ({monthSummary.busiestDay.bookings} bookings, {monthSummary.busiestDay.guests} guests)
                  </span>
                </div>
              </div>
            )}
          </CalendarSummaryPanel>
        </ScreenSection>
        
        {/* Selected Day Details Modal */}
        {selectedCalendarDay && (() => {
          // Parse as local noon so weekday/month/day display correctly in all timezones
          const selectedDate = new Date(selectedCalendarDay + 'T12:00:00');
          const dayBookings = bookings.filter(b => b.visit_date?.split('T')[0] === selectedCalendarDay);
          const dayName = selectedDate.toLocaleDateString('en-GB', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          });
          
          // Group bookings by time slot
          const bookingsByTime = {};
          dayBookings.forEach(booking => {
            const time = booking.visit_time || 'No time';
            if (!bookingsByTime[time]) {
              bookingsByTime[time] = [];
            }
            bookingsByTime[time].push(booking);
          });
          
          // Calculate dietary requirements summary
          const dietarySummary = {};
          dayBookings.forEach(booking => {
            booking.products?.forEach(product => {
              if (product.modifiers && product.modifiers.length > 0) {
                product.modifiers.forEach(mod => {
                  if (mod.modifier_name) {
                    const qty = parseInt(product.quantity, 10) || 1;
                    dietarySummary[mod.modifier_name] = (dietarySummary[mod.modifier_name] || 0) + qty;
                  }
                });
              }
            });
          });
          
          // Inferred / unknown modifier alerts (written by reconciliation tool)
          const inferredAlerts = [];
          dayBookings.forEach(booking => {
            booking.products?.forEach(product => {
              product.modifiers?.forEach(m => {
                if (m.modifier_data && typeof m.modifier_data === 'string' &&
                    (m.modifier_data.toLowerCase().includes('inferred') ||
                     m.modifier_data.toLowerCase().includes('mixed') ||
                     m.modifier_data.toLowerCase().includes('unknown'))) {
                  inferredAlerts.push({
                    customer: booking.customer_name,
                    time: booking.visit_time,
                    product: product.product_name,
                    modifier: m.modifier_name,
                    note: m.modifier_data,
                    isMixed: m.modifier_data.toLowerCase().includes('mixed') || m.modifier_data.toLowerCase().includes('unknown'),
                  });
                }
              });
            });
          });

          // Allergen alerts
          const allergenAlerts = [];
          dayBookings.forEach(booking => {
            booking.products?.forEach(product => {
              if (product.allergen_notes && product.allergen_notes.trim()) {
                allergenAlerts.push({
                  customer: booking.customer_name,
                  time: booking.visit_time,
                  notes: product.allergen_notes,
                  product: product.product_name
                });
              }
              product.questions?.forEach(q => {
                if (q.answer && q.answer.trim()) {
                  allergenAlerts.push({
                    customer: booking.customer_name,
                    time: booking.visit_time,
                    notes: q.answer,
                    question: q.question
                  });
                }
              });
            });
          });
          
          const totalGuests = dayBookings.reduce((sum, b) => {
            return sum + (b.products?.reduce((pSum, p) => pSum + (parseInt(p.quantity, 10) || 1), 0) || parseInt(b.number_of_guests, 10) || 0);
          }, 0);
          const totalRevenue = dayBookings.reduce((sum, b) => sum + (parseFloat(b.total_amount) || 0), 0);
          
          return (
            <Modal onClick={() => setSelectedCalendarDay(null)}>
              <ModalContent 
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: '1200px', maxHeight: '90vh' }}
              >
                {/* Header */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  paddingBottom: '1.5rem',
                  borderBottom: '3px solid #007bff',
                  marginBottom: '2rem'
                }}>
                  <div>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: '#192f6a', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <FontAwesomeIcon icon={faCalendar} />
                      {dayName}
                    </div>
                    <div style={{ fontSize: '1rem', color: '#666' }}>
                      Afternoon Tea Bookings Overview
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Button
                      onClick={() => handlePrintDay(selectedCalendarDay)}
                      style={{ fontSize: '0.9rem', padding: '0.6rem 1.1rem', whiteSpace: 'nowrap', background: '#192f6a', color: '#fff', border: 'none' }}
                    >
                      <FontAwesomeIcon icon={faPrint} style={{ marginRight: '0.5rem' }} />
                      Print Day Sheet
                    </Button>
                    <button
                      onClick={() => setSelectedCalendarDay(null)}
                      style={{ background: 'transparent', border: 'none', fontSize: '2rem', cursor: 'pointer', color: '#999', lineHeight: '1', padding: '0.5rem' }}
                    >
                      ×
                    </button>
                  </div>
                </div>
                
                {/* Quick Stats */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  <div style={{ 
                    padding: '1.25rem', 
                    background: 'linear-gradient(135deg, #e7f3ff 0%, #cfe7ff 100%)',
                    borderRadius: '12px',
                    border: '2px solid #007bff',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.9rem', color: '#004085', marginBottom: '0.5rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FontAwesomeIcon icon={faList} />
                      Bookings
                    </div>
                    <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#007bff' }}>
                      {dayBookings.length}
                    </div>
                  </div>
                  <div style={{ 
                    padding: '1.25rem', 
                    background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                    borderRadius: '12px',
                    border: '2px solid #28a745',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.9rem', color: '#155724', marginBottom: '0.5rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FontAwesomeIcon icon={faUsers} />
                      Guests
                    </div>
                    <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#28a745' }}>
                      {totalGuests}
                    </div>
                  </div>
                </div>
                
                {/* Inferred / Unknown Dietary Alerts */}
                {inferredAlerts.length > 0 && (
                  <div style={{
                    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                    border: '3px solid #f59e0b',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    marginBottom: '2rem'
                  }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#92400e', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      ⚑ {inferredAlerts.length} Dietary Requirement{inferredAlerts.length !== 1 ? 's' : ''} Need Checking
                    </div>
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {inferredAlerts.map((alert, idx) => (
                        <div key={idx} style={{
                          background: 'white',
                          padding: '1rem',
                          borderRadius: '8px',
                          border: `2px solid ${alert.isMixed ? '#94a3b8' : '#f59e0b'}`
                        }}>
                          <div style={{ fontWeight: '700', color: alert.isMixed ? '#475569' : '#92400e', marginBottom: '0.4rem' }}>
                            {alert.isMixed ? '⚠ Unknown' : '⚑ Inferred'} — {alert.customer} ({formatTime(alert.time)})
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                            {alert.product} · Recorded as: <strong>{alert.modifier}</strong>
                          </div>
                          <div style={{ fontSize: '0.9rem', color: alert.isMixed ? '#475569' : '#92400e', fontStyle: 'italic' }}>
                            {alert.note}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Historical Bookings Alert */}
                {(() => {
                  const historicalBookings = dayBookings.filter(b =>
                    b.booking_date && new Date(b.booking_date.replace(' ', 'T')) < new Date('2026-03-25T00:00:00') && !/(TABLE|COACH)/i.test(b.booking_reference || '')
                  );
                  if (historicalBookings.length === 0) return null;
                  return (
                    <div style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', border: '3px solid #6366f1', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#3730a3', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        📋 {historicalBookings.length} Historical Booking{historicalBookings.length !== 1 ? 's' : ''} — Please Double Check Allergen Information
                      </div>
                      <div style={{ display: 'grid', gap: '0.6rem' }}>
                        {historicalBookings.map((b, idx) => (
                          <div key={idx} style={{ background: 'white', padding: '0.75rem 1rem', borderRadius: '8px', border: '1px solid #a5b4fc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <span style={{ fontWeight: '700', color: '#3730a3' }}>{b.customer_name}</span>
                              <span style={{ marginLeft: '0.75rem', fontSize: '0.85rem', color: '#666', fontFamily: 'monospace' }}>{b.booking_reference}</span>
                            </div>
                            <span style={{ fontSize: '0.85rem', color: '#4338ca' }}>{formatTime(b.visit_time)}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#4338ca', fontStyle: 'italic' }}>
                        These bookings were made before 25 March 2026. Allergen data may not have been fully captured — confirm with each customer before their visit.
                      </div>
                    </div>
                  );
                })()}

                {/* Allergen Alerts */}
                {allergenAlerts.length > 0 && (
                  <div style={{
                    background: 'linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)',
                    border: '3px solid #ffc107',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    marginBottom: '2rem'
                  }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#856404', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <span>{allergenAlerts.length} Allergen/Dietary Alert{allergenAlerts.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {allergenAlerts.map((alert, idx) => (
                        <div key={idx} style={{ 
                          background: 'white', 
                          padding: '1rem', 
                          borderRadius: '8px',
                          border: '2px solid #ffc107'
                        }}>
                          <div style={{ fontWeight: '700', color: '#856404', marginBottom: '0.5rem' }}>
                            {alert.customer} ({formatTime(alert.time)})
                          </div>
                          {alert.product && (
                            <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                              Product: {alert.product}
                            </div>
                          )}
                          {alert.question && (
                            <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                              {alert.question}:
                            </div>
                          )}
                          <div style={{ color: '#721c24', fontSize: '1rem', fontWeight: '600' }}>
                            {alert.notes}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(() => {
                  const tearoomDayList = dayBookings.filter((b) => b.tearoom_staff_notes?.trim());
                  if (tearoomDayList.length === 0) return null;
                  return (
                    <div style={{
                      background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                      border: '3px solid #f59e0b',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      marginBottom: '2rem'
                    }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#92400e', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FontAwesomeIcon icon={faCommentDots} />
                        <span>{tearoomDayList.length} Tearoom special request{tearoomDayList.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {tearoomDayList.map((b) => (
                          <div key={b.id} style={{
                            background: 'white',
                            padding: '1rem',
                            borderRadius: '8px',
                            border: '2px solid #fcd34d'
                          }}>
                            <div style={{ fontWeight: '700', color: '#92400e', marginBottom: '0.35rem' }}>
                              {b.customer_name}{' '}
                              <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#666', fontFamily: 'monospace' }}>({b.booking_reference})</span>
                              {b.visit_time && <span style={{ marginLeft: '0.5rem', color: '#64748b' }}>{formatTime(b.visit_time)}</span>}
                            </div>
                            <div style={{ color: '#78350f', fontSize: '1rem', fontWeight: '600', whiteSpace: 'pre-wrap' }}>{b.tearoom_staff_notes}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Dietary Requirements Summary */}
                {Object.keys(dietarySummary).length > 0 && (
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    marginBottom: '2rem',
                    border: '2px solid #dee2e6'
                  }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#192f6a', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FontAwesomeIcon icon={faUtensils} />
                      Dietary Requirements Summary
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      {Object.entries(dietarySummary)
                        .sort((a, b) => b[1] - a[1])
                        .map(([diet, count]) => (
                          <div key={diet} style={{
                            padding: '0.75rem 1.25rem',
                            background: 'white',
                            borderRadius: '8px',
                            border: '2px solid #007bff',
                            fontSize: '1rem',
                            fontWeight: '600'
                          }}>
                            <span style={{ color: '#007bff' }}>{count}×</span> {diet}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                
                {/* Bookings by Time Slot */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#192f6a', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FontAwesomeIcon icon={faClock} />
                    Bookings by Time Slot
                  </div>
                  {Object.entries(bookingsByTime)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([time, timeBookings]) => {
                      const timeGuests = timeBookings.reduce((sum, b) => {
                        return sum + (b.products?.reduce((pSum, p) => pSum + (parseInt(p.quantity, 10) || 1), 0) || parseInt(b.number_of_guests, 10) || 0);
                      }, 0);
                      
                      return (
                        <div key={time} style={{
                          background: 'white',
                          borderRadius: '12px',
                          padding: '1.25rem',
                          marginBottom: '1rem',
                          border: '2px solid #e9ecef',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '1rem',
                            paddingBottom: '0.75rem',
                            borderBottom: '2px solid #e9ecef'
                          }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#007bff' }}>
                              {formatTime(time)}
                            </div>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                              <div style={{ fontSize: '0.95rem', color: '#666' }}>
                                <strong>{timeBookings.length}</strong> booking{timeBookings.length !== 1 ? 's' : ''}
                              </div>
                              <div style={{ fontSize: '0.95rem', color: '#666' }}>
                                <strong>{timeGuests}</strong> guest{timeGuests !== 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                          
                          <div style={{ display: 'grid', gap: '0.75rem' }}>
                            {timeBookings.map(booking => {
                              const guests = booking.products?.reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 1), 0) || parseInt(booking.number_of_guests, 10) || 0;

                              // Build per-product detail rows for dietary/allergen display
                              const productRows = [];
                              booking.products?.forEach(product => {
                                const modifier = product.modifiers?.[0];
                                const dietaryChoice = modifier?.modifier_name || '—';
                                const allergen = product.allergen_notes?.trim() || null;
                                const modData = modifier?.modifier_data || null;
                                const isInferred = modData && (modData.toLowerCase().includes('inferred') || modData.toLowerCase().includes('unknown') || modData.toLowerCase().includes('mixed'));
                                productRows.push({ name: product.product_name, dietaryChoice, allergen, modData, isInferred });
                              });

                              const hasAllergens = productRows.some(r => r.allergen);
                              const hasInferred = productRows.some(r => r.isInferred);
                              const isHistorical = booking.booking_date && new Date(booking.booking_date.replace(' ', 'T')) < new Date('2026-03-25T00:00:00') && !/(TABLE|COACH)/i.test(booking.booking_reference || '');

                              return (
                                <div key={booking.id} style={{
                                  background: '#f8f9fa',
                                  borderRadius: '10px',
                                  borderLeft: `5px solid ${getBookingColor(booking)}`,
                                  overflow: 'hidden'
                                }}>
                                  {/* Top row: name / guests / status / buttons */}
                                  <div style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: '1rem', padding: '0.85rem 1rem', alignItems: 'center', background: '#fff', borderBottom: productRows.length > 0 ? '1px solid #e9ecef' : 'none' }}>
                                    <div>
                                      <div style={{ fontWeight: '700', fontSize: '1rem', color: '#192f6a' }}>
                                        {booking.customer_name}
                                      </div>
                                      <div style={{ fontSize: '0.82rem', color: '#666', fontFamily: 'monospace', marginTop: '0.15rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
                                        {booking.booking_reference}
                                        {(booking.booking_type && booking.booking_type !== 'afternoon_tea') && (
                                          <span style={{ fontSize: '0.7rem', fontWeight: '600', color: 'white', background: getBookingColor(booking), padding: '0.12rem 0.45rem', borderRadius: '4px' }}>
                                            {getBookingTypeLabel(booking)}
                                          </span>
                                        )}
                                        {booking.admin_notes?.includes('Manual booking') && (
                                          <span style={{ fontSize: '0.7rem', fontWeight: '600', color: '#856404', background: '#fff3cd', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>Manual</span>
                                        )}
                                        {isHistorical && (
                                          <span style={{ fontSize: '0.7rem', fontWeight: '600', color: '#3730a3', background: '#eef2ff', border: '1px solid #6366f1', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>📋 Historical</span>
                                        )}
                                      </div>
                                    </div>
                                    <div style={{ textAlign: 'center', minWidth: '52px' }}>
                                      <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#28a745', lineHeight: 1 }}>{guests}</div>
                                      <div style={{ fontSize: '0.72rem', color: '#666' }}>guest{guests !== 1 ? 's' : ''}</div>
                                    </div>
                                    <div>
                                      <StatusBadge status={booking.booking_status}>{booking.booking_status}</StatusBadge>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                      <Button size="small" onClick={() => { setSelectedCalendarDay(null); handleOpenEditModal(booking); }} style={{ fontSize: '0.82rem', padding: '0.4rem 0.8rem', whiteSpace: 'nowrap' }}>
                                        <FontAwesomeIcon icon={faEdit} style={{ marginRight: '0.4rem' }} />Edit
                                      </Button>
                                      <Button size="small" onClick={() => { setSelectedCalendarDay(null); handleOpenTransferModal(booking); }} style={{ fontSize: '0.82rem', padding: '0.4rem 0.8rem', whiteSpace: 'nowrap' }}>
                                        <FontAwesomeIcon icon={faExchangeAlt} style={{ marginRight: '0.4rem' }} />Transfer
                                      </Button>
                                    </div>
                                  </div>

                                  {booking.tearoom_staff_notes?.trim() && (
                                    <div style={{
                                      padding: '0.65rem 1rem',
                                      background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                                      borderBottom: '1px solid #fcd34d',
                                      borderLeft: '4px solid #d97706'
                                    }}>
                                      <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem' }}>
                                        Tearoom special requests
                                      </div>
                                      <div style={{ fontSize: '0.9rem', color: '#78350f', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                                        {booking.tearoom_staff_notes}
                                      </div>
                                    </div>
                                  )}

                                  {/* Per-product dietary & allergen table */}
                                  {productRows.length > 0 && (
                                    <div style={{ padding: '0.6rem 1rem 0.75rem' }}>
                                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                        <thead>
                                          <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                            <th style={{ textAlign: 'left', padding: '0.3rem 0.5rem 0.3rem 0', color: '#555', fontWeight: '600', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.03em', width: '35%' }}>Product</th>
                                            <th style={{ textAlign: 'left', padding: '0.3rem 0.5rem', color: '#555', fontWeight: '600', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.03em', width: '25%' }}>Dietary</th>
                                            <th style={{ textAlign: 'left', padding: '0.3rem 0 0.3rem 0.5rem', color: '#555', fontWeight: '600', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Allergen / Special Notes</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {productRows.map((row, ri) => (
                                            <tr key={ri} style={{ borderBottom: ri < productRows.length - 1 ? '1px solid #e9ecef' : 'none', background: row.allergen ? '#fffbf0' : 'transparent' }}>
                                              <td style={{ padding: '0.35rem 0.5rem 0.35rem 0', color: '#333', verticalAlign: 'top' }}>{row.name}</td>
                                              <td style={{ padding: '0.35rem 0.5rem', verticalAlign: 'top' }}>
                                                {row.isInferred ? (
                                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b', borderRadius: '4px', padding: '0.15rem 0.4rem', fontSize: '0.8rem', fontWeight: '600' }}>
                                                    ⚑ {row.dietaryChoice}
                                                  </span>
                                                ) : (
                                                  <span style={{ display: 'inline-block', background: row.dietaryChoice === '—' ? '#f1f5f9' : '#e7f3ff', color: row.dietaryChoice === '—' ? '#94a3b8' : '#1d4ed8', border: `1px solid ${row.dietaryChoice === '—' ? '#cbd5e1' : '#93c5fd'}`, borderRadius: '4px', padding: '0.15rem 0.45rem', fontSize: '0.82rem', fontWeight: '600' }}>
                                                    {row.dietaryChoice}
                                                  </span>
                                                )}
                                                {row.isInferred && row.modData && (
                                                  <div style={{ fontSize: '0.72rem', color: '#92400e', fontStyle: 'italic', marginTop: '0.2rem' }}>{row.modData}</div>
                                                )}
                                              </td>
                                              <td style={{ padding: '0.35rem 0 0.35rem 0.5rem', verticalAlign: 'top' }}>
                                                {row.allergen ? (
                                                  <span style={{ color: '#b91c1c', fontWeight: '600' }}>⚠ {row.allergen}</span>
                                                ) : (
                                                  <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>None recorded</span>
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                      {(hasAllergens || hasInferred || isHistorical) && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' }}>
                                          {hasAllergens && <span style={{ fontSize: '0.75rem', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: '4px', padding: '0.15rem 0.45rem', fontWeight: '600' }}>⚠ Allergen info — check before serving</span>}
                                          {hasInferred && <span style={{ fontSize: '0.75rem', background: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b', borderRadius: '4px', padding: '0.15rem 0.45rem', fontWeight: '600' }}>⚑ Inferred dietary — please confirm on day</span>}
                                          {isHistorical && <span style={{ fontSize: '0.75rem', background: '#eef2ff', color: '#3730a3', border: '1px solid #6366f1', borderRadius: '4px', padding: '0.15rem 0.45rem', fontWeight: '600' }}>📋 Historical booking — verify allergens with customer</span>}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </ModalContent>
            </Modal>
          );
        })()}
      </>
    );
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      params.append('limit', '500');
      
      // Always use WordPress REST API format
      const url = `${config.apiEndpoint}/custom/v1/get-afternoon-tea-bookings?${params.toString()}`;
      
      console.log('🍵 [AFTERNOON-TEA] Fetching bookings from:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [AFTERNOON-TEA] Response not OK:', response.status, errorText);
        throw new Error(`Failed to load bookings: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('🍵 [AFTERNOON-TEA] Response data:', data);
      
      if (data.success) {
        const raw = data.bookings || [];
        // Vineyard tours live in their own tables — never show them on afternoon tea calendar/overview
        setBookings(raw
          .filter((b) => b.booking_type !== 'vineyard_tour')
          .map((b) => {
          const rid = resolveBookingDbId(b);
          const bref = String(b.booking_reference ?? b.bookingReference ?? '').trim();
          return {
            ...b,
            ...(rid != null ? { id: rid } : {}),
            booking_reference: bref
          };
        }));
        setTotal(data.total || 0);
        console.log(`✅ [AFTERNOON-TEA] Loaded ${data.bookings?.length || 0} bookings`);
      } else {
        const errorMsg = data.message || data.error?.message || 'Failed to load bookings';
        console.error('❌ [AFTERNOON-TEA] API returned error:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error loading bookings:', err);
      setError('Failed to load bookings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchandisePickups = async (date, status) => {
    setMerchandiseLoading(true);
    setMerchandiseError(null);
    try {
      const params = new URLSearchParams();
      if (date) params.append('date', date);
      if (status && status !== 'all') params.append('status', status);
      const url = `${config.apiEndpoint}/custom/v1/get-merchandise-pickups?${params.toString()}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMerchandisePickups(data.items || []);
    } catch (err) {
      setMerchandiseError('Failed to load merchandise pickups: ' + err.message);
    } finally {
      setMerchandiseLoading(false);
    }
  };

  const fetchVineyardBookings = async () => {
    setVineyardLoading(true);
    setVineyardError(null);
    try {
      // Always load all vineyard bookings — only 4 slots (15 & 29 Aug), ignore afternoon tea date filters
      const url = `${config.apiEndpoint}/custom/v1/get-vineyard-tour-bookings?limit=500`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load vineyard bookings');
      setVineyardBookings(data.bookings || []);
    } catch (err) {
      setVineyardError('Failed to load vineyard tour bookings: ' + err.message);
      setVineyardBookings([]);
    } finally {
      setVineyardLoading(false);
    }
  };

  const fetchGrottoBookings = async () => {
    setGrottoLoading(true);
    setGrottoError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', '500');
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const url = `${config.apiEndpoint}/custom/v1/get-santas-grotto-bookings?${params.toString()}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load Santa\'s Grotto bookings');
      setGrottoBookings(data.bookings || []);
    } catch (err) {
      setGrottoError('Failed to load Santa\'s Grotto bookings: ' + err.message);
      setGrottoBookings([]);
    } finally {
      setGrottoLoading(false);
    }
  };

  const fetchSearchForSantaBookings = async () => {
    setSearchForSantaLoading(true);
    setSearchForSantaError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', '500');
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const url = `${config.apiEndpoint}/custom/v1/get-search-for-santa-bookings?${params.toString()}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load Search for Santa bookings');
      setSearchForSantaBookings(data.bookings || []);
    } catch (err) {
      setSearchForSantaError('Failed to load Search for Santa bookings: ' + err.message);
      setSearchForSantaBookings([]);
    } finally {
      setSearchForSantaLoading(false);
    }
  };

  const handleMerchandiseStatusUpdate = async (id, newStatus) => {
    try {
      await fetch(`${config.apiEndpoint}/custom/v1/update-merchandise-pickup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status: newStatus })
      });
      setMerchandisePickups(prev =>
        prev.map(p => p.id === id || String(p.id) === String(id) ? { ...p, pickup_status: newStatus } : p)
      );
    } catch (err) {
      console.error('Failed to update merchandise status:', err);
    }
  };

  // Calculate comprehensive statistics
  const statistics = useMemo(() => {
    if (!bookings.length) return null;

    const totalBookings = bookings.length;
    const totalGuests = bookings.reduce((sum, b) => {
      return sum + (b.products?.reduce((pSum, p) => pSum + (parseInt(p.quantity, 10) || 1), 0) || parseInt(b.number_of_guests, 10) || 0);
    }, 0);
    const totalRevenue = bookings.reduce((sum, b) => sum + parseFloat(b.total_amount || 0), 0);

    // Group by date
    const byDate = {};
    bookings.forEach(booking => {
      if (!booking.visit_date) return;
      const dateKey = booking.visit_date.split('T')[0];
      if (!byDate[dateKey]) {
        byDate[dateKey] = {
          date: dateKey,
          bookings: [],
          guests: 0,
          revenue: 0,
          timeSlots: {},
          modifiers: {},
          modifiersByPrompt: {},
          allergens: [],
          orders: []
        };
      }
      byDate[dateKey].bookings.push(booking);
      byDate[dateKey].orders.push(booking);
      const guests = booking.products?.reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 1), 0) || 0;
      byDate[dateKey].guests += guests;
      byDate[dateKey].revenue += parseFloat(booking.total_amount || 0);

      // Time slots with detailed breakdown
      const timeKey = booking.visit_time ? booking.visit_time.substring(0, 5) : 'No time';
      if (!byDate[dateKey].timeSlots[timeKey]) {
        byDate[dateKey].timeSlots[timeKey] = { 
          count: 0, 
          guests: 0,
          bookings: [],
          modifiers: {},
          allergens: []
        };
      }
      byDate[dateKey].timeSlots[timeKey].count += 1;
      byDate[dateKey].timeSlots[timeKey].guests += guests;
      byDate[dateKey].timeSlots[timeKey].bookings.push(booking);

      // Modifiers by prompt and selection
      booking.products?.forEach(product => {
        product.modifiers?.forEach(modifier => {
          const modName = modifier.modifier_name || 'Unknown';
          const modPrompt = modifier.modifier_value || 'Modifier';
          const qty = parseInt(product.quantity, 10) || 1;
          
          // Count by modifier name
          if (!byDate[dateKey].modifiers[modName]) {
            byDate[dateKey].modifiers[modName] = 0;
          }
          byDate[dateKey].modifiers[modName] += qty;
          
          // Count by prompt (e.g., "Dietary Requirements")
          if (!byDate[dateKey].modifiersByPrompt[modPrompt]) {
            byDate[dateKey].modifiersByPrompt[modPrompt] = {};
          }
          if (!byDate[dateKey].modifiersByPrompt[modPrompt][modName]) {
            byDate[dateKey].modifiersByPrompt[modPrompt][modName] = 0;
          }
          byDate[dateKey].modifiersByPrompt[modPrompt][modName] += qty;
          
          // Add to time slot modifiers
          if (!byDate[dateKey].timeSlots[timeKey].modifiers[modName]) {
            byDate[dateKey].timeSlots[timeKey].modifiers[modName] = 0;
          }
          byDate[dateKey].timeSlots[timeKey].modifiers[modName] += qty;
        });
      });

      // Allergens with full details - from both questions and allergen_notes
      booking.products?.forEach(product => {
        // Check allergen_notes field (new per-product allergen tracking)
        if (product.allergen_notes && product.allergen_notes.trim()) {
          const allergenInfo = {
            question: 'Allergen Information',
            answer: product.allergen_notes,
            customer: booking.customer_name,
            bookingRef: booking.booking_reference,
            visitTime: booking.visit_time,
            email: booking.customer_email,
            phone: booking.customer_phone,
            productName: product.product_name,
            productQuantity: product.quantity,
            source: 'allergen_notes'
          };
          byDate[dateKey].allergens.push(allergenInfo);
          byDate[dateKey].timeSlots[timeKey].allergens.push(allergenInfo);
        }
        
        // Also check questions for allergen-related answers (legacy/event questions)
        product.questions?.forEach(question => {
          const answer = question.answer?.toLowerCase() || '';
          const questionText = question.question_text?.toLowerCase() || '';
          if (answer && (answer.includes('allerg') || answer.includes('allergy') || answer.includes('intoler') || 
              questionText.includes('allerg') || questionText.includes('allergy'))) {
            const allergenInfo = {
              question: question.question_text,
              answer: question.answer,
              customer: booking.customer_name,
              bookingRef: booking.booking_reference,
              visitTime: booking.visit_time,
              email: booking.customer_email,
              phone: booking.customer_phone,
              productName: product.product_name,
              productQuantity: product.quantity,
              source: 'question'
            };
            byDate[dateKey].allergens.push(allergenInfo);
            byDate[dateKey].timeSlots[timeKey].allergens.push(allergenInfo);
          }
        });
      });
    });

    // Overall modifiers count
    const allModifiers = {};
    const allModifiersByPrompt = {};
    bookings.forEach(booking => {
      booking.products?.forEach(product => {
        product.modifiers?.forEach(modifier => {
          const modName = modifier.modifier_name || 'Unknown';
          const modPrompt = modifier.modifier_value || 'Modifier';
          const qty = parseInt(product.quantity, 10) || 1;
          
          if (!allModifiers[modName]) {
            allModifiers[modName] = { count: 0, prompt: modPrompt };
          }
          allModifiers[modName].count += qty;
          
          if (!allModifiersByPrompt[modPrompt]) {
            allModifiersByPrompt[modPrompt] = {};
          }
          if (!allModifiersByPrompt[modPrompt][modName]) {
            allModifiersByPrompt[modPrompt][modName] = 0;
          }
          allModifiersByPrompt[modPrompt][modName] += qty;
        });
      });
    });

    // Overall allergens - from both allergen_notes and questions
    const allAllergens = [];
    bookings.forEach(booking => {
      booking.products?.forEach(product => {
        // Check allergen_notes field (new per-product allergen tracking)
        if (product.allergen_notes && product.allergen_notes.trim()) {
          allAllergens.push({
            question: 'Allergen Information',
            answer: product.allergen_notes,
            customer: booking.customer_name,
            bookingRef: booking.booking_reference,
            visitDate: booking.visit_date,
            visitTime: booking.visit_time,
            email: booking.customer_email,
            phone: booking.customer_phone,
            productName: product.product_name,
            productQuantity: product.quantity,
            source: 'allergen_notes'
          });
        }
        
        // Also check questions for allergen-related answers (legacy/event questions)
        product.questions?.forEach(question => {
          const answer = question.answer?.toLowerCase() || '';
          const questionText = question.question_text?.toLowerCase() || '';
          if (answer && (answer.includes('allerg') || answer.includes('allergy') || answer.includes('intoler') ||
              questionText.includes('allerg') || questionText.includes('allergy'))) {
            allAllergens.push({
              question: question.question_text,
              answer: question.answer,
              customer: booking.customer_name,
              bookingRef: booking.booking_reference,
              visitDate: booking.visit_date,
              visitTime: booking.visit_time,
              email: booking.customer_email,
              phone: booking.customer_phone,
              productName: product.product_name,
              productQuantity: product.quantity,
              source: 'question'
            });
          }
        });
      });
    });

    const allTearoomStaffNotes = [];
    bookings.forEach(booking => {
      const tn = booking.tearoom_staff_notes?.trim();
      if (tn) {
        allTearoomStaffNotes.push({
          customer: booking.customer_name,
          bookingRef: booking.booking_reference,
          visitDate: booking.visit_date,
          visitTime: booking.visit_time,
          notes: tn
        });
      }
    });

    return {
      totalBookings,
      totalGuests,
      totalRevenue,
      byDate,
      allModifiers,
      allModifiersByPrompt,
      allAllergens,
      allTearoomStaffNotes
    };
  }, [bookings]);

  const toggleRow = (bookingId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(bookingId)) {
      newExpanded.delete(bookingId);
    } else {
      newExpanded.add(bookingId);
    }
    setExpandedRows(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `£${parseFloat(amount).toFixed(2)}`;
  };

  const escapeHtml = (s) => {
    if (s == null || s === '') return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  // Create a compact, professional PDF/Print document
  const createHospitalityDocument = (title, content, type = 'print') => {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>${title}</title>
          <style>
            @page {
              size: A4;
              margin: 1cm 1.2cm;
            }
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              font-size: 9pt;
              line-height: 1.3;
              color: #000;
              background: white;
            }
            .header {
              border-bottom: 3px solid #192f6a;
              padding-bottom: 6px;
              margin-bottom: 12px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .header h1 {
              color: #192f6a;
              font-size: 16pt;
              font-weight: bold;
            }
            .header-info {
              font-size: 8pt;
              color: #555;
              text-align: right;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 12px;
              font-size: 8.5pt;
            }
            th {
              background: #192f6a;
              color: white;
              padding: 5px 6px;
              text-align: left;
              font-weight: 600;
              font-size: 9pt;
            }
            td {
              padding: 4px 6px;
              border-bottom: 1px solid #e0e0e0;
              vertical-align: top;
            }
            tr:hover {
              background: #f8f9fa;
            }
            .time-slot {
              font-weight: 600;
              color: #192f6a;
            }
            .modifier {
              display: inline-block;
              background: #e8f4f8;
              padding: 2px 6px;
              margin: 2px;
              border-radius: 3px;
              font-size: 8pt;
            }
            .notes-cell {
              font-size: 7.5pt;
              max-width: 120px;
              line-height: 1.25;
              word-break: break-word;
            }
            .allergen {
              background: #fff3cd;
              padding: 4px 6px;
              margin: 2px 0;
              border-left: 3px solid #ffc107;
              font-size: 8.5pt;
            }
            .allergen strong {
              color: #856404;
            }
            .section-header {
              background: #192f6a;
              color: white;
              padding: 6px 8px;
              font-size: 11pt;
              font-weight: bold;
              margin: 10px 0 6px 0;
            }
            .stats-row {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 8px;
              margin-bottom: 10px;
            }
            .stat-box {
              background: #f5f5f5;
              padding: 6px;
              text-align: center;
              border-left: 3px solid #192f6a;
            }
            .stat-label {
              font-size: 7.5pt;
              color: #666;
              text-transform: uppercase;
              margin-bottom: 2px;
            }
            .stat-value {
              font-size: 13pt;
              font-weight: bold;
              color: #192f6a;
            }
            .compact-list {
              list-style: none;
              margin: 4px 0;
            }
            .compact-list li {
              padding: 3px 0;
              border-bottom: 1px dotted #ddd;
            }
            .time-slots {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 6px;
              margin: 6px 0;
            }
            .time-box {
              background: #f8f9fa;
              padding: 4px;
              text-align: center;
              border: 1px solid #dee2e6;
              border-radius: 3px;
            }
            .time-box .time {
              font-weight: bold;
              color: #192f6a;
              font-size: 10pt;
            }
            .time-box .count {
              font-size: 8pt;
              color: #666;
            }
            .order-compact {
              border: 1px solid #dee2e6;
              padding: 6px;
              margin-bottom: 6px;
              page-break-inside: avoid;
            }
            .order-compact .header-row {
              font-weight: bold;
              color: #192f6a;
              margin-bottom: 3px;
              padding-bottom: 3px;
              border-bottom: 1px solid #dee2e6;
            }
            .mini-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 4px;
              margin: 4px 0;
            }
            .mini-item {
              background: #f8f9fa;
              padding: 3px 5px;
              font-size: 8pt;
              border-left: 2px solid #007bff;
            }
            @media print {
              .no-print { display: none !important; }
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `;
    return htmlContent;
  };

  const handleExportPDF = async (title, content) => {
    try {
      // Create a new window with the formatted content
      const printWindow = window.open('', '_blank');
      const htmlContent = createHospitalityDocument(title, content, 'pdf');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for content to load, then trigger print (which can save as PDF)
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 500);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error generating PDF. Please try printing instead.');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePrintDay = (date) => {
    if (!statistics) return;
    const dayData = statistics.byDate[date];
    if (!dayData) return;

    const tearoomDayCount = (dayData.bookings || []).filter((b) => b.tearoom_staff_notes?.trim()).length;

    // Calculate STANDARD (no modifier) count
    const totalWithModifiers = Object.values(dayData.modifiers).reduce((sum, count) => sum + count, 0);
    const standardCount = dayData.guests - totalWithModifiers;
    
    // Create compact modifier summary
    let modifierSummary = Object.entries(dayData.modifiersByPrompt)
      .map(([prompt, selections]) => {
        const items = Object.entries(selections)
          .sort(([, a], [, b]) => b - a)
          .map(([modifier, count]) => `${modifier} (${count})`)
          .join(', ');
        return `<strong>${prompt}:</strong> ${items}`;
      }).join(' • ');
    
    // Add STANDARD count if there are bookings without modifiers
    if (standardCount > 0) {
      const standardText = `STANDARD (${standardCount})`;
      if (modifierSummary) {
        modifierSummary += ` • <strong>Standard:</strong> ${standardText}`;
      } else {
        modifierSummary = `<strong>Dietary Requirements:</strong> ${standardText}`;
      }
    }

    const content = `
      <div class="header">
        <h1>AFTERNOON TEA - ${formatDate(dayData.date).toUpperCase()}</h1>
        <div class="header-info">
          ${new Date().toLocaleDateString('en-GB')} | ${dayData.bookings.length} bookings, ${dayData.guests} guests
        </div>
      </div>

      <div class="stats-row">
        <div class="stat-box">
          <div class="stat-label">Bookings</div>
          <div class="stat-value">${dayData.bookings.length}</div>
        </div>
        <div class="stat-box">
          <div class="stat-label">Guests</div>
          <div class="stat-value">${dayData.guests}</div>
        </div>
        <div class="stat-box">
          <div class="stat-label">Allergens</div>
          <div class="stat-value" style="color: ${dayData.allergens.length > 0 ? '#ffc107' : '#192f6a'}">${dayData.allergens.length}</div>
        </div>
        <div class="stat-box">
          <div class="stat-label">Tearoom requests</div>
          <div class="stat-value" style="color: ${tearoomDayCount > 0 ? '#b45309' : '#192f6a'}">${tearoomDayCount}</div>
        </div>
      </div>

      ${Object.keys(dayData.timeSlots).length > 0 ? `
        <div class="section-header">TIME SLOTS</div>
        <div class="time-slots">
          ${Object.entries(dayData.timeSlots)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([time, data]) => `
              <div class="time-box">
                <div class="time">${time === 'No time' ? 'TBD' : time}</div>
                <div class="count">${data.count} booking${data.count !== 1 ? 's' : ''}, ${data.guests} guests${(data.bookings || []).filter((b) => b.tearoom_staff_notes?.trim()).length ? ` · 📋 ${(data.bookings || []).filter((b) => b.tearoom_staff_notes?.trim()).length} tearoom` : ''}</div>
              </div>
            `).join('')}
        </div>
      ` : ''}

      ${modifierSummary ? `
        <div class="section-header">DIETARY REQUIREMENTS</div>
        <div style="padding: 6px; background: #f8f9fa; margin-bottom: 10px; font-size: 8.5pt; line-height: 1.4;">
          ${modifierSummary}
        </div>
      ` : ''}

      ${dayData.allergens.length > 0 ? `
        <div class="section-header">⚠️ ALLERGENS & SPECIAL REQUESTS (${dayData.allergens.length})</div>
        ${dayData.allergens.map((allergen) => `
          <div class="allergen">
            <strong>${allergen.customer}</strong> (${allergen.visitTime ? formatTime(allergen.visitTime) : 'Time TBD'})${allergen.productName ? ` - ${allergen.productName}${allergen.productQuantity > 1 ? ` × ${allergen.productQuantity}` : ''}` : ''}
            <br><strong>${allergen.question}:</strong> ${allergen.answer}
          </div>
        `).join('')}
      ` : ''}

      ${tearoomDayCount > 0 ? `
        <div class="section-header" style="background:#b45309;">TEAROOM SPECIAL REQUESTS (${tearoomDayCount})</div>
        ${(dayData.bookings || []).filter((b) => b.tearoom_staff_notes?.trim()).map((b) => `
          <div style="padding:6px 8px;margin-bottom:6px;background:#fffbeb;border-left:4px solid #d97706;font-size:8.5pt;page-break-inside:avoid;">
            <strong>${escapeHtml(b.customer_name)}</strong> (${escapeHtml(b.booking_reference)})${b.visit_time ? ' · ' + escapeHtml(formatTime(b.visit_time)) : ''}
            <div style="margin-top:4px;color:#78350f;font-weight:600;line-height:1.35;">${escapeHtml(b.tearoom_staff_notes.trim())}</div>
          </div>
        `).join('')}
      ` : ''}

      <div class="section-header">BOOKING DETAILS — DIETARY &amp; ALLERGEN PER PERSON</div>
      ${dayData.orders
        .sort((a, b) => (a.visit_time || '99:99').localeCompare(b.visit_time || '99:99'))
        .map((booking) => {
          const guests = booking.products?.reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 1), 0) || 0;
          const isHistorical = booking.booking_date && new Date(booking.booking_date.replace(' ', 'T')) < new Date('2026-03-25T00:00:00') && !/(TABLE|COACH)/i.test(booking.booking_reference || '');

          const productRowsHtml = (booking.products || []).map(product => {
            const modifier = product.modifiers?.[0];
            const dietary = modifier?.modifier_name || 'Standard';
            const modData = modifier?.modifier_data || null;
            const isInferred = modData && (modData.toLowerCase().includes('inferred') || modData.toLowerCase().includes('unknown') || modData.toLowerCase().includes('mixed'));
            const allergenCell = product.allergen_notes?.trim()
              ? `<span style="color:#b91c1c;font-weight:600;">⚠ ${product.allergen_notes.trim()}</span>`
              : `<span style="color:#999;font-style:italic;">None recorded</span>`;
            const dietaryStyle = isInferred
              ? 'background:#fff3cd;color:#856404;border:1px solid #ffc107;'
              : 'background:#e7f3ff;color:#004085;border:1px solid #93c5fd;';
            const noteCell = isInferred && modData
              ? `<br><span style="font-size:7pt;color:#856404;font-style:italic;">⚑ ${modData}</span>` : '';
            const questions = (product.questions || []).filter(q => q.answer?.trim()).map(q => `<div style="font-size:7.5pt;color:#555;margin-top:2px;"><em>${q.question}:</em> ${q.answer}</div>`).join('');
            return `<tr style="border-top:1px solid #eee;">
              <td style="padding:4px 6px;font-size:8.5pt;">${product.product_name}</td>
              <td style="padding:4px 6px;font-size:8.5pt;"><span style="display:inline-block;padding:1px 6px;border-radius:3px;font-weight:600;${dietaryStyle}">${dietary}</span>${noteCell}</td>
              <td style="padding:4px 6px;font-size:8.5pt;">${allergenCell}${questions}</td>
            </tr>`;
          }).join('');

          return `
            <div class="order-compact" style="margin-bottom:10px;page-break-inside:avoid;">
              <div class="header-row" style="display:flex;justify-content:space-between;align-items:baseline;background:#192f6a;color:white;padding:5px 8px;border-radius:3px 3px 0 0;">
                <span style="font-size:10pt;">${booking.visit_time ? formatTime(booking.visit_time) : 'TBD'} &nbsp;•&nbsp; <strong>${booking.customer_name}</strong></span>
                <span style="font-size:8.5pt;opacity:0.85;">${booking.booking_reference} &nbsp;•&nbsp; ${guests} guest${guests !== 1 ? 's' : ''} &nbsp;•&nbsp; ${booking.booking_status || ''}</span>
              </div>
              ${booking.tearoom_staff_notes?.trim() ? `
              <div style="background:#fff8e6;border:2px solid #d4af37;border-top:none;padding:6px 8px;font-size:8.5pt;font-weight:600;color:#5c4a00;line-height:1.35;">
                <strong>Tearoom special requests:</strong> ${escapeHtml(booking.tearoom_staff_notes.trim())}
              </div>` : ''}
              ${isHistorical ? `<div style="background:#eef2ff;color:#3730a3;font-size:7.5pt;padding:3px 8px;border:1px solid #6366f1;border-top:none;font-weight:600;">📋 Historical booking — verify all allergen &amp; dietary information directly with customer before visit</div>` : ''}
              <table style="width:100%;border-collapse:collapse;border:1px solid #dee2e6;border-top:none;">
                <thead>
                  <tr style="background:#f1f5f9;">
                    <th style="text-align:left;padding:4px 6px;font-size:8pt;width:35%;color:#444;">Product</th>
                    <th style="text-align:left;padding:4px 6px;font-size:8pt;width:25%;color:#444;">Dietary Requirement</th>
                    <th style="text-align:left;padding:4px 6px;font-size:8pt;color:#444;">Allergen / Special Notes</th>
                  </tr>
                </thead>
                <tbody>${productRowsHtml}</tbody>
              </table>
            </div>
          `;
        }).join('')}
    `;

    handleExportPDF(`Afternoon Tea - ${formatDate(dayData.date)}`, content);
  };

  const handlePrintOrder = (booking) => {
    const guests = booking.products?.reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 1), 0) || 0;
    
    const content = `
      <div class="header">
        <h1>AFTERNOON TEA ORDER</h1>
        <div class="header-info">
          ${booking.booking_reference} | ${formatDate(booking.visit_date)} ${booking.visit_time ? formatTime(booking.visit_time) : ''}
        </div>
      </div>

      <table style="margin-bottom: 10px;">
        <tr>
          <td style="width: 25%; font-weight: bold; background: #f8f9fa;">Customer</td>
          <td style="width: 75%;">${booking.customer_name}</td>
        </tr>
        <tr>
          <td style="font-weight: bold; background: #f8f9fa;">Email</td>
          <td>${booking.customer_email}</td>
        </tr>
        ${booking.customer_phone ? `
          <tr>
            <td style="font-weight: bold; background: #f8f9fa;">Phone</td>
            <td>${booking.customer_phone}</td>
          </tr>
        ` : ''}
        <tr>
          <td style="font-weight: bold; background: #f8f9fa;">Guests</td>
          <td><strong style="font-size: 11pt;">${guests}</strong></td>
        </tr>
        <tr>
          <td style="font-weight: bold; background: #f8f9fa;">Total</td>
          <td><strong style="font-size: 12pt; color: #192f6a;">${formatCurrency(booking.total_amount)}</strong></td>
        </tr>
        ${booking.tearoom_staff_notes?.trim() ? `
        <tr>
          <td style="font-weight: bold; background: #fff8e6; vertical-align: top;">Tearoom requests</td>
          <td style="background: #fffbeb; font-weight: 600; color: #78350f;">${escapeHtml(booking.tearoom_staff_notes.trim())}</td>
        </tr>
        ` : ''}
      </table>

      ${booking.products?.map(product => {
        const hasDietary = product.modifiers && product.modifiers.length > 0;
        const hasNotes = product.questions?.length > 0;
        const dietaryText = hasDietary 
          ? product.modifiers.map(m => m.modifier_name).join(', ')
          : 'STANDARD';
        
        return `
          <div class="order-compact">
            <div class="header-row">${product.product_name} × ${product.quantity}</div>
            <div style="margin: 4px 0;">
              <strong style="font-size: 8pt; color: #666;">DIETARY:</strong> 
              <span class="modifier">${dietaryText}</span>
            </div>
            ${product.allergen_notes ? `
              <div style="margin: 4px 0;">
                <div class="allergen" style="background: #fffbf0; border-left-color: #f0ad4e;">
                  <strong>Allergen Information:</strong> ${product.allergen_notes}
                </div>
              </div>
            ` : ''}
            ${hasNotes ? `
              <div style="margin: 4px 0;">
                ${product.questions.map(q => `
                  <div class="allergen">
                    <strong>${q.question_text}:</strong> ${q.answer}
                  </div>
                `).join('')}
              </div>
            ` : ''}
          </div>
        `;
      }).join('')}
    `;

    handleExportPDF(`Order ${booking.booking_reference}`, content);
  };

  const handleExportAllPDF = () => {
    if (!statistics) return;
    
    const content = `
      <div class="header">
        <h1>AFTERNOON TEA BOOKINGS REPORT</h1>
        <div class="header-info">
          ${new Date().toLocaleDateString('en-GB')} | ${dateFrom || 'All'} to ${dateTo || 'All'}
        </div>
      </div>

      <div class="stats-row">
        <div class="stat-box">
          <div class="stat-label">Bookings</div>
          <div class="stat-value">${statistics.totalBookings}</div>
        </div>
        <div class="stat-box">
          <div class="stat-label">Guests</div>
          <div class="stat-value">${statistics.totalGuests}</div>
        </div>
        <div class="stat-box">
          <div class="stat-label">Allergens</div>
          <div class="stat-value" style="color: ${statistics.allAllergens.length > 0 ? '#ffc107' : '#192f6a'}">${statistics.allAllergens.length}</div>
        </div>
        <div class="stat-box">
          <div class="stat-label">Tearoom requests</div>
          <div class="stat-value" style="color: ${statistics.allTearoomStaffNotes.length > 0 ? '#b45309' : '#192f6a'}">${statistics.allTearoomStaffNotes.length}</div>
        </div>
      </div>

      ${statistics.allTearoomStaffNotes.length > 0 ? `
        <div class="section-header" style="background:#b45309;">ALL TEAROOM SPECIAL REQUESTS (${statistics.allTearoomStaffNotes.length})</div>
        ${statistics.allTearoomStaffNotes.map((row) => `
          <div style="padding:6px 8px;margin-bottom:6px;background:#fffbeb;border-left:4px solid #d97706;font-size:8.5pt;page-break-inside:avoid;">
            <strong>${escapeHtml(row.customer)}</strong> (${escapeHtml(row.bookingRef)}) · ${escapeHtml(formatDate(row.visitDate))}${row.visitTime ? ' ' + escapeHtml(formatTime(row.visitTime)) : ''}
            <div style="margin-top:4px;color:#78350f;font-weight:600;line-height:1.35;">${escapeHtml(row.notes)}</div>
          </div>
        `).join('')}
      ` : ''}

      ${Object.values(statistics.byDate)
        .sort((a, b) => a.date.localeCompare(b.date))
        .map(dayData => {
          // Calculate STANDARD (no modifier) count
          const totalWithModifiers = Object.values(dayData.modifiers).reduce((sum, count) => sum + count, 0);
          const standardCount = dayData.guests - totalWithModifiers;
          
          let modifierSummary = Object.entries(dayData.modifiersByPrompt)
            .map(([prompt, selections]) => {
              const items = Object.entries(selections)
                .sort(([, a], [, b]) => b - a)
                .map(([modifier, count]) => `${modifier}(${count})`)
                .join(', ');
              return `${prompt}: ${items}`;
            }).join(' • ');
          
          // Add STANDARD count if there are bookings without modifiers
          if (standardCount > 0) {
            const standardText = `STANDARD(${standardCount})`;
            if (modifierSummary) {
              modifierSummary += ` • Standard: ${standardText}`;
            } else {
              modifierSummary = `Dietary Requirements: ${standardText}`;
            }
          }
            
          return `
            <div class="section-header">${formatDate(dayData.date).toUpperCase()} - ${dayData.bookings.length} bookings, ${dayData.guests} guests${dayData.allergens.length > 0 ? `, ⚠️ ${dayData.allergens.length} allergens` : ''}</div>
            
            ${Object.keys(dayData.timeSlots).length > 0 ? `
              <div class="time-slots" style="margin-bottom: 8px;">
                ${Object.entries(dayData.timeSlots)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([time, data]) => `
                    <div class="time-box">
                      <div class="time">${time === 'No time' ? 'TBD' : time}</div>
                      <div class="count">${data.count} / ${data.guests}p</div>
                    </div>
                  `).join('')}
              </div>
            ` : ''}

            ${modifierSummary ? `
              <div style="padding: 4px 6px; background: #f8f9fa; margin-bottom: 6px; font-size: 8pt;">
                <strong>DIETARY:</strong> ${modifierSummary}
              </div>
            ` : ''}

            ${dayData.allergens.length > 0 ? `
              ${dayData.allergens.map(allergen => `
                <div class="allergen" style="margin-bottom: 3px;">
                  <strong>${allergen.customer}</strong> (${allergen.visitTime ? formatTime(allergen.visitTime) : 'TBD'})${allergen.productName ? ` - ${allergen.productName}${allergen.productQuantity > 1 ? ` × ${allergen.productQuantity}` : ''}` : ''}
                  <br><strong>${allergen.question}:</strong> ${allergen.answer}
                </div>
              `).join('')}
            ` : ''}

            ${(dayData.bookings || []).filter((b) => b.tearoom_staff_notes?.trim()).length > 0 ? `
              <div class="section-header" style="background:#b45309;margin-top:8px;">TEAROOM SPECIAL REQUESTS (${(dayData.bookings || []).filter((b) => b.tearoom_staff_notes?.trim()).length})</div>
              ${(dayData.bookings || []).filter((b) => b.tearoom_staff_notes?.trim()).map((b) => `
                <div style="padding:6px 8px;margin-bottom:5px;background:#fffbeb;border-left:4px solid #d97706;font-size:8.5pt;page-break-inside:avoid;">
                  <strong>${escapeHtml(b.customer_name)}</strong> (${escapeHtml(b.booking_reference)})${b.visit_time ? ' · ' + escapeHtml(formatTime(b.visit_time)) : ''}
                  <div style="margin-top:4px;color:#78350f;font-weight:600;line-height:1.35;">${escapeHtml(b.tearoom_staff_notes.trim())}</div>
                </div>
              `).join('')}
            ` : ''}
          `;
        }).join('')}
    `;

    handleExportPDF('Afternoon Tea Report', content);
  };

  if (loading) {
    return (
      <Panel>
        <LoadingSpinner>Loading afternoon tea bookings...</LoadingSpinner>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel>
        <SectionTitle>Afternoon Tea Bookings</SectionTitle>
        <div style={{ color: 'red', padding: '1rem', background: '#fee', borderRadius: '4px' }}>
          {error}
        </div>
        <Button onClick={loadBookings}>Retry</Button>
      </Panel>
    );
  }

  // Filter bookings for print if specific date selected
  const displayBookings = selectedDate 
    ? bookings.filter(b => b.visit_date?.split('T')[0] === selectedDate)
    : bookings;

  return (
    <Panel>
      <PageHeader>
        <HeaderContent>
          <HeaderTitle>
            <FontAwesomeIcon icon={faUtensils} style={{ marginRight: '0.75rem', fontSize: '1.75rem', color: '#6c757d' }} />
            Aviary Tearoom Booking Management
          </HeaderTitle>
          <HeaderSubtitle>
            Manage afternoon tea bookings, view analytics, and handle customer reservations
          </HeaderSubtitle>
        </HeaderContent>
      </PageHeader>
      
      <ScreenSection>
        <ActionBar>
      <SectionTitle>Bookings Dashboard</SectionTitle>
          <ButtonGroup>
            <Button 
              variant="success" 
              onClick={handleOpenManualAddModal}
              style={{ marginRight: '0.5rem', fontWeight: '600' }}
            >
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.5rem' }} />
              Afternoon Tea
            </Button>
            <Button 
              variant="primary" 
              onClick={() => handleOpenTableBookingModal('table')}
              style={{ marginRight: '0.5rem', fontWeight: '600', background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)' }}
            >
              <FontAwesomeIcon icon={faUtensils} style={{ marginRight: '0.5rem' }} />
              Table Booking
            </Button>
            <Button 
              variant="warning" 
              onClick={() => handleOpenTableBookingModal('coach')}
              style={{ marginRight: '1rem', fontWeight: '600', background: 'linear-gradient(135deg, #fd7e14 0%, #e8590c 100%)', color: 'white' }}
            >
              <FontAwesomeIcon icon={faUsers} style={{ marginRight: '0.5rem' }} />
              Coach Booking
            </Button>
            <Button variant="primary" onClick={handleExportAllPDF}>
              <FontAwesomeIcon icon={faFile} style={{ marginRight: '0.5rem' }} />
              Export PDF
            </Button>
            <Button variant="primary" onClick={handlePrint}>
              <FontAwesomeIcon icon={faPrint} style={{ marginRight: '0.5rem' }} />
              Print
            </Button>
            <Button variant="success" onClick={loadBookings}>
              <FontAwesomeIcon icon={faSync} style={{ marginRight: '0.5rem' }} />
              Refresh
            </Button>
          </ButtonGroup>
        </ActionBar>
      <SmallText>
          Comprehensive overview of afternoon tea bookings with day-by-day breakdowns, modifier counts, allergen tracking, and detailed order information.
      </SmallText>

      <Filters>
        <FilterGroup>
          <FilterLabel>From Date</FilterLabel>
          <FilterInput
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>To Date</FilterLabel>
          <FilterInput
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </FilterGroup>
      </Filters>

        <Tabs>
          <Tab active={activeTab === 'calendar'} onClick={() => setActiveTab('calendar')}>
            <FontAwesomeIcon icon={faCalendar} style={{ marginRight: '0.5rem' }} />
            Calendar View
          </Tab>
          <Tab active={activeTab === 'floorplan'} onClick={() => setActiveTab('floorplan')}>
            <FontAwesomeIcon icon={faTableCells} style={{ marginRight: '0.5rem' }} />
            Floor Plan
          </Tab>
          <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
            <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '0.5rem' }} />
            Overview
          </Tab>
          <Tab active={activeTab === 'details'} onClick={() => setActiveTab('details')}>
            <FontAwesomeIcon icon={faList} style={{ marginRight: '0.5rem' }} />
            Detailed List
          </Tab>
          <Tab active={activeTab === 'merchandise'} onClick={() => {
            setActiveTab('merchandise');
            fetchMerchandisePickups(merchandiseDate, merchandiseStatusFilter);
          }}>
            <FontAwesomeIcon icon={faUtensils} style={{ marginRight: '0.5rem' }} />
            Merchandise
          </Tab>
          <Tab active={activeTab === 'vineyard_tours'} onClick={() => {
            setActiveTab('vineyard_tours');
            fetchVineyardBookings();
          }}>
            <FontAwesomeIcon icon={faWineGlass} style={{ marginRight: '0.5rem' }} />
            Vineyard Tours
          </Tab>
          <Tab active={activeTab === 'santas_grotto'} onClick={() => {
            setActiveTab('santas_grotto');
            fetchGrottoBookings();
          }}>
            <FontAwesomeIcon icon={faGift} style={{ marginRight: '0.5rem' }} />
            Santa's Grotto
          </Tab>
          <Tab active={activeTab === 'search_for_santa'} onClick={() => {
            setActiveTab('search_for_santa');
            fetchSearchForSantaBookings();
          }}>
            <FontAwesomeIcon icon={faSearch} style={{ marginRight: '0.5rem' }} />
            Search for Santa
          </Tab>
        </Tabs>
      </ScreenSection>

      {/* Calendar View Tab */}
      {activeTab === 'calendar' && (
        <>
          {renderCalendarView()}
        </>
      )}

      {/* Floor Plan Tab */}
      {activeTab === 'floorplan' && (
        <FloorPlanView
          bookings={bookings}
          selectedDate={selectedCalendarDay || toLocalDateString(new Date())}
          onTableSelect={handleFloorPlanTableClick}
        />
      )}

      {activeTab === 'overview' && statistics && (
        <>
          <ScreenSection>
            <SummaryGrid>
              <SummaryCard variant="primary">
                <SummaryLabel>
                  <FontAwesomeIcon icon={faList} />
                  Total Bookings
                </SummaryLabel>
                <SummaryValue>{statistics.totalBookings}</SummaryValue>
              </SummaryCard>
              <SummaryCard variant="success">
                <SummaryLabel>
                  <FontAwesomeIcon icon={faUsers} />
                  Total Guests
                </SummaryLabel>
                <SummaryValue>{statistics.totalGuests}</SummaryValue>
              </SummaryCard>
              <SummaryCard variant="danger">
                <SummaryLabel>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  Allergen Alerts
                </SummaryLabel>
                <SummaryValue>{statistics.allAllergens.length}</SummaryValue>
              </SummaryCard>
              <SummaryCard variant="warning" style={{ color: '#5c4100' }}>
                <SummaryLabel style={{ color: '#5c4100' }}>
                  <FontAwesomeIcon icon={faCommentDots} />
                  Tearoom Requests
                </SummaryLabel>
                <SummaryValue style={{ color: '#5c4100' }}>{statistics.allTearoomStaffNotes.length}</SummaryValue>
              </SummaryCard>
            </SummaryGrid>

            {/* Day-by-Day Overview */}
            {Object.values(statistics.byDate)
              .sort((a, b) => a.date.localeCompare(b.date))
              .map(dayData => (
                <DayOverviewCard key={dayData.date}>
                  <DayHeader>
                    <div>
                      <DayTitle>{formatDate(dayData.date)}</DayTitle>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                        {dayData.bookings.length} booking{dayData.bookings.length !== 1 ? 's' : ''} • {dayData.guests} guest{dayData.guests !== 1 ? 's' : ''}
                        {(() => {
                          const tn = dayData.bookings.filter((b) => b.tearoom_staff_notes?.trim()).length;
                          return tn > 0 ? ` • ${tn} tearoom request${tn !== 1 ? 's' : ''}` : '';
                        })()}
                      </div>
                    </div>
                    <ButtonGroup>
                      <Button variant="primary" onClick={() => handlePrintDay(dayData.date)} style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}>
                        <FontAwesomeIcon icon={faFile} style={{ marginRight: '0.5rem' }} />
                        PDF Day
                      </Button>
                    </ButtonGroup>
                  </DayHeader>

                  {/* Time Slots with detailed breakdown */}
                  {Object.keys(dayData.timeSlots).length > 0 && (
                    <TimeSlotSection>
                      <TimeSlotTitle>
                        <FontAwesomeIcon icon={faClock} style={{ marginRight: '0.5rem' }} />
                        Time Slots
                      </TimeSlotTitle>
                      <StatsRow>
                        {Object.entries(dayData.timeSlots)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([time, data]) => (
                            <StatItem key={time}>
                              <StatLabel style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {time === 'No time' && <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.75rem' }} />}
                                {time === 'No time' ? 'Time TBD' : time}
                              </StatLabel>
                              <StatValue>{data.count} booking{data.count !== 1 ? 's' : ''}</StatValue>
                              <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                                {data.guests} guest{data.guests !== 1 ? 's' : ''}
                              </div>
                              {Object.keys(data.modifiers).length > 0 && (
                                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #dee2e6' }}>
                                  {Object.entries(data.modifiers).slice(0, 2).map(([mod, count]) => (
                                    <div key={mod}>{mod}: {count}</div>
                                  ))}
                                  {Object.keys(data.modifiers).length > 2 && (
                                    <div>+{Object.keys(data.modifiers).length - 2} more</div>
                                  )}
                                </div>
                              )}
                              {data.allergens.length > 0 && (
                                <div style={{ fontSize: '0.75rem', color: '#856404', marginTop: '0.5rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '0.7rem' }} />
                                  {data.allergens.length} allergen{data.allergens.length !== 1 ? 's' : ''}
                                </div>
                              )}
                              {(() => {
                                const tc = (data.bookings || []).filter((b) => b.tearoom_staff_notes?.trim()).length;
                                if (tc === 0) return null;
                                return (
                                  <div style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '0.45rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <FontAwesomeIcon icon={faCommentDots} style={{ fontSize: '0.7rem' }} />
                                    {tc} tearoom note{tc !== 1 ? 's' : ''}
                                  </div>
                                );
                              })()}
                            </StatItem>
                          ))}
                      </StatsRow>
                    </TimeSlotSection>
                  )}

                  {/* Modifiers by Prompt */}
                  {(() => {
                    // Calculate STANDARD (no modifier) count
                    const totalWithModifiers = Object.values(dayData.modifiers).reduce((sum, count) => sum + count, 0);
                    const standardCount = dayData.guests - totalWithModifiers;
                    const hasModifiers = Object.keys(dayData.modifiersByPrompt).length > 0;
                    
                    if (!hasModifiers && standardCount === 0) return null;
                    
                    return (
                      <TimeSlotSection>
                        <TimeSlotTitle>
                          <FontAwesomeIcon icon={faUtensils} style={{ marginRight: '0.5rem' }} />
                          Modifiers Breakdown
                        </TimeSlotTitle>
                        {Object.entries(dayData.modifiersByPrompt)
                          .sort(([, a], [, b]) => {
                            const totalA = Object.values(a).reduce((sum, val) => sum + val, 0);
                            const totalB = Object.values(b).reduce((sum, val) => sum + val, 0);
                            return totalB - totalA;
                          })
                          .map(([prompt, selections]) => (
                            <div key={prompt} style={{ marginBottom: '1rem' }}>
                              <div style={{ fontWeight: '600', color: '#192f6a', marginBottom: '0.5rem', fontSize: '0.95rem' }}>
                                {prompt}
                              </div>
                              <StatsRow>
                                {Object.entries(selections)
                                  .sort(([, a], [, b]) => b - a)
                                  .map(([modifier, count]) => (
                                    <StatItem key={modifier}>
                                      <StatLabel>{modifier}</StatLabel>
                                      <StatValue>{count} selection{count !== 1 ? 's' : ''}</StatValue>
                                    </StatItem>
                                  ))}
                              </StatsRow>
                            </div>
                          ))}
                        {standardCount > 0 && (
                          <div style={{ marginBottom: '1rem' }}>
                            <div style={{ fontWeight: '600', color: '#192f6a', marginBottom: '0.5rem', fontSize: '0.95rem' }}>
                              Standard (No Modifier)
                            </div>
                            <StatsRow>
                              <StatItem>
                                <StatLabel>STANDARD</StatLabel>
                                <StatValue>{standardCount} selection{standardCount !== 1 ? 's' : ''}</StatValue>
                              </StatItem>
                            </StatsRow>
                          </div>
                        )}
                      </TimeSlotSection>
                    );
                  })()}

                  {/* Allergens */}
                  {dayData.allergens.length > 0 && (
                    <TimeSlotSection>
                      <TimeSlotTitle>
                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem' }} />
                        Allergens & Special Requests ({dayData.allergens.length})
                      </TimeSlotTitle>
                      <div style={{ marginTop: '0.75rem' }}>
                        {dayData.allergens.map((allergen, idx) => (
                          <AllergenAlert key={idx}>
                            <div style={{ fontWeight: '600', color: '#856404', marginBottom: '0.25rem' }}>
                              {allergen.customer} <span style={{ fontSize: '0.85rem', fontWeight: '400' }}>({allergen.bookingRef})</span>
                            </div>
                              {allergen.productName && (
                              <div style={{ fontSize: '0.85rem', color: '#192f6a', marginBottom: '0.25rem', fontWeight: '600' }}>
                                {allergen.productName} {allergen.productQuantity > 1 ? `× ${allergen.productQuantity}` : ''}
                              </div>
                            )}
                            <div style={{ fontSize: '0.9rem', color: '#856404', marginBottom: '0.25rem' }}>
                              <strong>{allergen.question}:</strong> {allergen.answer}
                            </div>
                            {allergen.visitTime && (
                              <div style={{ fontSize: '0.85rem', color: '#999' }}>
                                Time: {formatTime(allergen.visitTime)}
                              </div>
                            )}
                          </AllergenAlert>
                        ))}
                      </div>
                    </TimeSlotSection>
                  )}

                  {dayData.bookings.filter((b) => b.tearoom_staff_notes?.trim()).length > 0 && (
                    <TimeSlotSection>
                      <TimeSlotTitle>
                        <FontAwesomeIcon icon={faCommentDots} style={{ marginRight: '0.5rem' }} />
                        Tearoom special requests ({dayData.bookings.filter((b) => b.tearoom_staff_notes?.trim()).length})
                      </TimeSlotTitle>
                      <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.5rem' }}>
                        {dayData.bookings.filter((b) => b.tearoom_staff_notes?.trim()).map((b) => (
                          <div
                            key={b.id}
                            style={{
                              padding: '0.85rem 1rem',
                              background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                              borderRadius: '10px',
                              border: '2px solid #fcd34d',
                              borderLeft: '5px solid #d97706'
                            }}
                          >
                            <div style={{ fontWeight: '700', color: '#92400e', marginBottom: '0.35rem' }}>
                              {b.customer_name}{' '}
                              <span style={{ fontSize: '0.82rem', fontWeight: '500', color: '#666', fontFamily: 'monospace' }}>({b.booking_reference})</span>
                              {b.visit_time && (
                                <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#64748b' }}>{formatTime(b.visit_time)}</span>
                              )}
                            </div>
                            <div style={{ fontSize: '0.92rem', color: '#78350f', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{b.tearoom_staff_notes}</div>
                          </div>
                        ))}
                      </div>
                    </TimeSlotSection>
                  )}

                  {/* Orders for this day */}
                  <TimeSlotSection>
                    <TimeSlotTitle>
                      <FontAwesomeIcon icon={faList} style={{ marginRight: '0.5rem' }} />
                      Orders for {formatDate(dayData.date)}
                    </TimeSlotTitle>
                    <div style={{ marginTop: '0.75rem' }}>
                      {dayData.orders.map((booking, idx) => (
                        <div key={booking.id} style={{ 
                          padding: '0.75rem', 
                          background: '#f8f9fa', 
                          borderRadius: '6px', 
                          marginBottom: '0.5rem',
                          borderLeft: `4px solid ${getBookingColor(booking)}`
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', color: '#192f6a', marginBottom: '0.25rem' }}>
                                {booking.customer_name} <span style={{ fontSize: '0.85rem', fontWeight: '400', color: '#666' }}>({booking.booking_reference})</span>
                                {(booking.booking_type && booking.booking_type !== 'afternoon_tea') && (
                                  <span style={{ 
                                    marginLeft: '0.35rem', 
                                    fontSize: '0.7rem', 
                                    fontWeight: '600', 
                                    color: 'white', 
                                    background: getBookingColor(booking), 
                                    padding: '0.15rem 0.5rem', 
                                    borderRadius: '4px' 
                                  }}>
                                    {getBookingTypeLabel(booking)}
                                  </span>
                                )}
                                {booking.admin_notes?.includes('Manual booking') && (
                                  <span style={{ marginLeft: '0.35rem', fontSize: '0.7rem', fontWeight: '600', color: '#856404', background: '#fff3cd', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>Manual</span>
                                )}
                              </div>
                              <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                {booking.visit_time && `${formatTime(booking.visit_time)} • `}
                                {(() => {
                                  const guestCount = booking.products?.reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 1), 0) || parseInt(booking.number_of_guests, 10) || 0;
                                  return `${guestCount} guest${guestCount !== 1 ? 's' : ''}`;
                                })()} • {formatCurrency(booking.total_amount)}
                              </div>
                              {booking.products?.some(p => (p.allergen_notes && p.allergen_notes.trim()) || p.questions?.length > 0) && (
                                <Badge variant="danger" style={{ marginTop: '0.5rem' }}>⚠️ Has Allergen Alert</Badge>
                              )}
                              {booking.products?.some(p => p.modifiers?.some(m => m.modifier_data && (m.modifier_data.toLowerCase().includes('inferred') || m.modifier_data.toLowerCase().includes('mixed') || m.modifier_data.toLowerCase().includes('unknown')))) && (
                                <Badge style={{ background: '#f59e0b', color: '#fff', marginTop: '0.5rem', display: 'inline-block' }}>⚑ Check Dietary</Badge>
                              )}
                              {booking.booking_date && new Date(booking.booking_date.replace(' ', 'T')) < new Date('2026-03-25T00:00:00') && !/(TABLE|COACH)/i.test(booking.booking_reference || '') && (
                                <div style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: '#3730a3', background: '#eef2ff', border: '1px solid #6366f1', borderRadius: '4px', padding: '0.2rem 0.5rem', display: 'inline-block' }}>
                                  📋 Historical — please double check allergen info
                                </div>
                              )}
                              {booking.tearoom_staff_notes?.trim() && (
                                <div style={{ marginTop: '0.55rem', padding: '0.5rem 0.65rem', background: '#fffbeb', borderRadius: '6px', border: '1px solid #fcd34d', fontSize: '0.85rem', color: '#78350f', lineHeight: 1.4 }}>
                                  <strong>Tearoom requests:</strong> {booking.tearoom_staff_notes}
                                </div>
                              )}
                            </div>
                            <Button 
                              variant="primary" 
                              onClick={() => handleOpenEditModal(booking)}
                              style={{ fontSize: '0.8rem', padding: '0.35rem 0.7rem', marginRight: '0.35rem' }}
                              title="Edit booking"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </Button>
                            <Button 
                              variant="primary" 
                              onClick={() => handlePrintOrder(booking)}
                              style={{ fontSize: '0.8rem', padding: '0.35rem 0.7rem' }}
                            >
                              <FontAwesomeIcon icon={faFile} style={{ marginRight: '0.5rem' }} />
                              PDF
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </TimeSlotSection>
                </DayOverviewCard>
              ))}

            {/* Overall Modifiers Summary */}
            {Object.keys(statistics.allModifiersByPrompt).length > 0 && (
              <DayOverviewCard>
                <DayHeader>
                  <DayTitle>
                    <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '0.5rem' }} />
                    Overall Modifier Summary
                  </DayTitle>
                </DayHeader>
                {(() => {
                  // Calculate overall STANDARD (no modifier) count
                  const totalWithModifiers = Object.values(statistics.allModifiers).reduce((sum, mod) => sum + mod.count, 0);
                  const standardCount = statistics.totalGuests - totalWithModifiers;
                  
                  const sections = Object.entries(statistics.allModifiersByPrompt)
                    .sort(([, a], [, b]) => {
                      const totalA = Object.values(a).reduce((sum, val) => sum + val, 0);
                      const totalB = Object.values(b).reduce((sum, val) => sum + val, 0);
                      return totalB - totalA;
                    })
                    .map(([prompt, selections]) => (
                      <TimeSlotSection key={prompt}>
                        <TimeSlotTitle>{prompt}</TimeSlotTitle>
                        <StatsRow>
                          {Object.entries(selections)
                            .sort(([, a], [, b]) => b - a)
                            .map(([modifier, count]) => (
                              <StatItem key={modifier}>
                                <StatLabel>{modifier}</StatLabel>
                                <StatValue>{count} total</StatValue>
                              </StatItem>
                            ))}
                        </StatsRow>
                      </TimeSlotSection>
                    ));
                  
                  // Add STANDARD section if there are bookings without modifiers
                  if (standardCount > 0) {
                    sections.push(
                      <TimeSlotSection key="standard">
                        <TimeSlotTitle>Standard (No Modifier)</TimeSlotTitle>
                        <StatsRow>
                          <StatItem>
                            <StatLabel>STANDARD</StatLabel>
                            <StatValue>{standardCount} total</StatValue>
                          </StatItem>
                        </StatsRow>
                      </TimeSlotSection>
                    );
                  }
                  
                  return sections;
                })()}
              </DayOverviewCard>
            )}

            {/* All tearoom requests in date range */}
            {statistics.allTearoomStaffNotes.length > 0 && (
              <DayOverviewCard>
                <DayHeader>
                  <DayTitle>
                    <FontAwesomeIcon icon={faCommentDots} style={{ marginRight: '0.5rem' }} />
                    All tearoom special requests ({statistics.allTearoomStaffNotes.length})
                  </DayTitle>
                </DayHeader>
                <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.75rem' }}>
                  {statistics.allTearoomStaffNotes.map((row, idx) => (
                    <div
                      key={`${row.bookingRef}-${idx}`}
                      style={{
                        padding: '1rem',
                        background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                        borderRadius: '10px',
                        border: '2px solid #fcd34d'
                      }}
                    >
                      <div style={{ fontWeight: '700', color: '#92400e', marginBottom: '0.35rem' }}>
                        {row.customer}{' '}
                        <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#666' }}>({row.bookingRef})</span>
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.5rem' }}>
                        {formatDate(row.visitDate)}
                        {row.visitTime ? ` · ${formatTime(row.visitTime)}` : ''}
                      </div>
                      <div style={{ fontSize: '0.95rem', color: '#78350f', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{row.notes}</div>
                    </div>
                  ))}
                </div>
              </DayOverviewCard>
            )}

            {/* Overall Allergens Summary */}
            {statistics.allAllergens.length > 0 && (
              <DayOverviewCard>
                <DayHeader>
                  <DayTitle>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem' }} />
                    All Allergens & Special Requests ({statistics.allAllergens.length})
                  </DayTitle>
                </DayHeader>
                <div style={{ marginTop: '0.75rem' }}>
                  {statistics.allAllergens.map((allergen, idx) => (
                    <AllergenAlert key={idx}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', color: '#856404', marginBottom: '0.25rem' }}>
                            {allergen.customer} <span style={{ fontSize: '0.85rem', fontWeight: '400' }}>({allergen.bookingRef})</span>
                          </div>
                              {allergen.productName && (
                              <div style={{ fontSize: '0.85rem', color: '#192f6a', marginBottom: '0.25rem', fontWeight: '600' }}>
                                {allergen.productName} {allergen.productQuantity > 1 ? `× ${allergen.productQuantity}` : ''}
                              </div>
                            )}
                          <div style={{ fontSize: '0.9rem', color: '#856404', marginBottom: '0.25rem' }}>
                            <strong>{allergen.question}:</strong> {allergen.answer}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#999' }}>
                            {formatDate(allergen.visitDate)} {allergen.visitTime && `at ${formatTime(allergen.visitTime)}`}
                          </div>
                        </div>
                      </div>
                    </AllergenAlert>
                  ))}
                </div>
              </DayOverviewCard>
            )}
          </ScreenSection>

          {/* Print Section */}
          <PrintSection>
            <h1>Afternoon Tea Bookings Report</h1>
            <p><strong>Generated:</strong> {new Date().toLocaleString('en-GB')}</p>
            <p><strong>Date Range:</strong> {dateFrom || 'All'} to {dateTo || 'All'}</p>
            <p><strong>Total Bookings:</strong> {statistics.totalBookings} | <strong>Total Guests:</strong> {statistics.totalGuests}</p>
            <p><strong>Tearoom special requests:</strong> {statistics.allTearoomStaffNotes.length} booking{statistics.allTearoomStaffNotes.length !== 1 ? 's' : ''} with staff notes (see per-day sections below)</p>
            <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '2px solid #dee2e6' }} />
            {Object.values(statistics.byDate)
              .sort((a, b) => a.date.localeCompare(b.date))
              .map(dayData => (
                <div key={dayData.date} style={{ pageBreakInside: 'avoid', marginBottom: '2rem', padding: '1rem', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                  <h2 style={{ marginTop: 0, color: '#192f6a' }}>{formatDate(dayData.date)}</h2>
                  <p><strong>Bookings:</strong> {dayData.bookings.length} | <strong>Guests:</strong> {dayData.guests}</p>
                  <p style={{ color: '#92400e', fontWeight: '600' }}>
                    <strong>Tearoom requests:</strong>{' '}
                    {dayData.bookings.filter((b) => b.tearoom_staff_notes?.trim()).length || 'none'}
                  </p>
                  
                  {Object.keys(dayData.timeSlots).length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <h3>Time Slots:</h3>
                      {Object.entries(dayData.timeSlots)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([time, data]) => (
                          <div key={time} style={{ marginBottom: '0.5rem', padding: '0.5rem', background: '#f8f9fa', borderRadius: '4px' }}>
                            <strong>{time}:</strong> {data.count} booking{data.count !== 1 ? 's' : ''}, {data.guests} guest{data.guests !== 1 ? 's' : ''}
                            {Object.keys(data.modifiers).length > 0 && (
                              <div style={{ marginLeft: '1rem', fontSize: '0.9em', color: '#666' }}>
                                Modifiers: {Object.entries(data.modifiers).map(([mod, count]) => `${mod} (${count})`).join(', ')}
                              </div>
                            )}
                            {data.allergens.length > 0 && (
                              <div style={{ marginLeft: '1rem', fontSize: '0.9em', color: '#856404', fontWeight: '600' }}>
                                ⚠️ {data.allergens.length} allergen{data.allergens.length !== 1 ? 's' : ''}
                              </div>
                            )}
                            {(data.bookings || []).filter((b) => b.tearoom_staff_notes?.trim()).length > 0 && (
                              <div style={{ marginLeft: '1rem', fontSize: '0.9em', color: '#92400e', fontWeight: '600' }}>
                                📋 {(data.bookings || []).filter((b) => b.tearoom_staff_notes?.trim()).length} tearoom note{(data.bookings || []).filter((b) => b.tearoom_staff_notes?.trim()).length !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                  
                  {Object.keys(dayData.modifiersByPrompt).length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <h3>Modifiers:</h3>
                      {Object.entries(dayData.modifiersByPrompt).map(([prompt, selections]) => (
                        <div key={prompt} style={{ marginBottom: '0.75rem' }}>
                          <strong>{prompt}:</strong>
                          <div style={{ marginLeft: '1rem' }}>
                            {Object.entries(selections).map(([modifier, count]) => (
                              <div key={modifier}>{modifier}: {count}</div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {dayData.allergens.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        Allergens & Special Requests:
                      </h3>
                      {dayData.allergens.map((allergen, idx) => (
                        <div key={idx} style={{ marginBottom: '0.5rem', padding: '0.5rem', background: '#fff3cd', borderRadius: '4px' }}>
                          <strong>{allergen.customer}</strong> ({allergen.bookingRef}){allergen.productName ? ` - ${allergen.productName}${allergen.productQuantity > 1 ? ` × ${allergen.productQuantity}` : ''}` : ''}
                          <br /><strong>{allergen.question}:</strong> {allergen.answer}
                          {allergen.visitTime && ` [Time: ${formatTime(allergen.visitTime)}]`}
                        </div>
                      ))}
                    </div>
                  )}

                  {(dayData.bookings || []).filter((b) => b.tearoom_staff_notes?.trim()).length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <h3>Tearoom special requests (staff)</h3>
                      {(dayData.bookings || []).filter((b) => b.tearoom_staff_notes?.trim()).map((b) => (
                        <div key={b.id} style={{ marginBottom: '0.5rem', padding: '0.6rem 0.75rem', background: '#fffbeb', borderLeft: '4px solid #d97706', borderRadius: '4px', fontSize: '0.92rem' }}>
                          <strong>{b.customer_name}</strong> ({b.booking_reference})
                          {b.visit_time && ` · ${formatTime(b.visit_time)}`}
                          <div style={{ marginTop: '0.35rem', color: '#78350f', whiteSpace: 'pre-wrap' }}>{b.tearoom_staff_notes}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </PrintSection>
        </>
      )}

      {activeTab === 'details' && (() => {
        const searchLower = (detailsSearch || '').trim().toLowerCase();
        const filteredDetailsBookings = searchLower
          ? bookings.filter((b) => {
              const ref = (b.booking_reference || '').toLowerCase();
              const name = (b.customer_name || '').toLowerCase();
              const email = (b.customer_email || '').toLowerCase();
              const phone = (b.customer_phone || '').toLowerCase();
              const tearoom = (b.tearoom_staff_notes || '').toLowerCase();
              return ref.includes(searchLower) || name.includes(searchLower) || email.includes(searchLower) || phone.includes(searchLower) || tearoom.includes(searchLower);
            })
          : bookings;
        return (
        <>
          <ScreenSection>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '220px', maxWidth: '400px' }}>
                <input
                  type="text"
                  placeholder="Search by reference, name, email, phone, or tearoom notes..."
                  value={detailsSearch}
                  onChange={(e) => setDetailsSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '0.95rem'
                  }}
                />
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>
                Showing {filteredDetailsBookings.length} of {total} bookings
                {searchLower && filteredDetailsBookings.length < bookings.length && (
                  <span style={{ marginLeft: '0.5rem', color: '#007bff' }}>(filtered)</span>
                )}
              </div>
            </div>
          </ScreenSection>
          <Table>
            <thead>
              <tr>
                <Th style={{ width: '30px' }}></Th>
                <Th>Booking Reference</Th>
                <Th>Transaction Date/Time</Th>
                <Th>Visit Date/Time</Th>
                <Th>Customer</Th>
                <Th>Email</Th>
                <Th>Total</Th>
                <Th>Products</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredDetailsBookings.map((booking) => {
                const isExpanded = expandedRows.has(booking.id);
                const productsCount = booking.products?.length || 0;
                
                return (
                  <React.Fragment key={booking.id}>
                    <Tr>
                      <Td>
                        <Button
                          variant="primary"
                          onClick={() => toggleRow(booking.id)}
                          style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                        >
                          {isExpanded ? '−' : '+'}
                        </Button>
                      </Td>
                      <Td>
                        <code style={{ fontSize: '0.85rem', background: '#f8f9fa', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                          {booking.booking_reference}
                        </code>
                        {booking.admin_notes?.includes('Manual booking') && (
                          <span style={{ marginLeft: '0.35rem', fontSize: '0.7rem', fontWeight: '600', color: '#856404', background: '#fff3cd', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>Manual</span>
                        )}
                      </Td>
                      <Td>
                        <div style={{ fontSize: '0.9rem' }}>
                          {formatDateTime(booking.booking_date)}
                        </div>
                      </Td>
                      <Td>
                        <div style={{ fontWeight: '600', color: '#192f6a' }}>
                          {formatDate(booking.visit_date)}
                        </div>
                        {booking.visit_time && (
                          <div style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                            {formatTime(booking.visit_time)}
                          </div>
                        )}
                      </Td>
                      <Td>{booking.customer_name}</Td>
                      <Td>{booking.customer_email}</Td>
                      <Td><strong>{formatCurrency(booking.total_amount)}</strong></Td>
                      <Td>
                        <Badge variant="success">{productsCount} product{productsCount !== 1 ? 's' : ''}</Badge>
                        {booking.tearoom_staff_notes?.trim() && (
                          <Badge style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', marginLeft: '0.25rem' }}>
                            Tearoom note
                          </Badge>
                        )}
                        {booking.products?.some(p => (p.allergen_notes && p.allergen_notes.trim()) || p.questions?.length > 0) && (
                          <Badge variant="danger">
                            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.25rem', fontSize: '0.75rem' }} />
                            Allergen
                          </Badge>
                        )}
                        {booking.products?.some(p => p.modifiers?.some(m => m.modifier_data && (m.modifier_data.toLowerCase().includes('inferred') || m.modifier_data.toLowerCase().includes('mixed') || m.modifier_data.toLowerCase().includes('unknown')))) && (
                          <Badge style={{ background: '#f59e0b', color: '#fff', marginLeft: '0.25rem' }}>
                            ⚑ Check Dietary
                          </Badge>
                        )}
                        {booking.booking_date && new Date(booking.booking_date.replace(' ', 'T')) < new Date('2026-03-25T00:00:00') && !/(TABLE|COACH)/i.test(booking.booking_reference || '') && (
                          <Badge style={{ background: '#6366f1', color: '#fff', marginLeft: '0.25rem' }} title="Booking made before the allergen capture system was in place — verify allergen information directly with the customer">
                            📋 Historical — verify allergens
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <Button 
                          variant="primary" 
                          onClick={() => handleOpenEditModal(booking)}
                          style={{ fontSize: '0.8rem', padding: '0.35rem 0.7rem', marginRight: '0.25rem' }}
                          title="Edit booking (notes, time, dietary)"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button 
                          variant="primary" 
                          onClick={() => handlePrintOrder(booking)}
                          style={{ fontSize: '0.8rem', padding: '0.35rem 0.7rem', marginRight: '0.25rem' }}
                          title="Export to PDF"
                        >
                          📄
                        </Button>
                        <Button 
                          variant="warning" 
                          onClick={() => handleOpenTransferModal(booking)}
                          style={{ fontSize: '0.8rem', padding: '0.35rem 0.7rem', marginRight: '0.25rem', background: '#ffc107', color: '#000' }}
                          title="Transfer Booking"
                        >
                          🔄
                        </Button>
                        <Button 
                          onClick={() => handleViewAuditTrail(booking)}
                          style={{ fontSize: '0.8rem', padding: '0.35rem 0.7rem', background: '#6c757d' }}
                          title="View Audit Trail"
                        >
                          <FontAwesomeIcon icon={faFile} />
                        </Button>
                      </Td>
                    </Tr>
                    {isExpanded && (
                      <DetailsRow>
                        <Td colSpan="9">
                          {booking.booking_date && new Date(booking.booking_date.replace(' ', 'T')) < new Date('2026-03-25T00:00:00') && !/(TABLE|COACH)/i.test(booking.booking_reference || '') && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', background: '#eef2ff', border: '2px solid #6366f1', borderRadius: '8px', padding: '0.9rem 1.1rem', marginBottom: '1.25rem' }}>
                              <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>📋</span>
                              <div>
                                <div style={{ fontWeight: '700', color: '#3730a3', fontSize: '0.95rem', marginBottom: '0.2rem' }}>Historical booking — please double check allergen information</div>
                                <div style={{ fontSize: '0.85rem', color: '#4338ca' }}>This booking was made before 25 March 2026. Allergen data may not have been fully captured. Please confirm dietary requirements directly with the customer before their visit.</div>
                              </div>
                            </div>
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '1.5rem' }}>
                            <div>
                              <h4 style={{ margin: '0 0 0.75rem 0', color: '#192f6a', fontSize: '1rem', fontWeight: '600' }}>Customer Information</h4>
                              <div style={{ fontSize: '0.9rem', color: '#333', lineHeight: '1.8' }}>
                                <div><strong>Name:</strong> {booking.customer_name}</div>
                                <div><strong>Email:</strong> {booking.customer_email}</div>
                                {booking.customer_phone && <div><strong>Phone:</strong> {booking.customer_phone}</div>}
                                <div><strong>Transaction Date:</strong> {formatDateTime(booking.booking_date)}</div>
                              </div>
                            </div>
                            <div>
                              <h4 style={{ margin: '0 0 0.75rem 0', color: '#192f6a', fontSize: '1rem', fontWeight: '600' }}>Visit Information</h4>
                              <div style={{ fontSize: '0.9rem', color: '#333', lineHeight: '1.8' }}>
                                <div><strong>Visit Date:</strong> {formatDate(booking.visit_date)}</div>
                                {booking.visit_time && <div><strong>Visit Time:</strong> {formatTime(booking.visit_time)}</div>}
                              </div>
                            </div>
                          </div>

                          {booking.tearoom_staff_notes?.trim() && (
                            <div style={{ marginBottom: '1.25rem', padding: '1rem 1.15rem', background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', borderRadius: '10px', border: '2px solid #fcd34d' }}>
                              <h4 style={{ margin: '0 0 0.5rem 0', color: '#92400e', fontSize: '1rem', fontWeight: '700' }}>Tearoom special requests</h4>
                              <div style={{ whiteSpace: 'pre-wrap', color: '#78350f', fontSize: '0.95rem', lineHeight: 1.5 }}>{booking.tearoom_staff_notes}</div>
                              <div style={{ fontSize: '0.78rem', color: '#92400e', marginTop: '0.65rem', fontStyle: 'italic' }}>Visible on daily print sheet and calendar day view for tearoom staff.</div>
                            </div>
                          )}
                          
                          {booking.products && booking.products.length > 0 && (
                            <div>
                              <h4 style={{ margin: '0 0 1rem 0', color: '#192f6a', fontSize: '1rem', fontWeight: '600' }}>Order Details</h4>
                              <ProductList>
                                {booking.products.map((product) => {
                                  return (
                                  <ProductItem key={product.id} style={{ marginBottom: '1rem', padding: '1rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                                      <div>
                                        <div style={{ fontWeight: '600', fontSize: '1rem', color: '#192f6a', marginBottom: '0.25rem' }}>
                                          {product.product_name} × {product.quantity}
                                        </div>
                                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                          {formatCurrency(product.unit_price)} each = <strong>{formatCurrency(product.total_price)}</strong>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {product.modifiers && product.modifiers.length > 0 && (
                                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '4px', borderLeft: '3px solid #007bff' }}>
                                        <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#333', marginBottom: '0.5rem' }}>Modifiers:</div>
                                        <ModifierList style={{ margin: 0, padding: 0 }}>
                                          {product.modifiers.map((modifier, idx) => {
                                            const isInferredOrMixed = modifier.modifier_data &&
                                              (modifier.modifier_data.toLowerCase().includes('inferred') ||
                                               modifier.modifier_data.toLowerCase().includes('mixed') ||
                                               modifier.modifier_data.toLowerCase().includes('unknown'));
                                            const isMixed = modifier.modifier_data && (modifier.modifier_data.toLowerCase().includes('mixed') || modifier.modifier_data.toLowerCase().includes('unknown'));
                                            return (
                                            <li key={idx} style={{ marginBottom: '0.5rem', padding: '0.5rem', background: isInferredOrMixed ? (isMixed ? '#f8fafc' : '#fffbeb') : 'white', borderRadius: '4px', border: isInferredOrMixed ? `1px solid ${isMixed ? '#94a3b8' : '#f59e0b'}` : 'none' }}>
                                              {modifier.modifier_value ? (
                                                <>
                                                  <div style={{ color: '#666', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                                                    {modifier.modifier_value}
                                                  </div>
                                                  <strong style={{ color: '#192f6a', fontSize: '0.95rem' }}>
                                                    {modifier.modifier_name}
                                                  </strong>
                                                </>
                                              ) : (
                                                <strong style={{ color: '#192f6a' }}>
                                                  {modifier.modifier_name}
                                                </strong>
                                              )}
                                              {modifier.modifier_price > 0 && (
                                                <div style={{ color: '#9d6d49', marginTop: '0.25rem', fontWeight: '600' }}>
                                                  Additional: {formatCurrency(modifier.modifier_price)}
                                                </div>
                                              )}
                                              {isInferredOrMixed && (
                                                <div style={{ marginTop: '0.4rem', fontSize: '0.82rem', color: isMixed ? '#475569' : '#92400e', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                                  {isMixed ? '⚠' : '⚑'} {modifier.modifier_data}
                                                </div>
                                              )}
                                            </li>
                                            );
                                          })}
                                        </ModifierList>
                                      </div>
                                    )}
                                    
                                    {product.allergen_notes && (
                                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#fffbf0', borderRadius: '4px', borderLeft: '3px solid #f0ad4e' }}>
                                        <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#333', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f0ad4e' }} />
                                          Allergen Information:
                                        </div>
                                        <div style={{ color: '#333', fontSize: '0.95rem', padding: '0.5rem', background: 'white', borderRadius: '4px', whiteSpace: 'pre-wrap' }}>
                                          {product.allergen_notes}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '0.7rem' }} />
                                          This information is stored separately from Venpos and is for internal tracking only.
                                        </div>
                                      </div>
                                    )}
                                    
                                    {product.questions && product.questions.length > 0 && (
                                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#fff3cd', borderRadius: '4px', borderLeft: '3px solid #ffc107' }}>
                                        <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#333', marginBottom: '0.5rem' }}>Special Requests / Allergies:</div>
                                        <QuestionList style={{ margin: 0, padding: 0 }}>
                                          {product.questions.map((question, idx) => (
                                            <li key={idx} style={{ marginBottom: '0.5rem', padding: '0.5rem', background: 'white', borderRadius: '4px' }}>
                                              <div style={{ fontWeight: '600', color: '#192f6a', marginBottom: '0.25rem' }}>
                                                {question.question_text}
                                              </div>
                                              <div style={{ color: '#333', fontSize: '0.9rem', paddingLeft: '0.5rem', borderLeft: '2px solid #ffc107' }}>
                                                {question.answer}
                                              </div>
                                            </li>
                                          ))}
                                        </QuestionList>
                                      </div>
                                    )}
                                  </ProductItem>
                                  );
                                })}
                              </ProductList>
                            </div>
                          )}
                        </Td>
                      </DetailsRow>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </>
        );
      })()}

      {bookings.length === 0 && activeTab === 'details' && (
        <EmptyState>
          <p>No afternoon tea bookings found.</p>
          <p style={{ fontSize: '0.9rem', color: '#999', marginTop: '0.5rem' }}>
            {total > 0 ? `Total: ${total} bookings` : 'Try adjusting your date filters.'}
          </p>
        </EmptyState>
      )}

      {/* ── Merchandise Pickups Tab ── */}
      {activeTab === 'merchandise' && (
        <ScreenSection>
          {/* Controls row */}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: '0.25rem' }}>Date</label>
              <input
                type="date"
                value={merchandiseDate}
                onChange={e => setMerchandiseDate(e.target.value)}
                style={{ padding: '0.4rem 0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: '0.25rem' }}>Status</label>
              <select
                value={merchandiseStatusFilter}
                onChange={e => setMerchandiseStatusFilter(e.target.value)}
                style={{ padding: '0.4rem 0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="collected">Collected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <button
              onClick={() => fetchMerchandisePickups(merchandiseDate, merchandiseStatusFilter)}
              style={{
                marginTop: '1.1rem', padding: '0.45rem 1rem', background: '#101d40', color: '#fff',
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
              }}
            >
              <FontAwesomeIcon icon={faSync} style={{ marginRight: '0.4rem' }} />
              Refresh
            </button>
          </div>

          {/* Summary counts */}
          {!merchandiseLoading && merchandisePickups.length > 0 && (() => {
            const pending   = merchandisePickups.filter(p => p.pickup_status === 'pending').length;
            const collected = merchandisePickups.filter(p => p.pickup_status === 'collected').length;
            const totalQty  = merchandisePickups.reduce((s, p) => s + (parseInt(p.item_quantity, 10) || 1), 0);
            return (
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                {[
                  { label: 'Total items', value: totalQty, bg: '#f0f4ff' },
                  { label: 'Pending collection', value: pending, bg: '#fff8e1' },
                  { label: 'Collected', value: collected, bg: '#e8f5e9' },
                ].map(c => (
                  <div key={c.label} style={{ background: c.bg, borderRadius: '8px', padding: '0.6rem 1.1rem', minWidth: '130px' }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#101d40' }}>{c.value}</div>
                    <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.1rem' }}>{c.label}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          {merchandiseLoading && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
              Loading merchandise pickups…
            </div>
          )}

          {merchandiseError && (
            <div style={{ background: '#fff3f3', border: '1px solid #f5c6c6', borderRadius: '8px', padding: '1rem', color: '#c00', marginBottom: '1rem' }}>
              {merchandiseError}
            </div>
          )}

          {!merchandiseLoading && !merchandiseError && merchandisePickups.length === 0 && (
            <EmptyState>
              <p>No merchandise pickups found for {merchandiseDate || 'this date'}.</p>
            </EmptyState>
          )}

          {!merchandiseLoading && merchandisePickups.length > 0 && (
            <Table>
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>Time</th>
                  <th>Customer</th>
                  <th>Item</th>
                  <th style={{ textAlign: 'center', width: '60px' }}>Qty</th>
                  <th style={{ textAlign: 'right', width: '80px' }}>Total</th>
                  <th style={{ textAlign: 'center', width: '130px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {merchandisePickups.map(p => (
                  <tr key={p.id} style={{ opacity: p.pickup_status === 'cancelled' ? 0.45 : 1 }}>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {p.visit_time || '—'}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{p.customer_name || '—'}</div>
                      {p.customer_email && (
                        <div style={{ fontSize: '0.78rem', color: '#666' }}>{p.customer_email}</div>
                      )}
                      {p.customer_phone && (
                        <div style={{ fontSize: '0.78rem', color: '#666' }}>{p.customer_phone}</div>
                      )}
                      <div style={{ fontSize: '0.72rem', color: '#aaa', marginTop: '0.1rem' }}>Ref: {p.booking_reference}</div>
                    </td>
                    <td>{p.item_name}</td>
                    <td style={{ textAlign: 'center', fontWeight: 700 }}>{p.item_quantity}</td>
                    <td style={{ textAlign: 'right' }}>£{parseFloat(p.item_total || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'center' }}>
                      {p.pickup_status === 'collected' ? (
                        <span
                          onClick={() => handleMerchandiseStatusUpdate(p.id, 'pending')}
                          style={{
                            display: 'inline-block', padding: '0.25rem 0.65rem', borderRadius: '999px',
                            fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                            background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7'
                          }}
                          title="Click to mark as pending"
                        >
                          ✓ Collected
                        </span>
                      ) : p.pickup_status === 'cancelled' ? (
                        <span style={{
                          display: 'inline-block', padding: '0.25rem 0.65rem', borderRadius: '999px',
                          fontSize: '0.75rem', fontWeight: 700,
                          background: '#fafafa', color: '#999', border: '1px solid #e0e0e0'
                        }}>
                          Cancelled
                        </span>
                      ) : (
                        <button
                          onClick={() => handleMerchandiseStatusUpdate(p.id, 'collected')}
                          style={{
                            padding: '0.25rem 0.65rem', borderRadius: '999px', fontSize: '0.75rem',
                            fontWeight: 700, cursor: 'pointer', border: '1px solid #ffe082',
                            background: '#fff8e1', color: '#f57f17'
                          }}
                        >
                          Mark Collected
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ScreenSection>
      )}

      {/* Vineyard Tours Tab — dedicated tables, not afternoon tea */}
      {activeTab === 'vineyard_tours' && (() => {
        const DIETARY_LABEL_COLOURS = {
          'None':          { bg: '#f1f5f9', color: '#475569' },
          'Pescetarian':   { bg: '#e0f2fe', color: '#0369a1' },
          'No Red Meat':   { bg: '#fef3c7', color: '#92400e' },
          'Vegetarian':    { bg: '#dcfce7', color: '#166534' },
          'Vegan':         { bg: '#bbf7d0', color: '#14532d' },
          'Gluten Free':   { bg: '#fce7f3', color: '#9d174d' },
          'Coeliac':       { bg: '#ffe4e6', color: '#9f1239' },
          'Other':         { bg: '#f3e8ff', color: '#6b21a8' },
        };
        const dietaryPill = (label) => {
          const c = DIETARY_LABEL_COLOURS[label] || { bg: '#f1f5f9', color: '#475569' };
          return (
            <span style={{
              display: 'inline-block', padding: '0.2rem 0.55rem',
              borderRadius: '999px', fontSize: '0.78rem', fontWeight: 700,
              background: c.bg, color: c.color, border: `1px solid ${c.color}22`
            }}>
              {label}
            </span>
          );
        };

        const vtBookings = vineyardBookings || [];

        const dietaryCounts = {};
        vtBookings.forEach(b => {
          (b.guests || []).forEach(g => {
            const choice = g.dietary_choice || 'None';
            dietaryCounts[choice] = (dietaryCounts[choice] || 0) + 1;
          });
        });

        const totalVTGuests = vtBookings.reduce(
          (sum, b) => sum + (parseInt(b.number_of_guests, 10) || (b.guests || []).length || 0),
          0
        );

        return (
          <ScreenSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
              <div>
                <div style={{ fontWeight: 700, color: '#192f6a', fontSize: '1.1rem' }}>Vineyard Tour Bookings</div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.2rem' }}>
                  Stored separately from afternoon tea — dietary requirements for tasting boxes
                </div>
              </div>
              <button
                onClick={fetchVineyardBookings}
                style={{
                  padding: '0.45rem 1rem', background: '#101d40', color: '#fff',
                  border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
                }}
              >
                <FontAwesomeIcon icon={faSync} spin={vineyardLoading} style={{ marginRight: '0.4rem' }} />
                Refresh
              </button>
            </div>

            <SummaryGrid style={{ marginBottom: '1.5rem' }}>
              <SummaryCard variant="primary">
                <SummaryLabel><FontAwesomeIcon icon={faList} /> Total Bookings</SummaryLabel>
                <SummaryValue>{vtBookings.length}</SummaryValue>
              </SummaryCard>
              <SummaryCard variant="success">
                <SummaryLabel><FontAwesomeIcon icon={faUsers} /> Total Guests</SummaryLabel>
                <SummaryValue>{totalVTGuests}</SummaryValue>
              </SummaryCard>
            </SummaryGrid>

            {Object.keys(dietaryCounts).length > 0 && (
              <div style={{ background: 'white', borderRadius: '10px', padding: '1.25rem 1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
                <div style={{ fontWeight: 700, color: '#192f6a', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FontAwesomeIcon icon={faUtensils} />
                  Dietary Requirements Summary
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {Object.entries(dietaryCounts)
                    .sort(([, a], [, b]) => b - a)
                    .map(([choice, count]) => (
                      <div key={choice} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#f8f9fa', borderRadius: '8px', padding: '0.6rem 1rem' }}>
                        {dietaryPill(choice)}
                        <span style={{ fontWeight: 700, fontSize: '1.15rem', color: '#101d40' }}>× {count}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {vineyardLoading && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                Loading vineyard tour bookings…
              </div>
            )}

            {vineyardError && (
              <div style={{ background: '#fff3f3', border: '1px solid #f5c6c6', borderRadius: '8px', padding: '1rem', color: '#c00', marginBottom: '1rem' }}>
                {vineyardError}
              </div>
            )}

            {!vineyardLoading && !vineyardError && vtBookings.length === 0 && (
              <EmptyState>
                <p>No vineyard tour bookings yet.</p>
                <p style={{ fontSize: '0.85rem', color: '#888', marginTop: '0.5rem' }}>
                  Saturdays 15 &amp; 29 August · 12:00–14:30 &amp; 15:00–17:30
                </p>
              </EmptyState>
            )}

            {!vineyardLoading && vtBookings.length > 0 && (
              <Table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Slot</th>
                    <th>Customer</th>
                    <th>Ref</th>
                    <th style={{ textAlign: 'center' }}>Guests</th>
                    <th>Dietary per guest</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {vtBookings.map(b => {
                    const guests = b.guests || [];
                    const guestCount = parseInt(b.number_of_guests, 10) || guests.length || 0;
                    return (
                      <tr key={b.id || b.booking_reference}>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                          {b.visit_date
                            ? new Date(b.visit_date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
                            : '—'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{b.visit_time ? String(b.visit_time).substring(0, 5) : '—'}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{b.customer_name || '—'}</div>
                          {b.customer_email && <div style={{ fontSize: '0.78rem', color: '#666' }}>{b.customer_email}</div>}
                          {b.customer_phone && <div style={{ fontSize: '0.78rem', color: '#666' }}>{b.customer_phone}</div>}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: '#666' }}>{b.booking_reference || '—'}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700 }}>{guestCount}</td>
                        <td>
                          {guests.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                              {guests.map((g) => (
                                <div key={g.id || g.guest_number} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                  {guestCount > 1 && (
                                    <span style={{ fontSize: '0.72rem', color: '#aaa', minWidth: '52px' }}>
                                      Guest {g.guest_number}
                                    </span>
                                  )}
                                  {dietaryPill(g.dietary_choice || 'None')}
                                  {g.dietary_notes && (
                                    <span style={{ fontSize: '0.78rem', color: '#666', fontStyle: 'italic' }}>– {g.dietary_notes}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            dietaryPill('None')
                          )}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>£{parseFloat(b.total_amount || 0).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </ScreenSection>
        );
      })()}

      {activeTab === 'santas_grotto' && (() => {
        const sgBookings = grottoBookings || [];
        const allChildren = sgBookings.flatMap(b => (b.children || []).map(c => ({
          ...c,
          visit_date: b.visit_date,
          visit_time: b.visit_time,
          customer_name: b.customer_name,
          location: c.location || b.location,
        })));
        const totalChildren = allChildren.length;
        const ageBands = { '0–3': 0, '4–6': 0, '7–9': 0, '10–12': 0, '13+': 0 };
        allChildren.forEach(c => {
          const age = parseInt(c.child_age, 10);
          if (Number.isNaN(age) || age <= 3) ageBands['0–3'] += 1;
          else if (age <= 6) ageBands['4–6'] += 1;
          else if (age <= 9) ageBands['7–9'] += 1;
          else if (age <= 12) ageBands['10–12'] += 1;
          else ageBands['13+'] += 1;
        });
        const byDate = {};
        sgBookings.forEach(b => {
          const key = b.visit_date || 'undated';
          if (!byDate[key]) byDate[key] = [];
          byDate[key].push(b);
        });
        const dateKeys = Object.keys(byDate).sort();
        const formatGrottoDate = (iso) => {
          if (!iso || iso === 'undated') return 'Date TBC';
          return new Date(iso + 'T12:00:00').toLocaleDateString('en-GB', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
          });
        };

        return (
          <ScreenSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
              <div>
                <div style={{ fontWeight: 700, color: '#192f6a', fontSize: '1.1rem' }}>Santa's Grotto</div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.2rem' }}>
                  Child names and ages for gift preparation — Castle and Retail Village
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => window.print()}
                  style={{
                    padding: '0.45rem 1rem', background: '#fff', color: '#101d40',
                    border: '1px solid #d0d5dd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
                  }}
                >
                  <FontAwesomeIcon icon={faPrint} style={{ marginRight: '0.4rem' }} />
                  Print list
                </button>
                <button
                  onClick={fetchGrottoBookings}
                  style={{
                    padding: '0.45rem 1rem', background: '#101d40', color: '#fff',
                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
                  }}
                >
                  <FontAwesomeIcon icon={faSync} spin={grottoLoading} style={{ marginRight: '0.4rem' }} />
                  Refresh
                </button>
              </div>
            </div>

            <SummaryGrid style={{ marginBottom: '1.5rem' }}>
              <SummaryCard variant="primary">
                <SummaryLabel><FontAwesomeIcon icon={faList} /> Bookings</SummaryLabel>
                <SummaryValue>{sgBookings.length}</SummaryValue>
              </SummaryCard>
              <SummaryCard variant="success">
                <SummaryLabel><FontAwesomeIcon icon={faGift} /> Children</SummaryLabel>
                <SummaryValue>{totalChildren}</SummaryValue>
              </SummaryCard>
            </SummaryGrid>

            {totalChildren > 0 && (
              <div style={{ background: 'white', borderRadius: '10px', padding: '1.25rem 1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
                <div style={{ fontWeight: 700, color: '#192f6a', marginBottom: '1rem' }}>Age groups</div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {Object.entries(ageBands).map(([label, count]) => (
                    <div key={label} style={{ background: '#f8f9fa', borderRadius: '8px', padding: '0.6rem 1rem', minWidth: '90px' }}>
                      <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#101d40' }}>{count}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {grottoLoading && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                Loading Santa's Grotto bookings…
              </div>
            )}

            {grottoError && (
              <div style={{ background: '#fff3f3', border: '1px solid #f5c6c6', borderRadius: '8px', padding: '1rem', color: '#c00', marginBottom: '1rem' }}>
                {grottoError}
              </div>
            )}

            {!grottoLoading && !grottoError && sgBookings.length === 0 && (
              <EmptyState>
                <p>No Santa's Grotto bookings yet.</p>
                <p style={{ fontSize: '0.85rem', color: '#888', marginTop: '0.5rem' }}>
                  Child names and ages will appear here after a Grotto booking is completed.
                </p>
              </EmptyState>
            )}

            {!grottoLoading && dateKeys.map((dateKey) => {
              const dayBookings = byDate[dateKey];
              const dayChildren = dayBookings.reduce((sum, b) => sum + (b.children || []).length, 0);
              return (
                <div key={dateKey} style={{ marginBottom: '1.75rem' }}>
                  <div style={{
                    background: '#101d40', color: '#fff', borderRadius: '10px 10px 0 0',
                    padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem'
                  }}>
                    <strong>{formatGrottoDate(dateKey)}</strong>
                    <span>{dayBookings.length} booking{dayBookings.length === 1 ? '' : 's'} · {dayChildren} child{dayChildren === 1 ? '' : 'ren'}</span>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #e9ecef', borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
                    {dayBookings.map((b) => (
                      <div key={b.id || b.booking_reference} style={{ padding: '1.15rem 1.25rem', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ marginBottom: '0.85rem' }}>
                          <div style={{ fontWeight: 700, color: '#101d40', fontSize: '1.02rem' }}>{b.customer_name || '—'}</div>
                          <div style={{ fontSize: '0.82rem', color: '#666', marginTop: '0.2rem' }}>
                            {b.visit_time ? String(b.visit_time).substring(0, 5) : 'Time TBC'}
                            {b.location ? ` · ${b.location}` : ''}
                            {b.booking_reference ? ` · ${b.booking_reference}` : ''}
                          </div>
                          {(b.customer_email || b.customer_phone) && (
                            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.15rem' }}>
                              {[b.customer_email, b.customer_phone].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                        {(b.children || []).length === 0 ? (
                          <div style={{ color: '#888', fontSize: '0.9rem' }}>No child details saved</div>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                            {(b.children || []).map((child) => (
                              <div
                                key={child.id || `${b.id}-${child.child_number}`}
                                style={{
                                  background: '#fff7ed',
                                  border: '1px solid #fdba74',
                                  borderRadius: '10px',
                                  padding: '0.7rem 0.95rem',
                                  minWidth: '140px',
                                }}
                              >
                                <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9a3412', marginBottom: '0.2rem' }}>
                                  Child {child.child_number || ''}
                                </div>
                                <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#7c2d12', lineHeight: 1.2 }}>
                                  {child.child_name || '—'}
                                </div>
                                <div style={{ fontSize: '0.95rem', color: '#9a3412', marginTop: '0.15rem', fontWeight: 700 }}>
                                  Age {child.child_age}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </ScreenSection>
        );
      })()}

      {activeTab === 'search_for_santa' && (() => {
        const sfsBookings = searchForSantaBookings || [];
        const allChildren = sfsBookings.flatMap(b => (b.children || []).map(c => ({
          ...c,
          visit_date: b.visit_date,
          visit_time: b.visit_time,
          customer_name: b.customer_name,
        })));
        const totalChildren = allChildren.length;
        const totalAdults = sfsBookings.reduce((sum, b) => sum + (parseInt(b.number_of_adults, 10) || 0), 0);
        const ageBands = { '0–3': 0, '4–6': 0, '7–9': 0, '10–12': 0, '13+': 0 };
        allChildren.forEach(c => {
          const age = parseInt(c.child_age, 10);
          if (Number.isNaN(age) || age <= 3) ageBands['0–3'] += 1;
          else if (age <= 6) ageBands['4–6'] += 1;
          else if (age <= 9) ageBands['7–9'] += 1;
          else if (age <= 12) ageBands['10–12'] += 1;
          else ageBands['13+'] += 1;
        });
        const byDate = {};
        sfsBookings.forEach(b => {
          const key = b.visit_date || 'undated';
          if (!byDate[key]) byDate[key] = [];
          byDate[key].push(b);
        });
        const dateKeys = Object.keys(byDate).sort();
        const formatSfsDate = (iso) => {
          if (!iso || iso === 'undated') return 'Date TBC';
          return new Date(iso + 'T12:00:00').toLocaleDateString('en-GB', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
          });
        };

        return (
          <ScreenSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
              <div>
                <div style={{ fontWeight: 700, color: '#192f6a', fontSize: '1.1rem' }}>Search for Santa</div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.2rem' }}>
                  Child names and ages for Child and Under 3 tickets, plus adult ticket counts
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => window.print()}
                  style={{
                    padding: '0.45rem 1rem', background: '#fff', color: '#101d40',
                    border: '1px solid #d0d5dd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
                  }}
                >
                  <FontAwesomeIcon icon={faPrint} style={{ marginRight: '0.4rem' }} />
                  Print list
                </button>
                <button
                  onClick={fetchSearchForSantaBookings}
                  style={{
                    padding: '0.45rem 1rem', background: '#101d40', color: '#fff',
                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
                  }}
                >
                  <FontAwesomeIcon icon={faSync} spin={searchForSantaLoading} style={{ marginRight: '0.4rem' }} />
                  Refresh
                </button>
              </div>
            </div>

            <SummaryGrid style={{ marginBottom: '1.5rem' }}>
              <SummaryCard variant="primary">
                <SummaryLabel><FontAwesomeIcon icon={faList} /> Bookings</SummaryLabel>
                <SummaryValue>{sfsBookings.length}</SummaryValue>
              </SummaryCard>
              <SummaryCard variant="success">
                <SummaryLabel><FontAwesomeIcon icon={faGift} /> Children</SummaryLabel>
                <SummaryValue>{totalChildren}</SummaryValue>
              </SummaryCard>
              <SummaryCard variant="info">
                <SummaryLabel><FontAwesomeIcon icon={faUsers} /> Adults</SummaryLabel>
                <SummaryValue>{totalAdults}</SummaryValue>
              </SummaryCard>
            </SummaryGrid>

            {totalChildren > 0 && (
              <div style={{ background: 'white', borderRadius: '10px', padding: '1.25rem 1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
                <div style={{ fontWeight: 700, color: '#192f6a', marginBottom: '1rem' }}>Age groups</div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {Object.entries(ageBands).map(([label, count]) => (
                    <div key={label} style={{ background: '#f8f9fa', borderRadius: '8px', padding: '0.6rem 1rem', minWidth: '90px' }}>
                      <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#101d40' }}>{count}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {searchForSantaLoading && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                Loading Search for Santa bookings…
              </div>
            )}

            {searchForSantaError && (
              <div style={{ background: '#fff3f3', border: '1px solid #f5c6c6', borderRadius: '8px', padding: '1rem', color: '#c00', marginBottom: '1rem' }}>
                {searchForSantaError}
              </div>
            )}

            {!searchForSantaLoading && !searchForSantaError && sfsBookings.length === 0 && (
              <EmptyState>
                <p>No Search for Santa bookings yet.</p>
                <p style={{ fontSize: '0.85rem', color: '#888', marginTop: '0.5rem' }}>
                  Child names and ages, plus adult tickets, will appear here after a Search for Santa booking is completed.
                </p>
              </EmptyState>
            )}

            {!searchForSantaLoading && dateKeys.map((dateKey) => {
              const dayBookings = byDate[dateKey];
              const dayChildren = dayBookings.reduce((sum, b) => sum + (b.children || []).length, 0);
              const dayAdults = dayBookings.reduce((sum, b) => sum + (parseInt(b.number_of_adults, 10) || 0), 0);
              return (
                <div key={dateKey} style={{ marginBottom: '1.75rem' }}>
                  <div style={{
                    background: '#101d40', color: '#fff', borderRadius: '10px 10px 0 0',
                    padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem'
                  }}>
                    <strong>{formatSfsDate(dateKey)}</strong>
                    <span>
                      {dayBookings.length} booking{dayBookings.length === 1 ? '' : 's'} · {dayChildren} child{dayChildren === 1 ? '' : 'ren'} · {dayAdults} adult{dayAdults === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #e9ecef', borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
                    {dayBookings.map((b) => (
                      <div key={b.id || b.booking_reference} style={{ padding: '1.15rem 1.25rem', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ marginBottom: '0.85rem' }}>
                          <div style={{ fontWeight: 700, color: '#101d40', fontSize: '1.02rem' }}>{b.customer_name || '—'}</div>
                          <div style={{ fontSize: '0.82rem', color: '#666', marginTop: '0.2rem' }}>
                            {b.visit_time ? String(b.visit_time).substring(0, 5) : 'Time TBC'}
                            {b.booking_reference ? ` · ${b.booking_reference}` : ''}
                            {b.total_amount ? ` · £${parseFloat(b.total_amount).toFixed(2)}` : ''}
                          </div>
                          {(b.customer_email || b.customer_phone) && (
                            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.15rem' }}>
                              {[b.customer_email, b.customer_phone].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                        {(b.adults || []).length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            {(b.adults || []).map((adult) => (
                              <div
                                key={adult.id || `${b.id}-adult-${adult.ticket_name}`}
                                style={{
                                  background: '#eef2ff',
                                  border: '1px solid #c7d2fe',
                                  borderRadius: '10px',
                                  padding: '0.55rem 0.85rem',
                                }}
                              >
                                <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#3730a3' }}>
                                  Adult tickets
                                </div>
                                <div style={{ fontWeight: 700, color: '#1e1b4b', marginTop: '0.15rem' }}>
                                  {adult.quantity} × {adult.ticket_name || 'Search for Santa (Adult)'}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {(b.children || []).length === 0 ? (
                          <div style={{ color: '#888', fontSize: '0.9rem' }}>No child details saved</div>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                            {(b.children || []).map((child) => (
                              <div
                                key={child.id || `${b.id}-${child.child_number}`}
                                style={{
                                  background: '#fff7ed',
                                  border: '1px solid #fdba74',
                                  borderRadius: '10px',
                                  padding: '0.7rem 0.95rem',
                                  minWidth: '140px',
                                }}
                              >
                                <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9a3412', marginBottom: '0.2rem' }}>
                                  {child.ticket_type === 'under_3' ? 'Under 3' : `Child ${child.child_number || ''}`}
                                </div>
                                <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#7c2d12', lineHeight: 1.2 }}>
                                  {child.child_name || '—'}
                                </div>
                                <div style={{ fontSize: '0.95rem', color: '#9a3412', marginTop: '0.15rem', fontWeight: 700 }}>
                                  Age {child.child_age}
                                </div>
                                {child.ticket_name && (
                                  <div style={{ fontSize: '0.72rem', color: '#9a3412', marginTop: '0.25rem' }}>
                                    {child.ticket_name}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </ScreenSection>
        );
      })()}

      {/* Transfer Booking Modal */}
      {showTransferModal && transferBooking && (
        <Modal onClick={handleCloseTransferModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <FontAwesomeIcon icon={faExchangeAlt} style={{ fontSize: '1.75rem', marginRight: '0.75rem' }} />
                <span>{isLocalTableBooking(transferBooking) ? 'Transfer Table Booking' : 'Transfer Booking'}</span>
              </ModalTitle>
              <ModalClose onClick={handleCloseTransferModal} style={{ fontSize: '1.5rem', fontWeight: 'bold', cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faTimes} />
              </ModalClose>
            </ModalHeader>

            {/* Current Booking Summary - Large and Clear */}
            <div style={{
              background: 'linear-gradient(135deg, #192f6a 0%, #2c4a8a 100%)',
              color: 'white',
              padding: '2rem',
              borderRadius: '12px',
              marginBottom: '2rem',
              boxShadow: '0 4px 12px rgba(25, 47, 106, 0.3)'
            }}>
              <div style={{ 
                fontSize: '1.1rem', 
                fontWeight: '600', 
                marginBottom: '1.5rem',
                opacity: 0.95,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <FontAwesomeIcon icon={faList} />
                Current Booking Details
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '1.5rem',
                fontSize: '1.05rem'
              }}>
                <div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem' }}>Transaction Reference</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700' }}>
                    {transferBooking.admin_notes?.includes('Manual booking') && transferBooking.transaction_reference
                      ? transferBooking.transaction_reference
                      : transferBooking.booking_reference}
                  </div>
                  {transferBooking.admin_notes?.includes('Manual booking') && (
                    <span style={{
                      display: 'inline-block',
                      marginTop: '0.35rem',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: '#856404',
                      background: '#fff3cd',
                      padding: '0.2rem 0.5rem',
                      borderRadius: '4px'
                    }}>
                      Manual booking
                    </span>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem' }}>Customer Name</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700' }}>{transferBooking.customer_name}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem' }}>Current Date & Time</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700' }}>
                    {formatDate(transferBooking.visit_date)} at {formatTime(transferBooking.visit_time)}
                  </div>
                </div>
              </div>
            </div>

            {isLocalTableBooking(transferBooking) && (
              <div style={{ marginBottom: '2rem' }}>
                <div style={{
                  background: '#e7f5ff',
                  border: '2px solid #17a2b8',
                  borderRadius: '10px',
                  padding: '1rem 1.25rem',
                  marginBottom: '1.5rem',
                  fontSize: '0.95rem',
                  color: '#0c5460',
                  lineHeight: 1.5
                }}>
                  <strong>Table booking</strong> — this updates the tearoom calendar and audit trail only. Venpos is not used for standard table bookings.
                </div>

                <div style={{
                  marginBottom: '2rem',
                  paddingBottom: '2rem',
                  borderBottom: '2px solid #e9ecef'
                }}>
                  <div style={{
                    fontSize: '1.3rem',
                    fontWeight: '700',
                    color: '#192f6a',
                    marginBottom: '1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    <span style={{
                      background: '#17a2b8',
                      color: 'white',
                      width: '2.5rem',
                      height: '2.5rem',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      fontWeight: '700'
                    }}>2</span>
                    <span>New visit date &amp; time</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <FormGroup style={{ marginBottom: 0 }}>
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={simpleTransferNewDate}
                        onChange={(e) => setSimpleTransferNewDate(e.target.value)}
                        style={{ fontSize: '1.05rem', padding: '0.85rem' }}
                      />
                    </FormGroup>
                    <FormGroup style={{ marginBottom: 0 }}>
                      <Label>Time *</Label>
                      <Input
                        type="time"
                        value={simpleTransferNewTime}
                        onChange={(e) => setSimpleTransferNewTime(e.target.value)}
                        style={{ fontSize: '1.05rem', padding: '0.85rem' }}
                      />
                    </FormGroup>
                  </div>
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                  borderRadius: '12px',
                  padding: '2rem',
                  border: '3px solid #28a745',
                  boxShadow: '0 4px 12px rgba(40, 167, 69, 0.2)'
                }}>
                  <div style={{
                    fontSize: '1.3rem',
                    fontWeight: '700',
                    color: '#155724',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    <span style={{
                      background: '#28a745',
                      color: 'white',
                      width: '2.5rem',
                      height: '2.5rem',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      fontWeight: '700'
                    }}>3</span>
                    <span>Confirm transfer</span>
                  </div>

                  <FormGroup>
                    <Label style={{ fontSize: '1.05rem', fontWeight: '700', color: '#155724', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FontAwesomeIcon icon={faEdit} />
                      Reason for transfer *
                    </Label>
                    <Input
                      as="textarea"
                      rows="3"
                      value={transferData.reason}
                      onChange={(e) => setTransferData({ ...transferData, reason: e.target.value })}
                      placeholder="E.g., Customer requested a different sitting…"
                      style={{
                        fontSize: '1rem',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '2px solid #dee2e6'
                      }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ fontSize: '1.05rem', fontWeight: '700', color: '#155724', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FontAwesomeIcon icon={faUser} />
                      Your name (admin) *
                    </Label>
                    <Input
                      type="text"
                      value={transferData.adminName}
                      onChange={(e) => setTransferData({ ...transferData, adminName: e.target.value })}
                      placeholder="For the audit trail"
                      style={{
                        fontSize: '1rem',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '2px solid #dee2e6'
                      }}
                    />
                  </FormGroup>

                  <div style={{
                    background: '#e7f3ff',
                    border: '2px solid #007bff',
                    borderRadius: '8px',
                    padding: '1.25rem',
                    marginTop: '1rem'
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      cursor: 'pointer',
                      fontSize: '1.05rem'
                    }}>
                      <input
                        type="checkbox"
                        checked={transferData.sendEmail}
                        onChange={(e) => setTransferData({ ...transferData, sendEmail: e.target.checked })}
                        style={{
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          marginTop: '2px',
                          accentColor: '#007bff'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '700', color: '#004085', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FontAwesomeIcon icon={faEnvelope} />
                          Send confirmation email to customer
                        </div>
                        <div style={{ fontSize: '0.95rem', color: '#004085', lineHeight: '1.5' }}>
                          {transferBooking?.customer_email ? (
                            <>Email will be sent to: <strong>{transferBooking.customer_email}</strong></>
                          ) : (
                            <span style={{ color: '#856404', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <FontAwesomeIcon icon={faExclamationTriangle} />
                              No email on file
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  </div>

                  <Button
                    variant="success"
                    onClick={handleConfirmSimpleTableTransfer}
                    disabled={
                      transferringToVenpos ||
                      emailSending ||
                      !transferData.reason?.trim() ||
                      !transferData.adminName?.trim() ||
                      !simpleTransferNewDate ||
                      !simpleTransferNewTime
                    }
                    style={{
                      width: '100%',
                      padding: '1.5rem',
                      fontSize: '1.25rem',
                      fontWeight: '700',
                      background: (transferringToVenpos || emailSending) ? '#6c757d' : 'linear-gradient(135deg, #28a745 0%, #218838 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                      transition: 'all 0.3s ease',
                      cursor: (transferringToVenpos || emailSending) ? 'not-allowed' : 'pointer',
                      marginTop: '1rem'
                    }}
                  >
                    {emailSending ? (
                      <>
                        <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '0.5rem' }} />
                        Sending email…
                      </>
                    ) : transferringToVenpos ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                        Transferring…
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '0.5rem' }} />
                        Confirm &amp; complete transfer
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {!isLocalTableBooking(transferBooking) && loadingIndividualBookings && (
              <LoadingMessage>
                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                Fetching individual booking references...
              </LoadingMessage>
            )}

            {/* Warning for multiple bookings */}
            {!isLocalTableBooking(transferBooking) && individualBookings.length > 1 && (
              <div style={{ 
                background: '#fff8e1',
                border: '2px solid #ffb74d',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem',
                borderLeft: '4px solid #ff9800'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '1.25rem', flexShrink: 0, color: '#ff9800', marginTop: '0.25rem' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontSize: '1rem', 
                      fontWeight: '700', 
                      color: '#e65100', 
                      marginBottom: '0.5rem'
                    }}>
                      Multiple Bookings in Transaction ({individualBookings.length} total)
                    </div>
                    <div style={{ fontSize: '0.95rem', color: '#5d4037', lineHeight: '1.5' }}>
                      You're transferring <strong>1 of {individualBookings.length}</strong> bookings. After this transfer, you may need to transfer the other {individualBookings.length - 1} to the same date.
                      {lastTransferredDate && (
                        <span style={{ display: 'block', marginTop: '0.5rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FontAwesomeIcon icon={faInfoCircle} />
                          Date picker will default to {new Date(lastTransferredDate).toLocaleDateString('en-GB')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isLocalTableBooking(transferBooking) && individualBookings.length > 0 && (
              <FormGroup>
                <Label>Select Booking to Transfer *</Label>
                <InfoBox style={{ background: '#e7f3ff', borderColor: '#007bff', padding: '1rem' }}>
                  <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                    This transaction contains {individualBookings.length} booking{individualBookings.length > 1 ? 's' : ''}:
                  </div>
                  {individualBookings.map((booking, index) => (
                    <div
                      key={booking.BookingReference}
                      style={{
                        padding: '0.75rem',
                        margin: '0.5rem 0',
                        background: selectedBookingRef === booking.BookingReference ? '#007bff' : 'white',
                        color: selectedBookingRef === booking.BookingReference ? 'white' : '#333',
                        border: `2px solid ${selectedBookingRef === booking.BookingReference ? '#007bff' : '#dee2e6'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleBookingSelection(booking.BookingReference)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                            {booking.BookingReference}
                            {individualBookings.length === 1 && ' (auto-selected)'}
                          </div>
                          <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                            Booked: {new Date(booking.BookedOn).toLocaleDateString()}
                            {booking.BookingStatusTypeId === 1 ? ' • Active' : ' • Status: ' + booking.BookingStatusTypeId}
                          </div>
                        </div>
                        {selectedBookingRef === booking.BookingReference && (
                          <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '1.25rem', color: 'white' }} />
                        )}
                      </div>
                    </div>
                  ))}
                </InfoBox>
              </FormGroup>
            )}

            {/* Step 2: Booking Details (Venpos path only) */}
            {!isLocalTableBooking(transferBooking) && (
            <div style={{
              marginBottom: '2.5rem',
              paddingBottom: '2rem',
              borderBottom: '2px solid #e9ecef'
            }}>
              <div style={{
                fontSize: '1.3rem',
                fontWeight: '700',
                color: '#192f6a',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <span style={{
                  background: '#192f6a',
                  color: 'white',
                  width: '2.5rem',
                  height: '2.5rem',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem',
                  fontWeight: '700'
                }}>2</span>
                <span>Review Booking Details</span>
              </div>

              {loadingVenposDetails && (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  color: '#666'
                }}>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', marginBottom: '1rem', color: '#192f6a' }} />
                  <div>Loading booking details from VenPos...</div>
                </div>
              )}

              {venposBookingDetails && (
                <div style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  padding: '1.5rem',
                  border: '2px solid #e9ecef',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                  <div style={{ marginBottom: '1.25rem', fontSize: '1.1rem', fontWeight: '700', color: '#192f6a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FontAwesomeIcon icon={faList} />
                    Booking Information
                  </div>
                
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {/* Event Information */}
                    {venposBookingDetails.event && (
                      <div style={{ 
                        padding: '1rem', 
                        background: '#f8f9fa', 
                        borderRadius: '8px', 
                        border: '1px solid #dee2e6' 
                      }}>
                        <div style={{ fontWeight: '700', fontSize: '1.05rem', marginBottom: '0.5rem', color: '#192f6a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FontAwesomeIcon icon={faCalendar} />
                          Event: {venposBookingDetails.event.description}
                        </div>
                        {venposBookingDetails.event.start && (
                          <div style={{ fontSize: '1rem', color: '#495057', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <FontAwesomeIcon icon={faClock} />
                            {new Date(venposBookingDetails.event.start).toLocaleString('en-GB', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Products */}
                    {venposBookingDetails.products && venposBookingDetails.products.length > 0 && (
                      <div style={{ 
                        padding: '1rem', 
                        background: '#f8f9fa', 
                        borderRadius: '8px', 
                        border: '1px solid #dee2e6' 
                      }}>
                        <div style={{ fontWeight: '700', fontSize: '1rem', marginBottom: '0.75rem', color: '#192f6a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FontAwesomeIcon icon={faUtensils} />
                          Products ({venposBookingDetails.products.length})
                        </div>
                        {venposBookingDetails.products.map((product, idx) => (
                          <div key={idx} style={{ 
                            fontSize: '1rem', 
                            color: '#495057', 
                            marginBottom: '0.5rem',
                            paddingLeft: '0.5rem',
                            borderLeft: '3px solid #007bff'
                          }}>
                            {product.description} <strong>(Qty: {product.quantity})</strong>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Lead Customer & Status */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      {venposBookingDetails.lead_customer_name && (
                        <div style={{ 
                          padding: '1rem', 
                          background: '#f8f9fa', 
                          borderRadius: '8px', 
                          border: '1px solid #dee2e6' 
                        }}>
                          <div style={{ fontWeight: '700', fontSize: '0.9rem', marginBottom: '0.5rem', color: '#192f6a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <FontAwesomeIcon icon={faUser} />
                            Customer
                          </div>
                          <div style={{ fontSize: '1.05rem', fontWeight: '600', color: '#495057' }}>
                            {venposBookingDetails.lead_customer_name}
                          </div>
                          {venposBookingDetails.postcode && (
                            <div style={{ fontSize: '0.9rem', color: '#6c757d', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '0.75rem' }} />
                              {venposBookingDetails.postcode}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Status & Payment */}
                      <div style={{ 
                        padding: '1rem', 
                        background: '#f8f9fa', 
                        borderRadius: '8px', 
                        border: '1px solid #dee2e6' 
                      }}>
                        <div style={{ fontWeight: '700', fontSize: '0.9rem', marginBottom: '0.75rem', color: '#192f6a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FontAwesomeIcon icon={faPoundSign} />
                          Status & Payment
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <div style={{ 
                            padding: '0.5rem 0.75rem', 
                            background: '#d4edda', 
                            borderRadius: '6px', 
                            fontSize: '0.95rem', 
                            fontWeight: '600', 
                            color: '#155724',
                            textAlign: 'center'
                          }}>
                            {venposBookingDetails.booking_status}
                          </div>
                          <div style={{ 
                            padding: '0.5rem 0.75rem', 
                            background: '#d1ecf1', 
                            borderRadius: '6px', 
                            fontSize: '0.95rem', 
                            fontWeight: '600', 
                            color: '#0c5460',
                            textAlign: 'center'
                          }}>
                            {venposBookingDetails.payment_status}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Step 3: Select New Date/Time */}
            {!isLocalTableBooking(transferBooking) && venposBookingDetails && !showAvailableEvents && (
              <div style={{
                marginBottom: '2.5rem',
                paddingBottom: '2rem',
                borderBottom: '2px solid #e9ecef'
              }}>
                <div style={{
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  color: '#192f6a',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <span style={{
                    background: '#192f6a',
                    color: 'white',
                    width: '2.5rem',
                    height: '2.5rem',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    fontWeight: '700'
                  }}>3</span>
                  <span>Choose New Date & Time</span>
                </div>

                <Button
                  variant="primary"
                  onClick={handleShowAvailableEvents}
                  style={{ 
                    width: '100%', 
                    padding: '1.5rem', 
                    fontSize: '1.2rem', 
                    fontWeight: '700',
                    background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 123, 255, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)';
                  }}
                >
                  <FontAwesomeIcon icon={faExchangeAlt} style={{ marginRight: '0.5rem' }} />
                  Show Available Events for Transfer
                </Button>
              </div>
            )}

            {/* Step 3: Select New Event */}
            {!isLocalTableBooking(transferBooking) && showAvailableEvents && (
              <div style={{
                marginBottom: '2.5rem',
                paddingBottom: '2rem',
                borderBottom: '2px solid #e9ecef'
              }}>
                <div style={{
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  color: '#192f6a',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <span style={{
                    background: '#192f6a',
                    color: 'white',
                    width: '2.5rem',
                    height: '2.5rem',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    fontWeight: '700'
                  }}>3</span>
                  <div>
                    <div>Select New Date & Time</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: '500', color: '#6c757d', marginTop: '0.25rem' }}>
                      {venposBookingDetails?.event?.description || 'Events'} events available
                    </div>
                  </div>
                </div>

                {loadingAvailableEvents && (
                  <LoadingMessage>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                    Loading available events from VenPos...
                  </LoadingMessage>
                )}

                {!loadingAvailableEvents && availableEvents.length > 0 && (
                    <>
                      {/* Date Picker - Larger and Clearer */}
                      <div style={{ 
                        background: 'white',
                        padding: '1.5rem',
                        borderRadius: '10px',
                        border: '2px solid #e9ecef',
                        marginBottom: '1.5rem'
                      }}>
                        <div style={{ 
                          fontSize: '1.05rem', 
                          fontWeight: '700', 
                          marginBottom: '1rem', 
                          color: '#192f6a',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <FontAwesomeIcon icon={faCalendar} />
                          <span>Filter by Date</span>
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'stretch' }}>
                          <div style={{ flex: 1 }}>
                            <Input
                              type="date"
                              value={eventsSearchDate}
                              onChange={(e) => handleDatePickerChange(e.target.value)}
                              min={new Date().toISOString().split('T')[0]}
                              style={{ 
                                fontSize: '1.1rem', 
                                padding: '1rem',
                                borderRadius: '8px',
                                border: '2px solid #dee2e6',
                                width: '100%'
                              }}
                            />
                          </div>
                          {eventsSearchDate && (
                            <Button
                              variant="secondary"
                              onClick={() => {
                                setEventsSearchDate('');
                                fetchAvailableEvents(1, false);
                              }}
                              style={{ 
                                fontSize: '1rem', 
                                padding: '1rem 1.5rem',
                                background: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                whiteSpace: 'nowrap',
                                fontWeight: '600'
                              }}
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                        {eventsSearchDate && (
                          <div style={{ 
                            fontSize: '0.95rem', 
                            color: '#0c5460', 
                            marginTop: '1rem', 
                            padding: '0.75rem', 
                            background: '#d1ecf1', 
                            borderRadius: '6px',
                            borderLeft: '3px solid #17a2b8'
                          }}>
                            <FontAwesomeIcon icon={faCalendar} style={{ marginRight: '0.5rem' }} />
                            Showing events from <strong>{new Date(eventsSearchDate).toLocaleDateString('en-GB', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}</strong> onwards
                          </div>
                        )}
                      </div>

                      {/* Events Count - Larger */}
                      <div style={{ 
                        marginBottom: '1rem', 
                        fontSize: '1.05rem', 
                        color: '#495057',
                        fontWeight: '600',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.75rem 1rem',
                        background: '#f8f9fa',
                        borderRadius: '8px'
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FontAwesomeIcon icon={faChartBar} />
                          Showing {availableEvents.length} of {eventsTotal} available events
                        </span>
                        {eventsCurrentPage < eventsTotalPages && (
                          <span style={{ 
                            color: '#007bff', 
                            fontSize: '0.95rem',
                            background: '#e7f3ff',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '4px'
                          }}>
                            Page {eventsCurrentPage}/{eventsTotalPages}
                          </span>
                        )}
                      </div>

                      {/* Events List - Larger Cards */}
                      <div style={{ 
                        maxHeight: '500px', 
                        overflowY: 'auto', 
                        border: '2px solid #e9ecef', 
                        borderRadius: '10px',
                        background: '#f8f9fa',
                        padding: '0.5rem'
                      }}>
                        {availableEvents.map((event, index) => {
                            const startDate = new Date(event.Start);
                            const endDate = new Date(event.End);
                            const capacity = event.Capacity - event.Usage - event.Reserved;
                            const isLowCapacity = capacity < 5 && capacity > 0;
                            const isFull = capacity <= 0;
                            const isSelected = selectedEvent && selectedEvent.Id === event.Id;

                            return (
                              <div
                                key={event.Id || index}
                                style={{
                                  padding: '1.5rem',
                                  marginBottom: '0.5rem',
                                  cursor: isFull ? 'not-allowed' : 'pointer',
                                  opacity: isFull ? 0.6 : 1,
                                  background: isSelected 
                                    ? 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)' 
                                    : 'white',
                                  color: isSelected ? 'white' : '#333',
                                  border: isSelected ? '3px solid #0056b3' : '2px solid #e9ecef',
                                  borderRadius: '10px',
                                  transition: 'all 0.3s ease',
                                  boxShadow: isSelected ? '0 4px 12px rgba(0, 123, 255, 0.3)' : '0 2px 4px rgba(0,0,0,0.05)',
                                  transform: isSelected ? 'scale(1.02)' : 'scale(1)'
                                }}
                                onClick={() => {
                                  if (!isFull) handleEventSelection(event);
                                }}
                                onMouseEnter={(e) => {
                                  if (!isFull && !isSelected) {
                                    e.currentTarget.style.background = '#f8f9fa';
                                    e.currentTarget.style.borderColor = '#007bff';
                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 123, 255, 0.2)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.background = 'white';
                                    e.currentTarget.style.borderColor = '#e9ecef';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                  }
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1.5rem' }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ 
                                      fontWeight: '700', 
                                      fontSize: '1.15rem', 
                                      color: isSelected ? 'white' : '#192f6a', 
                                      marginBottom: '0.75rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem'
                                    }}>
                                      {isSelected && <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '1.25rem' }} />}
                                      {event.Description || event.Code}
                                    </div>
                                    <div style={{ 
                                      fontSize: '1.05rem', 
                                      color: isSelected ? 'rgba(255,255,255,0.95)' : '#495057',
                                      marginBottom: '0.5rem',
                                      fontWeight: '600',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem'
                                    }}>
                                      <FontAwesomeIcon icon={faCalendar} />
                                      {startDate.toLocaleDateString('en-GB', { 
                                        weekday: 'long', 
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric' 
                                      })}
                                    </div>
                                    <div style={{ 
                                      fontSize: '1rem', 
                                      color: isSelected ? 'rgba(255,255,255,0.9)' : '#6c757d',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.75rem'
                                    }}>
                                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <FontAwesomeIcon icon={faClock} />
                                        {startDate.toLocaleTimeString('en-GB', { 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                      })} - {endDate.toLocaleTimeString('en-GB', { 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                      })}
                                      </span>
                                      <span style={{ opacity: 0.7 }}>• {event.Duration} mins</span>
                                    </div>
                                  </div>
                                  <div style={{ 
                                    textAlign: 'center',
                                    minWidth: '120px'
                                  }}>
                                    {isFull ? (
                                      <div style={{ 
                                        padding: '0.75rem 1rem', 
                                        background: '#dc3545', 
                                        color: 'white', 
                                        borderRadius: '8px', 
                                        fontSize: '1rem',
                                        fontWeight: '700',
                                        marginBottom: '0.5rem'
                                      }}>
                                        FULLY BOOKED
                                      </div>
                                    ) : (
                                      <>
                                        <div style={{ 
                                          padding: '0.75rem 1rem', 
                                          background: isSelected ? 'rgba(255,255,255,0.2)' : (isLowCapacity ? '#ffc107' : '#28a745'), 
                                          color: isSelected ? 'white' : (isLowCapacity ? '#000' : 'white'), 
                                          borderRadius: '8px', 
                                          fontSize: '1.1rem',
                                          fontWeight: '700',
                                          marginBottom: '0.5rem'
                                        }}>
                                          {capacity} spaces
                                        </div>
                                        <div style={{ 
                                          fontSize: '0.85rem', 
                                          color: isSelected ? 'rgba(255,255,255,0.8)' : '#6c757d'
                                        }}>
                                          of {event.Capacity} total
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                                {isSelected && (
                                  <div style={{
                                    marginTop: '1rem',
                                    padding: '0.75rem',
                                    background: 'rgba(255,255,255,0.15)',
                                    borderRadius: '6px',
                                    fontSize: '0.95rem',
                                    textAlign: 'center',
                                    fontWeight: '600'
                                  }}>
                                    <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '0.5rem' }} />
                                    This event is selected for transfer
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>

                      {/* Load More Button */}
                      {eventsCurrentPage < eventsTotalPages && (
                        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                          <Button
                            variant="primary"
                            onClick={handleLoadMoreEvents}
                            disabled={loadingAvailableEvents}
                            style={{ 
                              padding: '1rem 2.5rem',
                              fontSize: '1.05rem',
                              fontWeight: '600',
                              borderRadius: '8px',
                              background: '#007bff',
                              border: 'none',
                              boxShadow: '0 2px 8px rgba(0, 123, 255, 0.3)'
                            }}
                          >
                            {loadingAvailableEvents ? (
                              <>
                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                                Loading...
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faDownload} style={{ marginRight: '0.5rem' }} />
                                Load More Events ({eventsTotal - availableEvents.length} remaining)
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </>
                )}

                {!loadingAvailableEvents && availableEvents.length === 0 && (
                  <div style={{ 
                    padding: '3rem', 
                    textAlign: 'center', 
                    background: 'white',
                    borderRadius: '10px',
                    border: '2px dashed #dee2e6'
                  }}>
                    <FontAwesomeIcon icon={faCalendar} style={{ fontSize: '3rem', marginBottom: '1rem', color: '#6c757d' }} />
                    <div style={{ fontSize: '1.1rem', color: '#6c757d', fontWeight: '600' }}>
                      {eventsSearchDate 
                        ? `No events found from ${new Date(eventsSearchDate).toLocaleDateString('en-GB')} onwards` 
                        : 'No available events found'}
                    </div>
                    <div style={{ fontSize: '0.95rem', color: '#999', marginTop: '0.5rem' }}>
                      Try selecting a different date or clearing filters
                    </div>
                  </div>
                )}

                {/* Step 4: Confirm Transfer */}
                {selectedEvent && (
                  <div style={{
                    marginTop: '2rem',
                    background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                    borderRadius: '12px',
                    padding: '2rem',
                    border: '3px solid #28a745',
                    boxShadow: '0 4px 12px rgba(40, 167, 69, 0.2)'
                  }}>
                    <div style={{
                      fontSize: '1.3rem',
                      fontWeight: '700',
                      color: '#155724',
                      marginBottom: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}>
                      <span style={{
                        background: '#28a745',
                        color: 'white',
                        width: '2.5rem',
                        height: '2.5rem',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.2rem',
                        fontWeight: '700'
                      }}>4</span>
                      <span>Confirm Transfer Details</span>
                    </div>
                    
                    {/* Selected Event Summary */}
                    <div style={{ 
                      background: 'white', 
                      padding: '1.5rem', 
                      borderRadius: '10px', 
                      marginBottom: '1.5rem',
                      border: '2px solid #28a745'
                    }}>
                      <div style={{ 
                        fontSize: '0.85rem', 
                        fontWeight: '700', 
                        color: '#28a745',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '1rem'
                      }}>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '0.5rem' }} />
                        Selected Event
                      </div>
                      <div style={{ fontWeight: '700', fontSize: '1.2rem', color: '#192f6a', marginBottom: '1rem' }}>
                        {selectedEvent.Description || selectedEvent.Code}
                      </div>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr', 
                        gap: '1rem',
                        fontSize: '1rem',
                        color: '#495057'
                      }}>
                        <div>
                          <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '0.25rem' }}>Date</div>
                          <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <FontAwesomeIcon icon={faCalendar} />
                            {new Date(selectedEvent.Start).toLocaleDateString('en-GB', { 
                              weekday: 'short', 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '0.25rem' }}>Time</div>
                          <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <FontAwesomeIcon icon={faClock} />
                            {new Date(selectedEvent.Start).toLocaleTimeString('en-GB', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })} - {new Date(selectedEvent.End).toLocaleTimeString('en-GB', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                      <div style={{ 
                        marginTop: '1rem',
                        padding: '0.75rem',
                        background: '#f8f9fa',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        color: '#495057'
                      }}>
                        <strong>{selectedEvent.Capacity - selectedEvent.Usage - selectedEvent.Reserved}</strong> spaces available
                      </div>
                    </div>

                    {/* Transfer Form */}
                    <FormGroup>
                      <Label style={{ fontSize: '1.05rem', fontWeight: '700', color: '#155724', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FontAwesomeIcon icon={faEdit} />
                        Reason for Transfer *
                      </Label>
                      <Input
                        as="textarea"
                        rows="3"
                        value={transferData.reason}
                        onChange={(e) => setTransferData({ ...transferData, reason: e.target.value })}
                        placeholder="E.g., Customer requested date change, venue conflict, etc."
                        required
                        style={{
                          fontSize: '1rem',
                          padding: '1rem',
                          borderRadius: '8px',
                          border: '2px solid #dee2e6'
                        }}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label style={{ fontSize: '1.05rem', fontWeight: '700', color: '#155724', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FontAwesomeIcon icon={faUser} />
                        Your Name (Admin) *
                      </Label>
                      <Input
                        type="text"
                        value={transferData.adminName}
                        onChange={(e) => setTransferData({ ...transferData, adminName: e.target.value })}
                        placeholder="Enter your name for audit trail"
                        required
                        style={{
                          fontSize: '1rem',
                          padding: '1rem',
                          borderRadius: '8px',
                          border: '2px solid #dee2e6'
                        }}
                      />
                    </FormGroup>

                    {/* Email Confirmation Checkbox */}
                    <div style={{
                      background: '#e7f3ff',
                      border: '2px solid #007bff',
                      borderRadius: '8px',
                      padding: '1.25rem',
                      marginTop: '1rem'
                    }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '1rem',
                        cursor: 'pointer',
                        fontSize: '1.05rem'
                      }}>
                        <input
                          type="checkbox"
                          checked={transferData.sendEmail}
                          onChange={(e) => setTransferData({ ...transferData, sendEmail: e.target.checked })}
                          style={{
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            marginTop: '2px',
                            accentColor: '#007bff'
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '700', color: '#004085', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <FontAwesomeIcon icon={faEnvelope} />
                            Send Confirmation Email to Customer
                          </div>
                          <div style={{ fontSize: '0.95rem', color: '#004085', lineHeight: '1.5' }}>
                            {transferBooking?.customer_email ? (
                              <>
                                Email will be sent to: <strong>{transferBooking.customer_email}</strong>
                                <br />
                                <span style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                                  Includes new booking details and QR code with {individualBookings.length > 1 ? 'booking reference' : 'transaction reference'}
                                </span>
                              </>
                            ) : (
                              <span style={{ color: '#856404', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                No email address on file for this customer
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    </div>

                    {/* Confirm Button */}
                    <Button
                      variant="success"
                      onClick={handleConfirmTransfer}
                      disabled={transferringToVenpos || emailSending || !transferData.reason || !transferData.adminName}
                      style={{ 
                        width: '100%', 
                        padding: '1.5rem', 
                        fontSize: '1.25rem', 
                        fontWeight: '700',
                        background: (transferringToVenpos || emailSending) ? '#6c757d' : 'linear-gradient(135deg, #28a745 0%, #218838 100%)',
                        border: 'none',
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                        transition: 'all 0.3s ease',
                        cursor: (transferringToVenpos || emailSending) ? 'not-allowed' : 'pointer',
                        marginTop: '1rem'
                      }}
                      onMouseEnter={(e) => {
                        if (!transferringToVenpos && !emailSending) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(40, 167, 69, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.3)';
                      }}
                    >
                      {emailSending ? (
                        <>
                          <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '0.5rem' }} />
                          Sending Email...
                        </>
                      ) : transferringToVenpos ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                          Transferring Booking...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '0.5rem' }} />
                          Confirm & Complete Transfer
                        </>
                      )}
                    </Button>

                    {/* Change Selection Button */}
                    <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedEvent(null);
                          setTransferData({ ...transferData, reason: '', adminName: '' });
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#155724',
                          fontSize: '1rem',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontWeight: '600',
                          padding: '0.5rem'
                        }}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} style={{ marginRight: '0.5rem' }} />
                        Choose Different Event
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {transferSuccessMessage && (
              <div style={{ 
                background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                border: '2px solid #28a745',
                borderRadius: '10px',
                padding: '1.5rem',
                marginBottom: '2rem',
                boxShadow: '0 4px 12px rgba(40, 167, 69, 0.2)'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '2rem', lineHeight: '1', color: '#28a745', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#155724', marginBottom: '0.5rem' }}>
                      Transfer Successful!
                    </div>
                    <div style={{ fontSize: '1rem', color: '#155724', lineHeight: '1.6' }}>
                      {transferSuccessMessage}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {transferError && (
              <ErrorBox>
                <strong>Error:</strong> {transferError}
              </ErrorBox>
            )}


          </ModalContent>
        </Modal>
      )}

      {/* Edit Booking Modal */}
      {editBooking && (
        <Modal onClick={handleCloseEditModal}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <ModalHeader>
              <ModalTitle>
                <FontAwesomeIcon icon={faEdit} style={{ marginRight: '0.5rem' }} />
                Edit Booking – {editBooking.booking_reference}
              </ModalTitle>
              <ModalClose onClick={handleCloseEditModal}>
                <FontAwesomeIcon icon={faTimes} />
              </ModalClose>
            </ModalHeader>
            <div style={{ marginBottom: '1rem' }}>
              <strong>{editBooking.customer_name}</strong>
              {editBooking.customer_email && <span style={{ color: '#666', marginLeft: '0.5rem' }}>{editBooking.customer_email}</span>}
            </div>
            {editError && (
              <ErrorBox style={{ marginBottom: '1rem' }}>{editError}</ErrorBox>
            )}
            <FormGroup>
              <Label>Visit Date</Label>
              <Input
                type="date"
                value={editForm.visit_date}
                onChange={(e) => setEditForm((f) => ({ ...f, visit_date: e.target.value }))}
              />
            </FormGroup>
            <FormGroup>
              <Label>Visit Time</Label>
              <Input
                type="time"
                value={editForm.visit_time}
                onChange={(e) => setEditForm((f) => ({ ...f, visit_time: e.target.value }))}
              />
            </FormGroup>
            {isLocalTableBooking(editBooking) && (
              <FormGroup>
                <Label>Number of guests</Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={editForm.number_of_guests}
                  onChange={(e) => setEditForm((f) => ({
                    ...f,
                    number_of_guests: Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1))
                  }))}
                />
              </FormGroup>
            )}
            <FormGroup>
              <Label>Tearoom special requests</Label>
              <SmallText style={{ marginTop: '-0.35rem', marginBottom: '0.5rem', fontSize: '0.82rem', color: '#666' }}>
                For phone-in requests (gift bags on table, celebrations, etc.). Shown on the daily print sheet, calendar day view, and floor plan.
              </SmallText>
              <TextArea
                value={editForm.tearoom_staff_notes}
                onChange={(e) => setEditForm((f) => ({ ...f, tearoom_staff_notes: e.target.value }))}
                placeholder="e.g. Gift bags on table for Father’s Day surprise"
                rows={3}
              />
            </FormGroup>
            <FormGroup>
              <Label>Admin notes</Label>
              <SmallText style={{ marginTop: '-0.35rem', marginBottom: '0.5rem', fontSize: '0.82rem', color: '#666' }}>
                Internal use (manual booking text, transfers, etc.). Not highlighted for tearoom staff on the daily sheet.
              </SmallText>
              <TextArea
                value={editForm.admin_notes}
                onChange={(e) => setEditForm((f) => ({ ...f, admin_notes: e.target.value }))}
                placeholder="Internal notes"
                rows={3}
              />
            </FormGroup>
            {(editBooking.products || []).length > 0 && (
              <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #dee2e6' }}>
                <div style={{ fontWeight: '600', color: '#192f6a', marginBottom: '1rem' }}>Dietary &amp; allergen notes per product</div>
                {(editBooking.products || []).map((product) => (
                  <div key={product.id} style={{ marginBottom: '1.25rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#333' }}>{product.product_name}</div>
                    <FormGroup>
                      <Label style={{ fontSize: '0.85rem' }}>Dietary requirement</Label>
                      <Input
                        value={editForm.products[product.id]?.dietary ?? ''}
                        onChange={(e) => setEditForm((f) => ({
                          ...f,
                          products: { ...f.products, [product.id]: { ...f.products[product.id], dietary: e.target.value } }
                        }))}
                        placeholder="e.g. Gluten free, Standard"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label style={{ fontSize: '0.85rem' }}>Allergen / special notes</Label>
                      <TextArea
                        value={editForm.products[product.id]?.allergen_notes ?? ''}
                        onChange={(e) => setEditForm((f) => ({
                          ...f,
                          products: { ...f.products, [product.id]: { ...f.products[product.id], allergen_notes: e.target.value } }
                        }))}
                        placeholder="Allergen information or special requests"
                        rows={2}
                        style={{ minHeight: '60px' }}
                      />
                    </FormGroup>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
              <div>
                {isLocalTableBooking(editBooking) && (
                  <Button
                    type="button"
                    onClick={handleDeleteTableBooking}
                    disabled={editSaving || editDeleting}
                    style={{ background: '#dc3545', color: '#fff', borderColor: '#dc3545' }}
                  >
                    {editDeleting ? (
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                    ) : (
                      <FontAwesomeIcon icon={faTrash} style={{ marginRight: '0.5rem' }} />
                    )}
                    {editDeleting ? 'Deleting…' : 'Delete booking'}
                  </Button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <Button onClick={handleCloseEditModal} disabled={editSaving || editDeleting}>Cancel</Button>
                <Button variant="primary" onClick={handleSaveEdit} disabled={editSaving || editDeleting}>
                  {editSaving ? <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} /> : null}
                  Save changes
                </Button>
              </div>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Audit Trail Modal */}
      {showAuditTrail && auditTrailBooking && (
        <Modal onClick={handleCloseAuditTrail}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <FontAwesomeIcon icon={faFile} style={{ marginRight: '0.5rem' }} />
                Booking Audit Trail
              </ModalTitle>
              <ModalClose onClick={handleCloseAuditTrail}>
                <FontAwesomeIcon icon={faTimes} />
              </ModalClose>
            </ModalHeader>

            <InfoBox>
              <div><strong>Booking Reference:</strong> {auditTrailBooking.booking_reference}</div>
              <div><strong>Customer:</strong> {auditTrailBooking.customer_name}</div>
              <div><strong>Current Date:</strong> {formatDate(auditTrailBooking.visit_date)} {formatTime(auditTrailBooking.visit_time)}</div>
            </InfoBox>

            {auditTrailData.length === 0 ? (
              <EmptyState style={{ padding: '2rem' }}>
                <p>No audit trail records found for this booking.</p>
              </EmptyState>
            ) : (
              <AuditTrailList>
                {auditTrailData.map((audit, index) => (
                  <AuditTrailItem key={index}>
                    <AuditDate>
                      <strong>{formatDateTime(audit.changed_at)}</strong> by {audit.admin_name}
                    </AuditDate>
                    <AuditChange>
                      <strong>From:</strong> {formatDate(audit.old_date)} {formatTime(audit.old_time)}
                    </AuditChange>
                    <AuditChange>
                      <strong>To:</strong> {formatDate(audit.new_date)} {formatTime(audit.new_time)}
                    </AuditChange>
                    {audit.reason && (
                      <AuditReason>
                        <strong>Reason:</strong> {audit.reason}
                      </AuditReason>
                    )}
                  </AuditTrailItem>
                ))}
              </AuditTrailList>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <Button onClick={handleCloseAuditTrail}>Close</Button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Manual Add Booking Modal */}
      {showManualAddModal && (
        <Modal onClick={handleCloseManualAddModal}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ 
              background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)',
              padding: '2rem',
              borderRadius: '12px 12px 0 0',
              marginTop: '-1.5rem',
              marginLeft: '-1.5rem',
              marginRight: '-1.5rem',
              marginBottom: '1.5rem',
              color: 'white'
            }}>
              <h2 style={{ 
                margin: 0, 
                fontSize: '2rem', 
                fontWeight: '700',
                textAlign: 'center',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                <FontAwesomeIcon icon={faPlus} style={{ marginRight: '1rem' }} />
                Manual Add Booking
              </h2>
              <p style={{ 
                margin: '0.5rem 0 0 0', 
                fontSize: '1rem', 
                textAlign: 'center',
                opacity: 0.95
              }}>
                Create a new afternoon tea booking manually
              </p>
              {selectedFloorPlanTable && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '6px',
                  textAlign: 'center',
                  fontSize: '0.95rem',
                  fontWeight: '600'
                }}>
                  <FontAwesomeIcon icon={faTableCells} style={{ marginRight: '0.5rem' }} />
                  Table: {selectedFloorPlanTable.name} (Seats {selectedFloorPlanTable.capacity})
                </div>
              )}
            </div>

            {manualBookingSuccess ? (
              <div style={{
                padding: '2rem',
                background: '#d4edda',
                border: '2px solid #28a745',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                whiteSpace: 'pre-line',
                textAlign: 'center'
              }}>
                <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '3rem', color: '#28a745', marginBottom: '1rem' }} />
                <div style={{ fontSize: '1.1rem', color: '#155724', fontWeight: '600' }}>
                  {manualBookingSuccess}
                </div>
                <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
                  Closing in 3 seconds...
                </div>
              </div>
            ) : (
              <>
                {/* Booking Progress Indicator */}
                {bookingProgress.message && (
                  <div style={{
                    padding: '1.25rem',
                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                    border: '2px solid #2196f3',
                    borderRadius: '12px',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.2)'
                  }}>
                    <FontAwesomeIcon 
                      icon={faSpinner} 
                      spin 
                      style={{ 
                        fontSize: '1.5rem', 
                        color: '#1976d2'
                      }} 
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: '700', 
                        color: '#0d47a1',
                        fontSize: '1.1rem',
                        marginBottom: '0.25rem'
                      }}>
                        {bookingProgress.step === 'reserving' && 'Step 1 of 2: Reserving Tickets'}
                        {bookingProgress.step === 'saving' && 'Step 2 of 2: Saving Booking'}
                      </div>
                      <div style={{ 
                        color: '#1565c0',
                        fontSize: '0.95rem'
                      }}>
                        {bookingProgress.message}
                      </div>
                    </div>
                  </div>
                )}

                {manualBookingError && (
                  <div style={{
                    padding: '1rem',
                    background: '#f8d7da',
                    border: '2px solid #dc3545',
                    borderRadius: '8px',
                    marginBottom: '1.5rem',
                    color: '#721c24'
                  }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem' }} />
                    {manualBookingError}
                  </div>
                )}

                {/* Event Selection Section */}
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ 
                    display: 'block',
                    fontWeight: '600', 
                    color: '#333',
                    fontSize: '1.1rem',
                    marginBottom: '0.5rem'
                  }}>
                    Select Event *
                  </label>
                  {isAddingAnotherBooking && lastManualBookingDate && (
                    <div style={{
                      fontSize: '0.9rem',
                      color: '#666',
                      marginBottom: '0.75rem',
                      padding: '0.5rem',
                      background: '#e7f3ff',
                      borderRadius: '6px',
                      border: '1px solid #b3d9ff'
                    }}>
                      ℹ️ Showing events on {new Date(lastManualBookingDate + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} (same as afternoon tea booking)
                    </div>
                  )}
                  
                  <div style={{ 
                    display: 'flex', 
                    gap: '0.75rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ flex: 1 }}>
                      <input
                        type="text"
                        value={eventSearchTerm}
                        onChange={(e) => setEventSearchTerm(e.target.value)}
                        placeholder="Search events..."
                        disabled={creatingManualBooking}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #ddd',
                          borderRadius: '8px',
                          fontSize: '0.95rem'
                        }}
                      />
                    </div>
                    <input
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      value={manualBookingDateFilter}
                      onChange={(e) => {
                        setManualBookingDateFilter(e.target.value);
                        if (e.target.value) {
                          // If adding another (entry ticket), load all events; otherwise afternoon tea only
                          fetchManualBookingEvents(e.target.value, isAddingAnotherBooking ? '' : 'afternoon tea');
                        }
                      }}
                      disabled={creatingManualBooking}
                      style={{
                        padding: '0.75rem',
                        border: '2px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        minWidth: '160px'
                      }}
                      title="Filter by date"
                    />
                  </div>

                  {loadingManualEvents ? (
                    <div style={{
                      padding: '3rem',
                      textAlign: 'center',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      border: '2px dashed #dee2e6'
                    }}>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#6c757d', marginBottom: '1rem' }} />
                      <div style={{ color: '#6c757d', fontSize: '1rem' }}>Loading available events...</div>
                    </div>
                  ) : manualBookingEvents.length === 0 ? (
                    <div style={{
                      padding: '3rem',
                      textAlign: 'center',
                      background: '#fff3cd',
                      borderRadius: '8px',
                      border: '2px solid #ffc107'
                    }}>
                      <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '2rem', color: '#856404', marginBottom: '1rem' }} />
                      <div style={{ color: '#856404', fontSize: '1rem', fontWeight: '600' }}>No events found</div>
                      <div style={{ color: '#856404', fontSize: '0.9rem', marginTop: '0.5rem' }}>Try selecting a different date</div>
                    </div>
                  ) : (() => {
                    const filteredEvents = manualBookingEvents.filter(event => {
                      const matchesSearch = eventSearchTerm === '' || 
                        event.Description.toLowerCase().includes(eventSearchTerm.toLowerCase());
                      // If adding another booking (entry ticket), show all events; otherwise filter by afternoon tea
                      if (isAddingAnotherBooking) {
                        return matchesSearch;
                      }
                      const isAfternoonTea = event.Description.toLowerCase().includes('afternoon tea') || 
                                            event.Description.toLowerCase().includes('tea');
                      return matchesSearch && isAfternoonTea;
                    });

                    if (filteredEvents.length === 0) {
                      return (
                        <div style={{
                          padding: '3rem',
                          textAlign: 'center',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          border: '2px dashed #dee2e6'
                        }}>
                          <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '2rem', color: '#6c757d', marginBottom: '1rem' }} />
                          <div style={{ color: '#6c757d', fontSize: '1rem', fontWeight: '600' }}>No events match your search</div>
                          <div style={{ color: '#6c757d', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                            {eventSearchTerm ? `Try clearing the search filter "${eventSearchTerm}"` : 'Try selecting a different date'}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div style={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        padding: '0.5rem'
                      }}>
                        {filteredEvents.map(event => {
                          const isSelected = manualBookingForm.event_id === event.Id;
                          const spacesLeft = event.Capacity - event.Usage;
                          const percentFull = (event.Usage / event.Capacity) * 100;
                          const eventDate = new Date(event.Start);
                          
                          return (
                            <div
                              key={event.Id}
                              onClick={async () => {
                                if (!creatingManualBooking && !loadingEventTickets) {
                                  setManualBookingForm({
                                    ...manualBookingForm,
                                    event_id: event.Id,
                                    event_date: event.Start
                                  });
                                  // Load tickets for this event
                                  await fetchEventTickets(event.Id);
                                }
                              }}
                              style={{
                                padding: '1rem',
                                marginBottom: '0.5rem',
                                border: isSelected ? '3px solid #28a745' : '2px solid #dee2e6',
                                borderRadius: '8px',
                                background: isSelected ? '#d4edda' : 'white',
                                cursor: creatingManualBooking ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s ease',
                                opacity: creatingManualBooking ? 0.6 : 1
                              }}
                              onMouseEnter={(e) => {
                                if (!creatingManualBooking && !isSelected) {
                                  e.currentTarget.style.background = '#f8f9fa';
                                  e.currentTarget.style.borderColor = '#adb5bd';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.background = 'white';
                                  e.currentTarget.style.borderColor = '#dee2e6';
                                }
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ 
                                    fontSize: '1.1rem', 
                                    fontWeight: '700', 
                                    color: isSelected ? '#155724' : '#192f6a',
                                    marginBottom: '0.5rem'
                                  }}>
                                    {event.Description}
                                  </div>
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '0.5rem',
                                    color: '#6c757d',
                                    fontSize: '0.95rem',
                                    marginBottom: '0.5rem'
                                  }}>
                                    <FontAwesomeIcon icon={faCalendar} />
                                    <span>{eventDate.toLocaleDateString('en-GB', {
                                      weekday: 'short',
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric'
                                    })}</span>
                                  </div>
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '0.5rem',
                                    color: '#6c757d',
                                    fontSize: '0.95rem'
                                  }}>
                                    <FontAwesomeIcon icon={faClock} />
                                    <span>{eventDate.toLocaleTimeString('en-GB', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}</span>
                                  </div>
                                </div>
                                
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{
                                    padding: '0.5rem 1rem',
                                    background: spacesLeft > event.Capacity * 0.5 ? '#28a745' : 
                                               spacesLeft > event.Capacity * 0.2 ? '#ffc107' : '#dc3545',
                                    color: 'white',
                                    borderRadius: '20px',
                                    fontSize: '0.9rem',
                                    fontWeight: '700',
                                    marginBottom: '0.5rem',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    <FontAwesomeIcon icon={faUsers} style={{ marginRight: '0.25rem' }} />
                                    {spacesLeft} spaces
                                  </div>
                                  <div style={{ 
                                    fontSize: '0.8rem', 
                                    color: '#6c757d',
                                    textAlign: 'center'
                                  }}>
                                    {event.Usage}/{event.Capacity}
                                  </div>
                                  <div style={{
                                    width: '80px',
                                    height: '4px',
                                    background: '#e9ecef',
                                    borderRadius: '2px',
                                    overflow: 'hidden',
                                    marginTop: '0.25rem'
                                  }}>
                                    <div style={{
                                      width: `${percentFull}%`,
                                      height: '100%',
                                      background: percentFull < 50 ? '#28a745' : 
                                                 percentFull < 80 ? '#ffc107' : '#dc3545',
                                      transition: 'width 0.3s ease'
                                    }} />
                                  </div>
                                </div>
                              </div>
                              
                              {isSelected && (
                                <div style={{
                                  marginTop: '0.75rem',
                                  paddingTop: '0.75rem',
                                  borderTop: '2px solid #28a745',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  color: '#155724',
                                  fontWeight: '600'
                                }}>
                                  <FontAwesomeIcon icon={faCheckCircle} />
                                  Selected for booking
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Ticket Selection */}
                {manualBookingForm.event_id && (() => {
                  // Find the selected event to get capacity info
                  const selectedEvent = manualBookingEvents.find(e => e.Id === manualBookingForm.event_id);
                  const availableCapacity = selectedEvent ? (selectedEvent.Capacity - selectedEvent.Usage) : 0;
                  const totalSelectedTickets = Object.values(ticketQuantities).reduce((sum, qty) => sum + (qty || 0), 0);
                  const capacityReached = totalSelectedTickets >= availableCapacity;

                  return (
                  <div style={{ 
                    marginBottom: '1.5rem',
                    padding: '1.25rem',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h4 style={{ 
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        color: '#495057',
                        margin: 0
                      }}>
                        Select Tickets
                      </h4>
                      {selectedEvent && (
                        <div style={{
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          color: capacityReached ? '#dc3545' : totalSelectedTickets > 0 ? '#28a745' : '#6c757d',
                          padding: '0.35rem 0.75rem',
                          background: capacityReached ? '#f8d7da' : totalSelectedTickets > 0 ? '#d4edda' : 'white',
                          borderRadius: '20px',
                          border: `1px solid ${capacityReached ? '#dc3545' : totalSelectedTickets > 0 ? '#28a745' : '#dee2e6'}`
                        }}>
                          {totalSelectedTickets}/{availableCapacity} spaces
                        </div>
                      )}
                    </div>
                    
                    {capacityReached && (
                      <div style={{
                        padding: '0.75rem',
                        marginBottom: '1rem',
                        background: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '6px',
                        color: '#856404',
                        fontSize: '0.9rem'
                      }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem' }} />
                        <strong>Capacity reached:</strong> This event has {availableCapacity} space{availableCapacity !== 1 ? 's' : ''} available.
                      </div>
                    )}
                    
                    {loadingEventTickets ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
                        <div>Loading ticket options...</div>
                      </div>
                    ) : selectedEventTickets.length === 0 ? (
                      <div style={{ 
                        padding: '1.5rem',
                        textAlign: 'center',
                        color: '#6c757d',
                        background: 'white',
                        borderRadius: '6px',
                        border: '1px solid #dee2e6'
                      }}>
                        No tickets available for this event
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {selectedEventTickets.map(ticket => {
                          const currentQty = ticketQuantities[ticket.Id] || 0;
                          const canAddMore = totalSelectedTickets < availableCapacity;
                          
                          return (
                          <div
                            key={ticket.Id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '1rem',
                              background: 'white',
                              borderRadius: '6px',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', color: '#495057', marginBottom: '0.25rem' }}>
                                {ticket.Text || ticket.ProductDescription}
                              </div>
                              <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                                £{parseFloat(ticket.Price).toFixed(2)}
                              </div>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  const newQty = Math.max(0, currentQty - 1);
                                  setTicketQuantities({
                                    ...ticketQuantities,
                                    [ticket.Id]: newQty
                                  });
                                }}
                                disabled={currentQty === 0}
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  border: '1px solid #dee2e6',
                                  background: 'white',
                                  borderRadius: '4px',
                                  cursor: currentQty === 0 ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '1.2rem',
                                  opacity: currentQty === 0 ? 0.5 : 1
                                }}
                              >
                                -
                              </button>
                              
                              <div style={{
                                width: '50px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                background: 'white',
                                fontWeight: '600'
                              }}>
                                {currentQty}
                              </div>
                              
                              <button
                                type="button"
                                onClick={() => {
                                  if (canAddMore) {
                                    const newQty = currentQty + 1;
                                    setTicketQuantities({
                                      ...ticketQuantities,
                                      [ticket.Id]: newQty
                                    });
                                  }
                                }}
                                disabled={!canAddMore}
                                title={!canAddMore ? `Event capacity reached (${availableCapacity} spaces available)` : 'Add ticket'}
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  border: `1px solid ${canAddMore ? '#28a745' : '#dee2e6'}`,
                                  background: canAddMore ? '#28a745' : '#e9ecef',
                                  borderRadius: '4px',
                                  cursor: canAddMore ? 'pointer' : 'not-allowed',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '1.2rem',
                                  color: canAddMore ? 'white' : '#6c757d',
                                  opacity: canAddMore ? 1 : 0.6
                                }}
                              >
                                +
                              </button>
                            </div>
                          </div>
                          );
                        })}
                        
                        {/* Total Summary */}
                        {(() => {
                          const totalGuests = Object.values(ticketQuantities).reduce((sum, qty) => sum + qty, 0);
                          const totalPrice = selectedEventTickets.reduce((sum, ticket) => {
                            const qty = ticketQuantities[ticket.Id] || 0;
                            return sum + (qty * parseFloat(ticket.Price));
                          }, 0);
                          
                          if (totalGuests > 0) {
                            return (
                              <div style={{
                                marginTop: '0.75rem',
                                padding: '1rem',
                                background: '#e7f5ed',
                                border: '2px solid #28a745',
                                borderRadius: '6px'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <div style={{ fontWeight: '600', color: '#155724' }}>
                                    Total: {totalGuests} guest{totalGuests !== 1 ? 's' : ''}
                                  </div>
                                  <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#155724' }}>
                                    £{totalPrice.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                  </div>
                  );
                })()}

                {/* Guest Modifiers & Allergies (only for afternoon tea, not entry tickets) */}
                {!isAddingAnotherBooking && manualBookingForm.event_id && (() => {
                  const totalGuests = Object.values(ticketQuantities).reduce((sum, qty) => sum + qty, 0);
                  if (totalGuests === 0) return null;

                  return (
                    <div style={{ 
                      marginBottom: '1.5rem',
                      padding: '1.25rem',
                      background: '#f0f8ff',
                      borderRadius: '8px',
                      border: '1px solid #b3d9ff'
                    }}>
                      <h4 style={{ 
                        marginBottom: '1rem',
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        color: '#495057'
                      }}>
                        Guest Details (Modifiers & Allergies)
                      </h4>
                      <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                        Please specify dietary requirements for each guest
                      </div>
                      
                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {Array.from({ length: totalGuests }).map((_, guestIndex) => (
                          <div key={guestIndex} style={{
                            padding: '1rem',
                            background: 'white',
                            borderRadius: '6px',
                            border: '1px solid #dee2e6'
                          }}>
                            <div style={{ fontWeight: '600', color: '#495057', marginBottom: '0.75rem' }}>
                              Guest {guestIndex + 1}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem' }}>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.35rem', color: '#666' }}>
                                  Dietary Requirements
                                </label>
                                <select
                                  value={guestModifiers[guestIndex]?.modifier || 'Standard'}
                                  onChange={(e) => setGuestModifiers({
                                    ...guestModifiers,
                                    [guestIndex]: {
                                      ...guestModifiers[guestIndex],
                                      modifier: e.target.value
                                    }
                                  })}
                                  disabled={creatingManualBooking}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem'
                                  }}
                                >
                                  {availableModifiers.map(mod => (
                                    <option key={mod} value={mod}>{mod}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.35rem', color: '#666' }}>
                                  Allergy Information (optional)
                                </label>
                                <input
                                  type="text"
                                  value={guestModifiers[guestIndex]?.allergies || ''}
                                  onChange={(e) => setGuestModifiers({
                                    ...guestModifiers,
                                    [guestIndex]: {
                                      ...guestModifiers[guestIndex],
                                      allergies: e.target.value
                                    }
                                  })}
                                  placeholder="e.g. nuts, shellfish, eggs..."
                                  disabled={creatingManualBooking}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem'
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Customer Details */}
                <h4 style={{ 
                  marginBottom: '1rem',
                  marginTop: '1.5rem',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#495057',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #e9ecef'
                }}>
                  Customer Details
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontWeight: '600', 
                      marginBottom: '0.5rem',
                      color: '#333',
                      fontSize: '1rem'
                    }}>
                      Title *
                    </label>
                    <select
                      value={manualBookingForm.title}
                      onChange={(e) => setManualBookingForm({ ...manualBookingForm, title: parseInt(e.target.value) })}
                      disabled={creatingManualBooking}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        fontSize: '1rem',
                        border: '2px solid #ddd',
                        borderRadius: '8px',
                        background: 'white'
                      }}
                    >
                      <option value={1}>Mr</option>
                      <option value={2}>Mrs</option>
                      <option value={3}>Miss</option>
                      <option value={4}>Ms</option>
                      <option value={5}>Dr</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontWeight: '600', 
                      marginBottom: '0.5rem',
                      color: '#333',
                      fontSize: '1rem'
                    }}>
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={manualBookingForm.first_name}
                      onChange={(e) => setManualBookingForm({ ...manualBookingForm, first_name: e.target.value })}
                      disabled={creatingManualBooking}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        fontSize: '1rem',
                        border: '2px solid #ddd',
                        borderRadius: '8px'
                      }}
                      placeholder="Enter first name"
                    />
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontWeight: '600', 
                      marginBottom: '0.5rem',
                      color: '#333',
                      fontSize: '1rem'
                    }}>
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={manualBookingForm.last_name}
                      onChange={(e) => setManualBookingForm({ ...manualBookingForm, last_name: e.target.value })}
                      disabled={creatingManualBooking}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        fontSize: '1rem',
                        border: '2px solid #ddd',
                        borderRadius: '8px'
                      }}
                      placeholder="Enter last name"
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ 
                    display: 'block', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontSize: '1rem'
                  }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={manualBookingForm.email}
                    onChange={(e) => setManualBookingForm({ ...manualBookingForm, email: e.target.value })}
                    disabled={creatingManualBooking}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '1rem',
                      border: '2px solid #ddd',
                      borderRadius: '8px'
                    }}
                    placeholder="customer@example.com"
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ 
                    display: 'block', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontSize: '1rem'
                  }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={manualBookingForm.phone}
                    onChange={(e) => setManualBookingForm({ ...manualBookingForm, phone: e.target.value })}
                    disabled={creatingManualBooking}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '1rem',
                      border: '2px solid #ddd',
                      borderRadius: '8px'
                    }}
                    placeholder="07123456789"
                  />
                </div>

                {/* Address Section */}
                <h4 style={{ 
                  marginBottom: '1rem',
                  marginTop: '1.5rem',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#495057',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #e9ecef'
                }}>
                  Address
                </h4>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ 
                    display: 'block', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontSize: '1rem'
                  }}>
                    Address Line 1
                  </label>
                  <input
                    type="text"
                    value={manualBookingForm.address_line1}
                    onChange={(e) => setManualBookingForm({ ...manualBookingForm, address_line1: e.target.value })}
                    disabled={creatingManualBooking}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '1rem',
                      border: '2px solid #ddd',
                      borderRadius: '8px'
                    }}
                    placeholder="Street address"
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ 
                    display: 'block', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontSize: '1rem'
                  }}>
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    value={manualBookingForm.address_line2}
                    onChange={(e) => setManualBookingForm({ ...manualBookingForm, address_line2: e.target.value })}
                    disabled={creatingManualBooking}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '1rem',
                      border: '2px solid #ddd',
                      borderRadius: '8px'
                    }}
                    placeholder="Apartment, suite, etc. (optional)"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontWeight: '600', 
                      marginBottom: '0.5rem',
                      color: '#333',
                      fontSize: '1rem'
                    }}>
                      City *
                    </label>
                    <input
                      type="text"
                      value={manualBookingForm.city}
                      onChange={(e) => setManualBookingForm({ ...manualBookingForm, city: e.target.value })}
                      disabled={creatingManualBooking}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        fontSize: '1rem',
                        border: '2px solid #ddd',
                        borderRadius: '8px'
                      }}
                      placeholder="City (required)"
                    />
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontWeight: '600', 
                      marginBottom: '0.5rem',
                      color: '#333',
                      fontSize: '1rem'
                    }}>
                      County
                    </label>
                    <input
                      type="text"
                      value={manualBookingForm.county}
                      onChange={(e) => setManualBookingForm({ ...manualBookingForm, county: e.target.value })}
                      disabled={creatingManualBooking}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        fontSize: '1rem',
                        border: '2px solid #ddd',
                        borderRadius: '8px'
                      }}
                      placeholder="County"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontWeight: '600', 
                      marginBottom: '0.5rem',
                      color: '#333',
                      fontSize: '1rem'
                    }}>
                      Postcode *
                    </label>
                    <input
                      type="text"
                      value={manualBookingForm.postcode}
                      onChange={(e) => setManualBookingForm({ ...manualBookingForm, postcode: e.target.value.toUpperCase() })}
                      disabled={creatingManualBooking}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        fontSize: '1rem',
                        border: '2px solid #ddd',
                        borderRadius: '8px'
                      }}
                      placeholder="NG7 2JN"
                    />
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      fontWeight: '600', 
                      marginBottom: '0.5rem',
                      color: '#333',
                      fontSize: '1rem'
                    }}>
                      Country
                    </label>
                    <select
                      value={manualBookingForm.country}
                      onChange={(e) => setManualBookingForm({ ...manualBookingForm, country: e.target.value })}
                      disabled={creatingManualBooking}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        fontSize: '1rem',
                        border: '2px solid #ddd',
                        borderRadius: '8px',
                        background: 'white'
                      }}
                    >
                      <option value="GB">United Kingdom</option>
                      <option value="US">United States</option>
                      <option value="FR">France</option>
                      <option value="DE">Germany</option>
                      <option value="IE">Ireland</option>
                      <option value="ES">Spain</option>
                      <option value="IT">Italy</option>
                      <option value="NL">Netherlands</option>
                      <option value="AU">Australia</option>
                      <option value="CA">Canada</option>
                    </select>
                  </div>
                </div>

                {/* Booking Notes Section */}
                <h4 style={{ 
                  marginBottom: '1rem',
                  marginTop: '1.5rem',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#495057',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #e9ecef'
                }}>
                  Additional Information
                </h4>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ 
                    display: 'block', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontSize: '1rem'
                  }}>
                    Booking Notes
                  </label>
                  <textarea
                    value={manualBookingForm.notes}
                    onChange={(e) => setManualBookingForm({ ...manualBookingForm, notes: e.target.value })}
                    disabled={creatingManualBooking}
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '1rem',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                    placeholder="Any special requirements or notes..."
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ 
                    display: 'block', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontSize: '1rem'
                  }}>
                    Third Party Reference
                    <span style={{ 
                      fontWeight: '400', 
                      fontSize: '0.875rem', 
                      color: '#6c757d',
                      marginLeft: '0.5rem'
                    }}>
                      (Optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={manualBookingForm.third_party_reference}
                    onChange={(e) => setManualBookingForm({ ...manualBookingForm, third_party_reference: e.target.value })}
                    disabled={creatingManualBooking}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '1rem',
                      border: '2px solid #ddd',
                      borderRadius: '8px'
                    }}
                    placeholder="External booking reference (e.g., Expedia, Booking.com)"
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ 
                    display: 'block', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    color: '#333',
                    fontSize: '1rem'
                  }}>
                    Your Admin Name *
                  </label>
                  <input
                    type="text"
                    value={manualBookingForm.admin_name}
                    onChange={(e) => setManualBookingForm({ ...manualBookingForm, admin_name: e.target.value })}
                    disabled={creatingManualBooking}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '1rem',
                      border: '2px solid #ddd',
                      borderRadius: '8px'
                    }}
                    placeholder="Your name for audit trail"
                  />
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: '1rem', 
                  marginTop: '2rem',
                  borderTop: '2px solid #e9ecef',
                  paddingTop: '1.5rem'
                }}>
                  <button
                    onClick={handleCloseManualAddModal}
                    disabled={creatingManualBooking}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: creatingManualBooking ? 'not-allowed' : 'pointer',
                      opacity: creatingManualBooking ? 0.6 : 1
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateManualBooking}
                    disabled={creatingManualBooking}
                    style={{
                      flex: 2,
                      padding: '1rem',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      background: creatingManualBooking ? '#6c757d' : 'linear-gradient(135deg, #28a745 0%, #218838 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: creatingManualBooking ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)'
                    }}
                  >
                    {creatingManualBooking ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                        Creating Booking...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.5rem' }} />
                        Create Booking
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </ModalContent>
        </Modal>
      )}

      {/* Payment Modal */}
      {showPaymentModal && pendingPaymentBooking && (
        <Modal show={true} onHide={() => !processingPayment && setShowPaymentModal(false)} size="md">
          <ModalContent>
            <h2 style={{ marginBottom: '1.5rem', color: '#2c5f2d', fontSize: '1.8rem' }}>
              <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '0.75rem' }} />
              Process Payment
            </h2>

            <div style={{ 
              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
              padding: '1.5rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              color: 'white'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.2rem' }}>
                ✅ Booking Created Successfully!
              </h3>
              <div style={{ fontSize: '0.95rem', lineHeight: '1.8' }}>
                <div><strong>Booking Reference:</strong> {pendingPaymentBooking.bookingReference}</div>
                <div><strong>Customer:</strong> {pendingPaymentBooking.customerName}</div>
                <div><strong>Date:</strong> {pendingPaymentBooking.eventDate} at {pendingPaymentBooking.eventTime}</div>
                <div><strong>Guests:</strong> {pendingPaymentBooking.totalGuests}</div>
                <div style={{ 
                  fontSize: '1.3rem', 
                  marginTop: '1rem', 
                  paddingTop: '1rem', 
                  borderTop: '2px solid rgba(255,255,255,0.3)'
                }}>
                  <strong>Total Amount: £{pendingPaymentBooking.totalAmount.toFixed(2)}</strong>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600',
                color: '#2c5f2d'
              }}>
                <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '0.5rem' }} />
                Select Payment Method *
              </label>
              <select
                value={selectedPaymentMethod}
                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                disabled={processingPayment}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  backgroundColor: 'white',
                  cursor: processingPayment ? 'not-allowed' : 'pointer'
                }}
              >
                {paymentMethods.length === 0 && (
                  <option value="">Loading payment methods...</option>
                )}
                {paymentMethods.map(method => (
                  <option key={String(method.Id)} value={String(method.Id)}>
                    {method.Text || method.Description || method.Name || `Payment Method ${method.Id}`}
                  </option>
                ))}
              </select>
              {paymentMethods.length === 1 && paymentMethods[0]?.Text?.includes('Default') && (
                <div style={{ 
                  marginTop: '0.5rem', 
                  color: '#856404',
                  fontSize: '0.9rem',
                  padding: '0.5rem',
                  background: '#fff3cd',
                  borderRadius: '4px'
                }}>
                  ⚠️ VenPOS did not return payment methods; using default. You can still process payment. Configure payment methods in VenPOS to see full list.
                </div>
              )}
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'flex-end',
              marginTop: '2rem' 
            }}>
              <button
                onClick={async () => {
                  const booking = pendingPaymentBooking;
                  if (booking?.customerEmail) {
                    try {
                      const isLocal = window.location.hostname === 'localhost';
                      const apiBase = isLocal ? 'https://www.belvoircastle.com/api' : `${window.location.origin}/api`;
                      await fetch(`${apiBase}/send-manual-booking-confirmation.php`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          customer_email: booking.customerEmail,
                          customer_name: booking.customerName,
                          reference_for_qr: booking.bookingReference,
                          booking_reference: booking.bookingReference,
                          visit_date: booking.eventDate,
                          visit_time: booking.eventTime,
                          event_description: booking.eventDescription || 'Afternoon Tea - Aviary Tearoom',
                          payment_pending: true
                        })
                      });
                    } catch (e) {
                      console.warn('Confirmation email failed:', e);
                    }
                  }
                  setShowPaymentModal(false);
                  setPendingPaymentBooking(null);
                  setShowAddAnotherPrompt(true);
                }}
                disabled={processingPayment}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #6c757d',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: processingPayment ? 'not-allowed' : 'pointer',
                  backgroundColor: 'white',
                  color: '#6c757d',
                  opacity: processingPayment ? 0.6 : 1
                }}
              >
                Process Payment Later
              </button>
              <button
                onClick={handleProcessPayment}
                disabled={processingPayment || !selectedPaymentMethod}
                style={{
                  padding: '0.75rem 2rem',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: (processingPayment || !selectedPaymentMethod) ? 'not-allowed' : 'pointer',
                  background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                  color: 'white',
                  opacity: (processingPayment || !selectedPaymentMethod) ? 0.6 : 1,
                  boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)'
                }}
              >
                {processingPayment ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                    Processing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '0.5rem' }} />
                    Process Payment (£{pendingPaymentBooking.totalAmount.toFixed(2)})
                  </>
                )}
              </button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Add another booking? (e.g. entry ticket) - mandatory: must choose an option, no clicking away */}
      {showAddAnotherPrompt && (
        <Modal
          onClick={(e) => {
            if (e.target === e.currentTarget) e.stopPropagation();
          }}
          style={{ pointerEvents: 'auto' }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            {addAnotherCount === 0 ? (
              <>
                <h2 style={{ marginBottom: '0.75rem', color: '#2c5f2d', fontSize: '1.4rem' }}>
                  Add an entry ticket?
                </h2>
                <p style={{ marginBottom: '0.5rem', color: '#555', lineHeight: 1.5 }}>
                  An entry ticket is usually required with afternoon tea. Add one now for the same customer — their details will be pre-filled.
                </p>
                <p style={{ marginBottom: '1.25rem', fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
                  Please choose an option below to continue.
                </p>
              </>
            ) : (
              <>
                <h2 style={{ marginBottom: '0.75rem', color: '#2c5f2d', fontSize: '1.4rem' }}>
                  Add anything else?
                </h2>
                <p style={{ marginBottom: '0.5rem', color: '#555', lineHeight: 1.5 }}>
                  Add another event or ticket for the same customer on the same date. Their details will be pre-filled.
                </p>
                <p style={{ marginBottom: '1.25rem', fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
                  Please choose an option below to continue.
                </p>
              </>
            )}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setShowAddAnotherPrompt(false)}
                style={{
                  padding: '0.6rem 1.2rem',
                  border: '2px solid #6c757d',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: 'white',
                  color: '#6c757d'
                }}
              >
                No, I&apos;m done
              </button>
              <button
                type="button"
                onClick={handleOpenManualAddForAnother}
                style={{
                  padding: '0.6rem 1.2rem',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(40, 167, 69, 0.3)'
                }}
              >
                {addAnotherCount === 0 ? 'Yes, add entry ticket' : 'Yes, add another'}
              </button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Table/Coach Booking Modal */}
      {/* Booking Type Selector Modal */}
      {showBookingTypeSelector && (
        <Modal show={true} onHide={() => { setShowBookingTypeSelector(false); setSelectedFloorPlanTable(null); }} size="sm">
          <ModalContent style={{ textAlign: 'center', padding: '2rem' }}>
            <h2 style={{ marginBottom: '1.5rem', color: '#2d3748' }}>
              <FontAwesomeIcon icon={faTableCells} style={{ marginRight: '0.75rem', color: '#4a5568' }} />
              Select Booking Type
            </h2>
            <p style={{ fontSize: '0.95rem', color: '#4a5568', marginBottom: '1.5rem' }}>
              Table: <strong>{selectedFloorPlanTable?.name}</strong> (Seats {selectedFloorPlanTable?.capacity})
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <button
                onClick={() => handleSelectBookingTypeFromFloorPlan('afternoon_tea')}
                style={{
                  padding: '1rem',
                  backgroundColor: '#48bb78',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#38a169'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#48bb78'}
              >
                <FontAwesomeIcon icon={faUtensils} />
                Afternoon Tea
              </button>
              <button
                onClick={() => handleSelectBookingTypeFromFloorPlan('table')}
                style={{
                  padding: '1rem',
                  backgroundColor: '#4299e1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3182ce'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4299e1'}
              >
                <FontAwesomeIcon icon={faUtensils} />
                Table Booking
              </button>
              <button
                onClick={() => handleSelectBookingTypeFromFloorPlan('coach')}
                style={{
                  padding: '1rem',
                  backgroundColor: '#ed8936',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dd6b20'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ed8936'}
              >
                <FontAwesomeIcon icon={faUsers} />
                Coach Booking
              </button>
            </div>
            <button
              onClick={() => { setShowBookingTypeSelector(false); setSelectedFloorPlanTable(null); }}
              style={{
                marginTop: '1.5rem',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#e2e8f0',
                color: '#4a5568',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.95rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </ModalContent>
        </Modal>
      )}

      {showTableBookingModal && (
        <Modal show={true} onHide={handleCloseTableBookingModal} size="lg">
          <ModalContent>
            <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <FontAwesomeIcon 
                icon={tableBookingForm.booking_type === 'coach' ? faUsers : faUtensils} 
                style={{ color: tableBookingForm.booking_type === 'coach' ? '#fd7e14' : '#17a2b8' }}
              />
              Add {tableBookingForm.booking_type === 'coach' ? 'Coach' : 'Table'} Booking
            </h2>

            {selectedFloorPlanTable && (
              <div style={{
                padding: '0.75rem',
                background: 'linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%)',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                textAlign: 'center',
                fontSize: '0.95rem',
                fontWeight: '600',
                color: '#2d3748',
                border: '2px solid #dee2e6'
              }}>
                <FontAwesomeIcon icon={faTableCells} style={{ marginRight: '0.5rem', color: '#4a5568' }} />
                Assigning to Table: {selectedFloorPlanTable.name} (Seats {selectedFloorPlanTable.capacity})
              </div>
            )}

            {tableBookingError && (
              <div style={{ 
                padding: '1rem', 
                background: '#fee', 
                color: '#c33', 
                borderRadius: '8px', 
                marginBottom: '1.5rem',
                border: '2px solid #fcc'
              }}>
                {tableBookingError}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Customer Name *
                </label>
                <input
                  type="text"
                  value={tableBookingForm.customer_name}
                  onChange={(e) => setTableBookingForm({ ...tableBookingForm, customer_name: e.target.value })}
                  disabled={creatingTableBooking}
                  placeholder="Full name"
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid #ddd', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={tableBookingForm.customer_email}
                  onChange={(e) => setTableBookingForm({ ...tableBookingForm, customer_email: e.target.value })}
                  disabled={creatingTableBooking}
                  placeholder="customer@example.com"
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid #ddd', borderRadius: '8px' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={tableBookingForm.customer_phone}
                  onChange={(e) => setTableBookingForm({ ...tableBookingForm, customer_phone: e.target.value })}
                  disabled={creatingTableBooking}
                  placeholder="Phone number"
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid #ddd', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Number of Guests *
                </label>
                <input
                  type="number"
                  min="1"
                  max={tableBookingForm.booking_type === 'coach' ? "100" : "20"}
                  value={tableBookingForm.number_of_guests}
                  onChange={(e) => setTableBookingForm({ ...tableBookingForm, number_of_guests: parseInt(e.target.value) || 1 })}
                  disabled={creatingTableBooking}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid #ddd', borderRadius: '8px' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Visit Date *
                </label>
                <input
                  type="date"
                  value={tableBookingForm.visit_date}
                  onChange={(e) => setTableBookingForm({ ...tableBookingForm, visit_date: e.target.value })}
                  disabled={creatingTableBooking}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid #ddd', borderRadius: '8px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Visit Time *
                </label>
                <input
                  type="time"
                  value={tableBookingForm.visit_time}
                  onChange={(e) => setTableBookingForm({ ...tableBookingForm, visit_time: e.target.value })}
                  disabled={creatingTableBooking}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid #ddd', borderRadius: '8px' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                Booking Notes
              </label>
              <textarea
                value={tableBookingForm.notes}
                onChange={(e) => setTableBookingForm({ ...tableBookingForm, notes: e.target.value })}
                disabled={creatingTableBooking}
                rows="3"
                placeholder="Special requirements, dietary needs, etc..."
                style={{ width: '100%', padding: '0.75rem', border: '2px solid #ddd', borderRadius: '8px', fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                Your Admin Name *
              </label>
              <input
                type="text"
                value={tableBookingForm.admin_name}
                onChange={(e) => setTableBookingForm({ ...tableBookingForm, admin_name: e.target.value })}
                disabled={creatingTableBooking}
                placeholder="Your name for audit trail"
                style={{ width: '100%', padding: '0.75rem', border: '2px solid #ddd', borderRadius: '8px' }}
              />
            </div>

            <div style={{
              padding: '1rem 1.25rem',
              marginBottom: '1.5rem',
              background: '#fff3cd',
              border: '2px solid #ffc107',
              borderRadius: '8px',
              color: '#856404',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '1.25rem', flexShrink: 0 }} />
              <span>Please ensure this person has booked a sufficient entry ticket.</span>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem', paddingTop: '1.5rem', borderTop: '2px solid #e9ecef' }}>
              <button
                onClick={handleCloseTableBookingModal}
                disabled={creatingTableBooking}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #6c757d',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: creatingTableBooking ? 'not-allowed' : 'pointer',
                  backgroundColor: 'white',
                  color: '#6c757d'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTableBooking}
                disabled={creatingTableBooking}
                style={{
                  padding: '0.75rem 2rem',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: creatingTableBooking ? 'not-allowed' : 'pointer',
                  background: tableBookingForm.booking_type === 'coach' 
                    ? 'linear-gradient(135deg, #fd7e14 0%, #e8590c 100%)' 
                    : 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              >
                {creatingTableBooking ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                    Creating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.5rem' }} />
                    Create {tableBookingForm.booking_type === 'coach' ? 'Coach' : 'Table'} Booking
                  </>
                )}
              </button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </Panel>
  );
};

export default AfternoonTeaBookings;
